// ==========================================================================================
// NOMBRE: src/modules/StaffModule.jsx
// VERSIÓN: 2.0 - Sistema Profesional con QR, Fotos y Liquidación
// ==========================================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, Tabs, Tab,
  FormControl, InputLabel, Select, MenuItem, Chip, IconButton, Divider,
  Card, CardContent, CardMedia, Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
  InputAdornment, Alert, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, LinearProgress, CircularProgress, Tooltip, Checkbox, FormControlLabel,
  Rating, Stack, List, ListItem, ListItemText, RadioGroup, Radio
} from '@mui/material';

import PeopleIcon from '@mui/icons-material/People';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentsIcon from '@mui/icons-material/Payments';
import CategoryIcon from '@mui/icons-material/Category';
import DashboardIcon from '@mui/icons-material/Dashboard';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import SendIcon from '@mui/icons-material/Send';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import SecurityIcon from '@mui/icons-material/Security';
import StarIcon from '@mui/icons-material/Star';
import HistoryIcon from '@mui/icons-material/History';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LinkIcon from '@mui/icons-material/Link';
import SavingsIcon from '@mui/icons-material/Savings';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api';

// Helper centralizado: agrega Authorization header automáticamente a todos los fetch del módulo
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('token');
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};

// CONSTANTES
const TABS = [
  { id: 'dashboard',    label: 'Dashboard',   icon: <DashboardIcon /> },
  { id: 'schedule',     label: 'Programar',   icon: <PersonAddIcon /> },
  { id: 'attendance',   label: 'Asistencia',  icon: <AccessTimeIcon /> },
  { id: 'employees',    label: 'Empleados',   icon: <PeopleIcon /> },
  { id: 'payments',     label: 'Liquidación', icon: <PaymentsIcon /> },
  { id: 'categories',   label: 'Categorías',  icon: <CategoryIcon /> },
  { id: 'instructions', label: 'Plantillas',  icon: <AssignmentIcon /> },
  { id: 'savings',      label: 'Alcancías',   icon: <SavingsIcon /> },
];

const PAYMENT_METHODS = [
  { value: 'nequi', label: 'Nequi', color: '#E91E63', icon: '📱' },
  { value: 'daviplata', label: 'Daviplata', color: '#FF5722', icon: '📲' },
  { value: 'efectivo', label: 'Efectivo', color: '#4CAF50', icon: '💵' },
  { value: 'transferencia', label: 'Transferencia', color: '#2196F3', icon: '🏦' }
];

const STATUS_CONFIG = {
  activo: { label: 'Activo', color: '#10b981' },
  inactivo: { label: 'Inactivo', color: '#6b7280' },
  suspendido: { label: 'Suspendido', color: '#f59e0b' },
  retirado: { label: 'Retirado', color: '#ef4444' },
  prueba: { label: 'En Prueba', color: '#8b5cf6' }
};

const EMPTY_EMPLOYEE = {
  firstName: '', lastName: '', documentType: 'CC', documentNumber: '', phone: '', whatsapp: '',
  email: '', address: '', city: '', category: '', pin: '1234', status: 'activo', systemRole: 'employee',
  paymentMethod: 'nequi',
  paymentDetails: { nequiNumber: '', daviplataNumber: '', bankName: '', bankAccountType: '', bankAccountNumber: '' },
  emergencyContact: { name: '', phone: '', relationship: '' }, notes: '',
  bloodType: '',
  eps: { name: '', code: '' },
  arl: { name: '', code: '', riskLevel: '' },
  pensionFund: { name: '', code: '' },
  compensationFund: { name: '', code: '' },
  medicalInfo: { allergies: '', medications: '', disabilities: '', chronicDiseases: '', notes: '' },
  notificationRoles: [],
  systemModules: [],
};

// UTILIDADES
const formatCurrency = (v) => !v && v !== 0 ? '$0' : new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v);
const formatTime = (d) => !d ? '--:--' : new Date(d).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
const formatDate = (d) => !d ? '' : new Date(d).toLocaleDateString('es-CO', { weekday: 'short', month: 'short', day: 'numeric' });
const formatHours = (h) => !h ? '0h' : `${Math.floor(h)}h ${Math.round((h % 1) * 60)}m`;
const getQRUrl = (data, size = 200) => `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(data)}`;

// STAT CARD
const StatCard = ({ title, value, subtitle, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2" color="text.secondary">{title}</Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
          {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
        </Box>
        <Avatar sx={{ bgcolor: `${color}20`, color, width: 56, height: 56 }}>{icon}</Avatar>
      </Box>
    </CardContent>
  </Card>
);

// ESCÁNER DE ASISTENCIA — SOLO COORDINADOR/ADMIN
// Acepta: URL completa del QR, solo el token, o el código del empleado
const CoordinatorScanner = ({ onSuccess, todayEvents = [] }) => {
  const [scanInput, setScanInput] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [scanMode, setScanMode] = useState('in'); // 'in' | 'out'
  const inputRef = useRef(null);
  const scannerRef = useRef(null);
  const scannerDivId = 'coordinator-qr-scanner';

  useEffect(() => {
    if (todayEvents.length === 1) setSelectedEvent(todayEvents[0].title || todayEvents[0].name || '');
  }, [todayEvents]);

  // Detener cámara al desmontar
  useEffect(() => {
    return () => { stopCamera(); };
  }, []); // eslint-disable-line

  const extractToken = (raw) => {
    const s = (raw || '').trim();
    try { const u = new URL(s); const t = u.searchParams.get('token'); if (t) return t; } catch (_) {}
    return s || null;
  };

  const registerAttendance = async (action, tokenOverride) => {
    const token = extractToken(tokenOverride || scanInput);
    if (!token) return;
    if (action === 'in' && !selectedEvent) { setLastResult({ ok: false, message: 'Selecciona el evento primero' }); return; }
    setLoading(true);
    setLastResult(null);
    try {
      const endpoint = action === 'in' ? 'clock-in' : 'clock-out';
      const body = action === 'in' ? { token, eventName: selectedEvent } : { token };
      const r = await authFetch(`${API_URL}/staff/attendance/qr/${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json();
      setLastResult({ ok: d.ok, message: d.ok ? `✅ ${d.employee?.fullName}: ${d.message}` : `⚠️ ${d.error}` });
      if (d.ok) { setScanInput(''); if (onSuccess) onSuccess(); if (!cameraActive) setTimeout(() => inputRef.current?.focus(), 150); }
    } catch (_) { setLastResult({ ok: false, message: 'Error de conexión' }); }
    setLoading(false);
  };

  const stopCamera = () => {
    if (scannerRef.current) {
      try { scannerRef.current.stop().catch(() => {}); } catch (_) {}
      scannerRef.current = null;
    }
    setCameraActive(false);
  };

  const startCamera = async () => {
    setCameraError('');
    setLastResult(null);
    // Cargar html5-qrcode dinámicamente
    let Html5Qrcode;
    try {
      const mod = await import('html5-qrcode');
      Html5Qrcode = mod.Html5Qrcode;
    } catch (_) {
      setCameraError('No se pudo cargar el módulo de cámara. Intente recargar la página.');
      return;
    }
    try {
      const qr = new Html5Qrcode(scannerDivId);
      scannerRef.current = qr;
      await qr.start(
        { facingMode: 'environment' }, // cámara trasera
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          // QR detectado — registrar automáticamente
          stopCamera();
          registerAttendance(scanMode, decodedText);
        },
        () => {} // errores de frame ignorados
      );
      setCameraActive(true);
    } catch (err) {
      stopCamera();
      if (err && err.toString().includes('Permission')) {
        setCameraError('Permiso de cámara denegado. Permite el acceso en la configuración del navegador.');
      } else {
        setCameraError('No se pudo acceder a la cámara. Verifique los permisos o use el campo de texto.');
      }
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3, border: '2px solid #6366f1', borderRadius: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#6366f1' }}>
        <QrCode2Icon /> Modo Escáner — Coordinador
      </Typography>

      {/* Evento del día */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Evento del día *</InputLabel>
        <Select value={selectedEvent} onChange={e => setSelectedEvent(e.target.value)} label="Evento del día *">
          {todayEvents.map((ev, i) => (
            <MenuItem key={i} value={ev.title || ev.name || ''}>{ev.title || ev.name}</MenuItem>
          ))}
          <MenuItem value="__manual__"><em>Ingresar manualmente…</em></MenuItem>
        </Select>
      </FormControl>
      {selectedEvent === '__manual__' && (
        <TextField size="small" fullWidth label="Nombre del evento" sx={{ mb: 2 }}
          onChange={e => setSelectedEvent(e.target.value === '__manual__' ? '' : e.target.value)} />
      )}

      {/* Selector entrada/salida para modo cámara */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button size="small" variant={scanMode === 'in' ? 'contained' : 'outlined'} color="success"
          startIcon={<LoginIcon />} onClick={() => setScanMode('in')} sx={{ flex: 1 }}>
          Entrada
        </Button>
        <Button size="small" variant={scanMode === 'out' ? 'contained' : 'outlined'} color="error"
          startIcon={<LogoutIcon />} onClick={() => setScanMode('out')} sx={{ flex: 1 }}>
          Salida
        </Button>
      </Box>

      {/* Visor de cámara */}
      <Box id={scannerDivId} sx={{
        display: cameraActive ? 'block' : 'none',
        mb: 2, borderRadius: 2, overflow: 'hidden',
        border: '2px solid #6366f1',
        '& video': { width: '100% !important', borderRadius: '8px' }
      }} />

      {cameraActive ? (
        <Button fullWidth variant="outlined" color="secondary" startIcon={<CloseIcon />}
          onClick={stopCamera} sx={{ mb: 2 }}>
          Cerrar cámara
        </Button>
      ) : (
        <Button fullWidth variant="contained" startIcon={<CameraAltIcon />}
          onClick={startCamera} disabled={loading}
          sx={{ mb: 2, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff',
            '&:hover': { background: 'linear-gradient(135deg,#4f46e5,#7c3aed)' } }}>
          📷 Usar cámara del celular
        </Button>
      )}

      {cameraError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setCameraError('')}>{cameraError}</Alert>
      )}

      {/* Campo manual (alternativa) */}
      <TextField
        inputRef={inputRef}
        fullWidth size="small"
        label="O ingresa el código manualmente"
        placeholder="Código del empleado o pega el enlace QR…"
        value={scanInput}
        onChange={e => setScanInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') registerAttendance(scanMode); }}
        sx={{ mb: 2 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><QrCode2Icon sx={{ color: '#6366f1' }} /></InputAdornment> }}
        helperText={`Modo actual: ${scanMode === 'in' ? 'Entrada ✅' : 'Salida 🔴'} — Enter para registrar`}
        autoComplete="off"
      />

      {scanInput && (
        <Grid container spacing={2} sx={{ mb: lastResult ? 2 : 0 }}>
          <Grid item xs={6}>
            <Button fullWidth variant="contained" color="success" startIcon={<LoginIcon />}
              disabled={loading} onClick={() => registerAttendance('in')}>
              Registrar Entrada
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button fullWidth variant="contained" color="error" startIcon={<LogoutIcon />}
              disabled={loading} onClick={() => registerAttendance('out')}>
              Registrar Salida
            </Button>
          </Grid>
        </Grid>
      )}

      {lastResult && (
        <Alert severity={lastResult.ok ? 'success' : 'error'} sx={{ mt: 2 }} onClose={() => setLastResult(null)}>
          {lastResult.message}
        </Alert>
      )}
    </Paper>
  );
};

// RELOJ DE ASISTENCIA
const AttendanceClock = () => {
  const [time, setTime] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t); }, []);
  return (
    <Paper sx={{ p: 4, maxWidth: 420, mx: 'auto', textAlign: 'center', mb: 3 }}>
      <Typography variant="h2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
        {time.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </Typography>
      <Typography color="text.secondary">
        {time.toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
      </Typography>
    </Paper>
  );
};

// TABLA ASISTENCIA HOY
const TodayTable = ({ data, loading, onRefresh, categories, onEdit, onClearCheckout, onDelete, isAdmin, isCoord }) => (
  <Paper sx={{ mt: 3 }}>
    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="h6" fontWeight={600}>Asistencia de Hoy</Typography>
      <IconButton onClick={onRefresh}><RefreshIcon /></IconButton>
    </Box>
    {loading && <LinearProgress />}
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#f8fafc' }}>
            <TableCell>Empleado</TableCell>
            <TableCell>Categoría</TableCell>
            <TableCell align="center">Entrada</TableCell>
            <TableCell align="center">Salida</TableCell>
            <TableCell align="center">Horas</TableCell>
            <TableCell align="right">Ganado</TableCell>
            <TableCell align="center">Estado</TableCell>
            {(isAdmin || isCoord) && <TableCell align="center">Acciones</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((a) => {
            const cat = categories.find(c => c.code === a.category);
            return (
              <TableRow key={a.employeeId} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar src={a.photo ? `${API_URL.replace('/api', '')}${a.photo}` : undefined} sx={{ width: 36, height: 36, bgcolor: cat?.color || '#6366f1' }}>
                      {!a.photo && a.fullName?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{a.fullName}</Typography>
                      <Typography variant="caption" color="text.secondary">{a.employeeCode}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell><Chip label={cat?.name || a.category} size="small" sx={{ bgcolor: `${cat?.color || '#6366f1'}20`, color: cat?.color }} /></TableCell>
                <TableCell align="center" sx={{ fontFamily: 'monospace' }}>{formatTime(a.checkIn)}</TableCell>
                <TableCell align="center" sx={{ fontFamily: 'monospace' }}>{formatTime(a.checkOut)}</TableCell>
                <TableCell align="center">
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{formatHours(a.hoursWorked)}</Typography>
                    {a.breakdown?.extraDiurnas > 0 && <Typography variant="caption" color="warning.main" display="block">+{a.breakdown.extraDiurnas}h ext.d</Typography>}
                    {a.breakdown?.extraNocturnas > 0 && <Typography variant="caption" color="error.main" display="block">+{a.breakdown.extraNocturnas}h ext.n</Typography>}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="#10b981">{formatCurrency(a.earnings)}</Typography>
                    {a.earnings?.nightBonus > 0 && <Typography variant="caption" color="#7c3aed" display="block">+{formatCurrency(a.earnings.nightBonus)} noct.</Typography>}
                    {a.earnings?.overtimeBonus > 0 && <Typography variant="caption" color="warning.main" display="block">+{formatCurrency(a.earnings.overtimeBonus)} extra</Typography>}
                    {a.earnings?.sundayBonus > 0 && <Typography variant="caption" color="#dc2626" display="block">+{formatCurrency(a.earnings.sundayBonus)} dom.</Typography>}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Chip label={a.status === 'completed' ? 'Completado' : a.status === 'working' ? 'Trabajando' : 'Pendiente'} size="small"
                    sx={{ bgcolor: a.status === 'completed' ? '#10b98120' : a.status === 'working' ? '#3b82f620' : '#6b728020', color: a.status === 'completed' ? '#10b981' : a.status === 'working' ? '#3b82f6' : '#6b7280' }} />
                </TableCell>
                {(isAdmin || isCoord) && (
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      {isAdmin && (
                        <>
                          <Tooltip title="Editar horas">
                            <IconButton size="small" color="primary" onClick={() => onEdit(a)}><EditIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          {a.status === 'completed' && (
                            <Tooltip title="Quitar salida">
                              <IconButton size="small" color="warning" onClick={() => onClearCheckout(a)}><UndoIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                      {isAdmin && (
                        <Tooltip title="Eliminar registro">
                          <IconButton size="small" color="error" onClick={() => onDelete(a)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {data.length === 0 && !loading && <TableRow><TableCell colSpan={(isAdmin || isCoord) ? 8 : 7} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No hay registros hoy</Typography></TableCell></TableRow>}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);

// DIÁLOGO QR
const QRDialog = ({ open, onClose, employee, categories }) => {
  if (!employee) return null;
  const cat = categories?.find(c => c.code === employee.category);
  const qrLink = `${window.location.origin}/asistencia?token=${employee.qrToken}`;
  const qrImg = getQRUrl(qrLink, 280);
  
  const copyLink = () => navigator.clipboard.writeText(qrLink);
  const downloadQR = () => { const a = document.createElement('a'); a.href = qrImg; a.download = `QR-${employee.employeeCode}.png`; a.click(); };
  const sendWhatsApp = () => {
    const msg = `Hola ${employee.firstName}! 👋\n\nTu código QR para asistencia:\n${qrLink}\n\nCódigo: ${employee.employeeCode}`;
    window.open(`https://wa.me/57${employee.phone?.replace(/\D/g, '')}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle><Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="h6">Código QR</Typography><IconButton onClick={onClose}><CloseIcon /></IconButton></Box></DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        <Avatar src={employee.photo?.url ? `${API_URL.replace('/api', '')}${employee.photo.url}` : undefined} sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: cat?.color || '#6366f1', fontSize: '2rem' }}>
          {!employee.photo?.url && `${employee.firstName?.charAt(0)}${employee.lastName?.charAt(0)}`}
        </Avatar>
        <Typography variant="h5" fontWeight={700}>{employee.firstName} {employee.lastName}</Typography>
        <Typography color="text.secondary" sx={{ mb: 1 }}>{employee.employeeCode}</Typography>
        <Chip label={cat?.name || employee.category} sx={{ bgcolor: cat?.color, color: 'white', mb: 3 }} />
        <Paper elevation={3} sx={{ p: 2, display: 'inline-block', mb: 2 }}><img src={qrImg} alt="QR" /></Paper>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Escanea para registrar asistencia</Typography>
        <Grid container spacing={1}>
          <Grid item xs={4}><Button fullWidth variant="outlined" startIcon={<ContentCopyIcon />} onClick={copyLink}>Copiar</Button></Grid>
          <Grid item xs={4}><Button fullWidth variant="outlined" startIcon={<DownloadIcon />} onClick={downloadQR}>Descargar</Button></Grid>
          <Grid item xs={4}><Button fullWidth variant="contained" startIcon={<WhatsAppIcon />} onClick={sendWhatsApp} sx={{ bgcolor: '#25D366' }}>Enviar</Button></Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

// DIÁLOGO EMPLEADO
const EmployeeDialog = ({ open, onClose, employee, categories, onSave, onSendAccess }) => {
  const [form, setForm] = useState(EMPTY_EMPLOYEE);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [tab, setTab] = useState(0);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileRef = useRef();
  const isEdit = !!employee?._id;
  // — Documentos —
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState('arl');
  const [docUploading, setDocUploading] = useState(false);
  const [sendDlg, setSendDlg] = useState({ open: false, docId: '', canal: 'whatsapp', dest: '', msg: '', sending: false });
  const docInputRef = useRef();

  useEffect(() => {
    if (employee) {
      setForm({ ...EMPTY_EMPLOYEE, ...employee });
      setPhotoPreview(employee.photo?.url ? `${API_URL.replace('/api', '')}${employee.photo.url}` : null);
      setDocs(employee.documents || []);
    } else {
      setForm(EMPTY_EMPLOYEE);
      setPhotoPreview(null);
      setDocs([]);
    }
    setPhotoFile(null);
    setErrors({});
    setTab(0);
  }, [employee, open]);

  const validate = () => {
    const e = {};
    if (!form.firstName?.trim()) e.firstName = 'Requerido';
    if (!form.lastName?.trim()) e.lastName = 'Requerido';
    if (!form.documentNumber?.trim()) e.documentNumber = 'Requerido';
    if (!form.phone?.trim()) e.phone = 'Requerido';
    if (!form.category) e.category = 'Requerido';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const savedEmp = await onSave(form);
      if (photoFile && savedEmp?._id) {
        const fd = new FormData();
        fd.append('photo', photoFile);
        await authFetch(`${API_URL}/staff/employees/${savedEmp._id}/photo`, { method: 'POST', body: fd });
      }
      onClose();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const upd = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const updPay = (f, v) => setForm(p => ({ ...p, paymentDetails: { ...p.paymentDetails, [f]: v } }));

  // — Handlers de documentos —
  const handleDocUpload = async (file) => {
    if (!file || !employee?._id) return;
    setDocUploading(true);
    try {
      const fd = new FormData();
      fd.append('document', file);
      fd.append('type', docType);
      fd.append('name', file.name);
      const r = await authFetch(`${API_URL}/staff/employees/${employee._id}/documents`, { method: 'POST', body: fd });
      const d = await r.json();
      if (d.ok) setDocs(prev => [...prev, d.document]);
    } catch (e) { console.error(e); }
    setDocUploading(false);
  };
  const handleDocDelete = async (docId) => {
    try {
      await authFetch(`${API_URL}/staff/employees/${employee._id}/documents/${docId}`, { method: 'DELETE' });
      setDocs(prev => prev.filter(d => String(d._id) !== docId));
    } catch (e) { console.error(e); }
  };
  const handleDocSend = async () => {
    setSendDlg(p => ({ ...p, sending: true }));
    try {
      await authFetch(`${API_URL}/staff/employees/${employee._id}/documents/${sendDlg.docId}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ canal: sendDlg.canal, destino: sendDlg.dest, mensaje: sendDlg.msg })
      });
      setSendDlg(p => ({ ...p, open: false, sending: false }));
    } catch (e) { console.error(e); setSendDlg(p => ({ ...p, sending: false })); }
  };
  const DOC_LABELS = { arl: '🛡️ ARL', cedula_frente: '🪪 Cédula Frente', cedula_atras: '🪪 Cédula Atrás', rut: '📄 RUT', antecedentes: '📋 Antecedentes', contrato: '📝 Contrato', otro: '📎 Otro' };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle><Box sx={{ display: 'flex', justifyContent: 'space-between' }}><span>{isEdit ? 'Editar' : 'Nuevo'} Empleado</span><IconButton onClick={onClose}><CloseIcon /></IconButton></Box></DialogTitle>
      <DialogContent dividers>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
          <Tab label="Datos Personales" icon={<PersonIcon />} iconPosition="start" />
          <Tab label="Datos de Pago" icon={<PaymentsIcon />} iconPosition="start" />
          <Tab label="Datos Médicos" icon={<LocalHospitalIcon />} iconPosition="start" />
          <Tab label="Accesos" icon={<SecurityIcon />} iconPosition="start" />
          <Tab label="Documentos" icon={<AssignmentIcon />} iconPosition="start" />
        </Tabs>

        {tab === 0 && (
          <Grid container spacing={2}>
            {/* Foto */}
            <Grid item xs={12} sx={{ textAlign: 'center', mb: 2 }}>
              <input type="file" accept="image/*" hidden ref={fileRef} onChange={handlePhoto} />
              <Avatar src={photoPreview} sx={{ width: 100, height: 100, mx: 'auto', mb: 1, cursor: 'pointer', bgcolor: '#6366f1', fontSize: '2.5rem' }} onClick={() => fileRef.current?.click()}>
                {!photoPreview && <CameraAltIcon sx={{ fontSize: 40 }} />}
              </Avatar>
              <Button size="small" onClick={() => fileRef.current?.click()}>Subir Foto</Button>
            </Grid>
            
            <Grid item xs={12} sm={6}><TextField fullWidth required label="Nombres" value={form.firstName} onChange={(e) => upd('firstName', e.target.value)} error={!!errors.firstName} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth required label="Apellidos" value={form.lastName} onChange={(e) => upd('lastName', e.target.value)} error={!!errors.lastName} /></Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth><InputLabel>Tipo Doc.</InputLabel>
                <Select value={form.documentType} label="Tipo Doc." onChange={(e) => upd('documentType', e.target.value)}>
                  <MenuItem value="CC">Cédula</MenuItem><MenuItem value="CE">Cédula Extranjería</MenuItem><MenuItem value="TI">Tarjeta Identidad</MenuItem><MenuItem value="PPT">PPT</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth required label="Número Documento" value={form.documentNumber} onChange={(e) => upd('documentNumber', e.target.value)} error={!!errors.documentNumber} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth required label="Teléfono" value={form.phone} onChange={(e) => upd('phone', e.target.value)} error={!!errors.phone} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="WhatsApp" value={form.whatsapp} onChange={(e) => upd('whatsapp', e.target.value)} placeholder="Si es diferente al teléfono" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Email" value={form.email} onChange={(e) => upd('email', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Ciudad" value={form.city} onChange={(e) => upd('city', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Barrio" value={form.neighborhood} onChange={(e) => upd('neighborhood', e.target.value)} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Dirección" value={form.address} onChange={(e) => upd('address', e.target.value)} /></Grid>
            
            <Grid item xs={12}><Divider><Typography variant="caption">Información Laboral</Typography></Divider></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!errors.category}><InputLabel>Categoría</InputLabel>
                <Select value={form.category} label="Categoría" onChange={(e) => upd('category', e.target.value)}>
                  {categories.map(c => <MenuItem key={c.code} value={c.code}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: c.color }} />{c.icon} {c.name} - {formatCurrency(c.dailyRate)}/día</Box></MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth label="PIN (6 dígitos)" value={form.pin} onChange={(e) => upd('pin', e.target.value)} inputProps={{ maxLength: 6 }} helperText="Para marcar asistencia" /></Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth><InputLabel>Rol en sistema</InputLabel>
                <Select value={form.systemRole || 'employee'} label="Rol en sistema" onChange={(e) => upd('systemRole', e.target.value)}>
                  <MenuItem value="employee">Empleado</MenuItem>
                  <MenuItem value="manager">Coordinador</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth><InputLabel>Estado</InputLabel>
                <Select value={form.status} label="Estado" onChange={(e) => upd('status', e.target.value)}>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Notas" value={form.notes} onChange={(e) => upd('notes', e.target.value)} /></Grid>
          </Grid>
        )}

        {tab === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}><Alert severity="info">Configure el método de pago preferido para facilitar las transferencias.</Alert></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth><InputLabel>Método Preferido</InputLabel>
                <Select value={form.paymentMethod} label="Método Preferido" onChange={(e) => upd('paymentMethod', e.target.value)}>
                  {PAYMENT_METHODS.map(p => <MenuItem key={p.value} value={p.value}>{p.icon} {p.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Número Nequi" value={form.paymentDetails?.nequiNumber || ''} onChange={(e) => updPay('nequiNumber', e.target.value)} placeholder="3001234567" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Número Daviplata" value={form.paymentDetails?.daviplataNumber || ''} onChange={(e) => updPay('daviplataNumber', e.target.value)} placeholder="3001234567" /></Grid>
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Banco" value={form.paymentDetails?.bankName || ''} onChange={(e) => updPay('bankName', e.target.value)} /></Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth><InputLabel>Tipo Cuenta</InputLabel>
                <Select value={form.paymentDetails?.bankAccountType || ''} label="Tipo Cuenta" onChange={(e) => updPay('bankAccountType', e.target.value)}>
                  <MenuItem value="">-</MenuItem><MenuItem value="ahorros">Ahorros</MenuItem><MenuItem value="corriente">Corriente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Número Cuenta" value={form.paymentDetails?.bankAccountNumber || ''} onChange={(e) => updPay('bankAccountNumber', e.target.value)} /></Grid>
          </Grid>
        )}

        {tab === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Esta información es confidencial y solo se usa para emergencias médicas.
              </Alert>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Sangre</InputLabel>
                <Select value={form.bloodType || ''} label="Tipo de Sangre" onChange={(e) => upd('bloodType', e.target.value)}>
                  <MenuItem value="">No especificado</MenuItem>
                  <MenuItem value="A+">A+</MenuItem>
                  <MenuItem value="A-">A-</MenuItem>
                  <MenuItem value="B+">B+</MenuItem>
                  <MenuItem value="B-">B-</MenuItem>
                  <MenuItem value="AB+">AB+</MenuItem>
                  <MenuItem value="AB-">AB-</MenuItem>
                  <MenuItem value="O+">O+</MenuItem>
                  <MenuItem value="O-">O-</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}><Divider><Typography variant="caption">� Seguridad Social</Typography></Divider></Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="EPS (Entidad de Salud)" value={form.eps?.name || ''} onChange={(e) => setForm(p => ({ ...p, eps: { ...p.eps, name: e.target.value }}))} placeholder="Ej: Sanitas, Sura, Nueva EPS" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Código EPS" value={form.eps?.code || ''} onChange={(e) => setForm(p => ({ ...p, eps: { ...p.eps, code: e.target.value }}))} placeholder="Número de afiliación" />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="ARL (Riesgos Laborales)" value={form.arl?.name || ''} onChange={(e) => setForm(p => ({ ...p, arl: { ...p.arl, name: e.target.value }}))} placeholder="Ej: Positiva, Sura ARL" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Código ARL" value={form.arl?.code || ''} onChange={(e) => setForm(p => ({ ...p, arl: { ...p.arl, code: e.target.value }}))} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Nivel de Riesgo</InputLabel>
                <Select value={form.arl?.riskLevel || ''} label="Nivel de Riesgo" onChange={(e) => setForm(p => ({ ...p, arl: { ...p.arl, riskLevel: e.target.value }}))}>
                  <MenuItem value="">No especificado</MenuItem>
                  <MenuItem value="I">Nivel I (Mínimo)</MenuItem>
                  <MenuItem value="II">Nivel II (Bajo)</MenuItem>
                  <MenuItem value="III">Nivel III (Medio)</MenuItem>
                  <MenuItem value="IV">Nivel IV (Alto)</MenuItem>
                  <MenuItem value="V">Nivel V (Máximo)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Fondo de Pensiones" value={form.pensionFund?.name || ''} onChange={(e) => setForm(p => ({ ...p, pensionFund: { ...p.pensionFund, name: e.target.value }}))} placeholder="Ej: Porvenir, Protección, Colfondos" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Código Pensión" value={form.pensionFund?.code || ''} onChange={(e) => setForm(p => ({ ...p, pensionFund: { ...p.pensionFund, code: e.target.value }}))} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Caja de Compensación" value={form.compensationFund?.name || ''} onChange={(e) => setForm(p => ({ ...p, compensationFund: { ...p.compensationFund, name: e.target.value }}))} placeholder="Ej: Comfenalco, Compensar, Cafam" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Código Caja Compensación" value={form.compensationFund?.code || ''} onChange={(e) => setForm(p => ({ ...p, compensationFund: { ...p.compensationFund, code: e.target.value }}))} />
            </Grid>

            <Grid item xs={12}><Divider><Typography variant="caption">� Información Médica</Typography></Divider></Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth multiline rows={2} label="Alergias" value={form.medicalInfo?.allergies || ''} onChange={(e) => setForm(p => ({ ...p, medicalInfo: { ...p.medicalInfo, allergies: e.target.value }}))} placeholder="Ej: Penicilina, Mariscos, Polen..." />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth multiline rows={2} label="Medicamentos" value={form.medicalInfo?.medications || ''} onChange={(e) => setForm(p => ({ ...p, medicalInfo: { ...p.medicalInfo, medications: e.target.value }}))} placeholder="Medicamentos que toma regularmente" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth multiline rows={2} label="Discapacidades o Limitaciones" value={form.medicalInfo?.disabilities || ''} onChange={(e) => setForm(p => ({ ...p, medicalInfo: { ...p.medicalInfo, disabilities: e.target.value }}))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth multiline rows={2} label="Enfermedades Crónicas" value={form.medicalInfo?.chronicDiseases || ''} onChange={(e) => setForm(p => ({ ...p, medicalInfo: { ...p.medicalInfo, chronicDiseases: e.target.value }}))} placeholder="Diabetes, Hipertensión, Asma..." />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Notas Médicas Adicionales" value={form.medicalInfo?.notes || ''} onChange={(e) => setForm(p => ({ ...p, medicalInfo: { ...p.medicalInfo, notes: e.target.value }}))} />
            </Grid>
          </Grid>
        )}

        {tab === 3 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info">
                Configure qué notificaciones recibe este empleado por WhatsApp y a qué módulos del sistema tiene acceso.
              </Alert>
            </Grid>

            {/* Notificaciones WhatsApp */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Notificaciones WhatsApp al aprobar cotizaciones
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Selecciona qué avisos recibirá cuando un cliente apruebe una cotización. Requiere tener WhatsApp configurado arriba.
              </Typography>
              <Grid container spacing={1}>
                {[
                  { role: 'bodega',      label: 'Jefe de Bodega',   desc: 'Lista de equipos a preparar y cargar' },
                  { role: 'transporte',  label: 'Conductor',        desc: 'Ruta, dirección y horario de salida' },
                  { role: 'supervisor',  label: 'Supervisor/Admin',  desc: 'Resumen ejecutivo: cliente, total, equipos' },
                ].map(({ role, label, desc }) => {
                  const checked = (form.notificationRoles || []).includes(role);
                  return (
                    <Grid item xs={12} sm={4} key={role}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5, cursor: 'pointer', borderRadius: 2,
                          borderColor: checked ? '#6366f1' : undefined,
                          bgcolor: checked ? '#eef2ff' : undefined,
                        }}
                        onClick={() => {
                          const cur = form.notificationRoles || [];
                          upd('notificationRoles', checked ? cur.filter(r => r !== role) : [...cur, role]);
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox checked={checked} size="small" sx={{ p: 0 }} readOnly />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{label}</Typography>
                            <Typography variant="caption" color="text.secondary">{desc}</Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            {/* Módulos del sistema */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Módulos del sistema con acceso
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Define a qué secciones del sistema puede ingresar este empleado al iniciar sesión.
              </Typography>
              <Grid container spacing={1}>
                {[
                  { mod: 'calendario',   label: 'Calendario',         icon: '📅' },
                  { mod: 'cotizaciones', label: 'Cotizaciones',        icon: '📋' },
                  { mod: 'ordenes',      label: 'Órdenes de Servicio', icon: '🗂️' },
                  { mod: 'bodega',       label: 'Órdenes de Bodega',   icon: '📦' },
                  { mod: 'clientes',     label: 'Clientes',            icon: '👥' },
                  { mod: 'inventario',   label: 'Inventario',          icon: '🔧' },
                  { mod: 'finanzas',     label: 'Contabilidad',        icon: '💰' },
                  { mod: 'staff',        label: 'Personal',            icon: '👷' },
                ].map(({ mod, label, icon }) => {
                  const checked = (form.systemModules || []).includes(mod);
                  return (
                    <Grid item xs={6} sm={4} key={mod}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5, cursor: 'pointer', borderRadius: 2,
                          borderColor: checked ? '#10b981' : undefined,
                          bgcolor: checked ? '#ecfdf5' : undefined,
                        }}
                        onClick={() => {
                          const cur = form.systemModules || [];
                          upd('systemModules', checked ? cur.filter(m => m !== mod) : [...cur, mod]);
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox checked={checked} size="small" sx={{ p: 0 }} readOnly />
                          <Typography variant="body2">{icon} {label}</Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>

            {/* Credenciales de acceso */}
            {isEdit && (
              <>
                <Grid item xs={12}><Divider sx={{ mt: 1 }} /></Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                    Credenciales de Acceso al Sistema
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Crea o restablece la contraseña del empleado. Se enviará automáticamente por WhatsApp.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {onSendAccess && (
                      <Button variant="contained" startIcon={<SecurityIcon />}
                        sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
                        onClick={() => { onClose(); onSendAccess(employee); }}>
                        Crear / Restablecer Contraseña
                      </Button>
                    )}
                    {(employee?.whatsapp || employee?.phone) && (
                      <Button variant="outlined" startIcon={<WhatsAppIcon />} color="success"
                        onClick={() => {
                          const phone = (employee.whatsapp || employee.phone).replace(/\D/g, '');
                          window.open(`https://wa.me/${phone.startsWith('57') ? phone : '57' + phone}`, '_blank');
                        }}>
                        Abrir WhatsApp
                      </Button>
                    )}
                  </Box>
                  {employee?.employeeCode && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Usuario de acceso: <strong>{employee.employeeCode}</strong>
                    </Typography>
                  )}
                </Grid>
              </>
            )}
          </Grid>
        )}

        {/* ── TAB 4: DOCUMENTOS ── */}
        {tab === 4 && (
          <Box>
            {!isEdit ? (
              <Alert severity="info">Guarda el empleado primero para poder subir documentos.</Alert>
            ) : (
              <>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Tipo de documento</InputLabel>
                    <Select value={docType} label="Tipo de documento" onChange={e => setDocType(e.target.value)}>
                      <MenuItem value="arl">🛡️ ARL</MenuItem>
                      <MenuItem value="cedula_frente">🪪 Cédula Frente</MenuItem>
                      <MenuItem value="cedula_atras">🪪 Cédula Atrás</MenuItem>
                      <MenuItem value="rut">📄 RUT</MenuItem>
                      <MenuItem value="antecedentes">📋 Antecedentes</MenuItem>
                      <MenuItem value="contrato">📝 Contrato</MenuItem>
                      <MenuItem value="otro">📎 Otro</MenuItem>
                    </Select>
                  </FormControl>
                  <input type="file" ref={docInputRef} hidden accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={e => { if (e.target.files[0]) handleDocUpload(e.target.files[0]); e.target.value = ''; }} />
                  <Button variant="outlined" onClick={() => docInputRef.current?.click()} disabled={docUploading}
                    startIcon={docUploading ? <CircularProgress size={18} /> : <SaveIcon />}>
                    {docUploading ? 'Subiendo...' : 'Subir archivo (PDF/imagen)'}
                  </Button>
                </Box>

                {docs.length === 0 ? (
                  <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                    Sin documentos. Selecciona el tipo y sube el archivo.
                  </Typography>
                ) : (
                  <List dense>
                    {docs.map(doc => (
                      <ListItem key={String(doc._id)} divider
                        secondaryAction={
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Descargar">
                              <IconButton size="small" component="a" href={`${API_URL.replace('/api','')}${doc.url}`} target="_blank">
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Enviar por WhatsApp o email">
                              <IconButton size="small" color="primary"
                                onClick={() => setSendDlg({ open: true, docId: String(doc._id), canal: 'whatsapp', dest: employee?.phone || '', msg: '', sending: false })}>
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton size="small" color="error" onClick={() => handleDocDelete(String(doc._id))}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        }>
                        <ListItemText
                          primary={`${DOC_LABELS[doc.type] || doc.type} — ${doc.name}`}
                          secondary={doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString('es-CO') : ''} />
                      </ListItem>
                    ))}
                  </List>
                )}

                {/* Dialog de envío */}
                <Dialog open={sendDlg.open} onClose={() => setSendDlg(p => ({ ...p, open: false }))} maxWidth="xs" fullWidth>
                  <DialogTitle>Enviar documento</DialogTitle>
                  <DialogContent>
                    <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                      <InputLabel>Canal de envío</InputLabel>
                      <Select value={sendDlg.canal} label="Canal de envío"
                        onChange={e => setSendDlg(p => ({ ...p, canal: e.target.value }))}>
                        <MenuItem value="whatsapp">📱 WhatsApp</MenuItem>
                        <MenuItem value="email">✉️ Email</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField fullWidth sx={{ mb: 2 }}
                      label={sendDlg.canal === 'email' ? 'Correo destino' : 'Número WhatsApp (ej: 573001234567)'}
                      value={sendDlg.dest}
                      onChange={e => setSendDlg(p => ({ ...p, dest: e.target.value }))}
                      placeholder={sendDlg.canal === 'email' ? 'coordinador@empresa.com' : '573001234567'} />
                    <TextField fullWidth multiline rows={2} label="Mensaje adicional (opcional)"
                      value={sendDlg.msg} onChange={e => setSendDlg(p => ({ ...p, msg: e.target.value }))}
                      placeholder="Se enviará un mensaje predeterminado si se deja vacío" />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setSendDlg(p => ({ ...p, open: false }))}>Cancelar</Button>
                    <Button variant="contained" onClick={handleDocSend} disabled={!sendDlg.dest || sendDlg.sending}
                      startIcon={sendDlg.sending ? <CircularProgress size={18} /> : <SendIcon />}>
                      {sendDlg.sending ? 'Enviando...' : 'Enviar'}
                    </Button>
                  </DialogActions>
                </Dialog>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}>
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ==========================================================================================
// DIALOGO DE HISTORIAL Y EVALUACIÓN
// ==========================================================================================
const CRITERIOS = [
  { key: 'presentacion', label: 'Presentación personal',       desc: 'Puntualidad, uniforme, actitud' },
  { key: 'montaje',      label: 'Desempeño en montaje',        desc: 'Trabajo durante la instalación' },
  { key: 'desempeno',    label: 'Desempeño durante el evento', desc: 'Atención, servicio al cliente' },
  { key: 'desmontaje',   label: 'Desempeño en desmontaje',     desc: 'Orden, rapidez al recoger' },
];

const EmployeeEvalDialog = ({ open, onClose, employee }) => {
  const [evalTab, setEvalTab]       = useState(0);
  const [history, setHistory]       = useState([]);
  const [loadingH, setLoadingH]     = useState(false);
  const [saving, setSaving]         = useState(false);
  const [scores, setScores]         = useState({ presentacion: 0, montaje: 0, desempeno: 0, desmontaje: 0 });
  const [observaciones, setObs]     = useState('');
  const [evaluadoPor, setEvalPor]   = useState('');
  const [avgRating, setAvgRating]   = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (open && employee?._id) {
      setEvalTab(0);
      setScores({ presentacion: 0, montaje: 0, desempeno: 0, desmontaje: 0 });
      setObs(''); setEvalPor(''); setSuccessMsg('');
      setLoadingH(true);
      authFetch(`${API_URL}/staff/employees/${employee._id}/evaluations`)
        .then(r => r.json())
        .then(d => {
          if (d.ok) { setHistory(d.evaluations || []); setAvgRating(d.averageRating); }
        })
        .finally(() => setLoadingH(false));
    }
  }, [open, employee]);

  const handleSave = async () => {
    const atLeastOne = CRITERIOS.some(c => scores[c.key] >= 1);
    if (!atLeastOne) { alert('Califica al menos un criterio'); return; }
    // Necesitamos una asistencia reciente para asociar la evaluación
    // Usamos la última del historial o pedimos al usuario seleccionarla
    // Por simplicidad, usamos la última asistencia sin evaluar del employee
    setSaving(true);
    try {
      // Primero obtenemos la última asistencia sin evaluar
      const empRes = await authFetch(`${API_URL}/staff/portal/${employee._id}/history?status=pending`);
      const empData = await empRes.json();
      const lastAtt = empData.history?.[0];
      if (!lastAtt?._id) {
        alert('No hay asistencia reciente para evaluar. El empleado debe tener al menos un registro de entrada/salida.');
        setSaving(false);
        return;
      }
      const res = await authFetch(`${API_URL}/staff/employees/${employee._id}/evaluate/${lastAtt._id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...scores, observaciones, evaluadoPor }),
      });
      const data = await res.json();
      if (data.ok) {
        setAvgRating(data.averageRating);
        setSuccessMsg(`✅ Evaluación guardada. Promedio del evento: ${data.promedio}/5`);
        // Recargar historial
        const hRes = await authFetch(`${API_URL}/staff/employees/${employee._id}/evaluations`);
        const hData = await hRes.json();
        if (hData.ok) setHistory(hData.evaluations || []);
        setEvalTab(0);
      }
    } catch (e) {
      alert('Error guardando evaluación: ' + e.message);
    }
    setSaving(false);
  };

  const starColor = (avg) => avg >= 4 ? '#f59e0b' : avg >= 3 ? '#3b82f6' : '#ef4444';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {employee?.firstName} {employee?.lastName}
            </Typography>
            {avgRating > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Rating value={avgRating} precision={0.1} readOnly size="small" />
                <Typography variant="caption" sx={{ color: starColor(avgRating), fontWeight: 700 }}>
                  {avgRating}/5 ({history.length} evaluaciones)
                </Typography>
              </Box>
            )}
            {!avgRating && <Typography variant="caption" color="text.secondary">Sin evaluaciones</Typography>}
          </Box>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Tabs value={evalTab} onChange={(_, v) => setEvalTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Historial" icon={<HistoryIcon />} iconPosition="start" />
          <Tab label="Nueva evaluación" icon={<StarIcon />} iconPosition="start" />
        </Tabs>

        {/* TAB 0: HISTORIAL */}
        {evalTab === 0 && (
          <Box sx={{ p: 2 }}>
            {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
            {loadingH && <LinearProgress />}
            {!loadingH && history.length === 0 && (
              <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                Sin evaluaciones registradas aún
              </Typography>
            )}
            <Stack spacing={1.5}>
              {history.map((ev) => (
                <Paper key={ev._id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={700}>
                      {ev.eventName || 'Evento sin nombre'}
                    </Typography>
                    <Chip
                      icon={<StarIcon sx={{ fontSize: '14px !important' }} />}
                      label={`${ev.evaluation.promedio}/5`}
                      size="small"
                      sx={{ bgcolor: `${starColor(ev.evaluation.promedio)}20`, color: starColor(ev.evaluation.promedio), fontWeight: 700 }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(ev.date).toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    {ev.eventLocation ? ` · ${ev.eventLocation}` : ''}
                  </Typography>
                  <Grid container spacing={1} sx={{ mt: 0.5 }}>
                    {CRITERIOS.filter(c => ev.evaluation[c.key] != null).map(c => (
                      <Grid item xs={6} key={c.key}>
                        <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                        <Rating value={ev.evaluation[c.key]} readOnly size="small" precision={1} />
                      </Grid>
                    ))}
                  </Grid>
                  {ev.evaluation.observaciones && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#64748b', fontStyle: 'italic' }}>
                      "{ev.evaluation.observaciones}"
                    </Typography>
                  )}
                  {ev.evaluation.evaluadoPor && (
                    <Typography variant="caption" color="text.secondary"> — Evaluado por: {ev.evaluation.evaluadoPor}</Typography>
                  )}
                </Paper>
              ))}
            </Stack>
          </Box>
        )}

        {/* TAB 1: NUEVA EVALUACIÓN */}
        {evalTab === 1 && (
          <Box sx={{ p: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Se asociará con la última asistencia registrada del empleado.
            </Alert>
            <Stack spacing={2}>
              {CRITERIOS.map(c => (
                <Box key={c.key}>
                  <Typography variant="body2" fontWeight={600}>{c.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{c.desc}</Typography>
                  <Rating
                    value={scores[c.key]}
                    onChange={(_, v) => setScores(p => ({ ...p, [c.key]: v || 0 }))}
                    size="large"
                  />
                </Box>
              ))}
              <Divider />
              <TextField
                label="Observaciones"
                multiline
                minRows={2}
                fullWidth
                value={observaciones}
                onChange={e => setObs(e.target.value)}
                placeholder="Puntualidad, actitud, situaciones especiales..."
              />
              <TextField
                label="Evaluado por"
                fullWidth
                value={evaluadoPor}
                onChange={e => setEvalPor(e.target.value)}
                placeholder="Nombre del supervisor o encargado"
              />
            </Stack>
          </Box>
        )}
      </DialogContent>
      {evalTab === 1 && (
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : <StarIcon />}
            sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
          >
            {saving ? 'Guardando...' : 'Guardar evaluación'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

// LISTA EMPLEADOS
const EmployeesList = ({ employees, categories, loading, onEdit, onDelete, onViewQR, onAdd, onRefresh, onEval, onSendAccess, onViewHistory, isAdmin }) => {
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [showInvite, setShowInvite] = useState(false);
  const [copied, setCopied] = useState(false);

  const registrationUrl = `${window.location.origin}/registro-empleado`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(registrationUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShareWhatsApp = (phone = '') => {
    const msg = encodeURIComponent(`Hola! Te invitamos a unirte al equipo de Inflables Da Silva. Por favor completa tu registro aquí:\n\n${registrationUrl}\n\nGracias!`);
    const url = phone
      ? `https://wa.me/${phone.replace(/\D/g, '')}?text=${msg}`
      : `https://wa.me/?text=${msg}`;
    window.open(url, '_blank');
  };

  const filtered = useMemo(() => {
    const list = employees.filter(e => {
      const matchSearch = !search || `${e.firstName} ${e.lastName} ${e.documentNumber} ${e.employeeCode}`.toLowerCase().includes(search.toLowerCase());
      const matchCat = !filterCat || e.category === filterCat;
      const matchStatus = !filterStatus || e.status === filterStatus;
      return matchSearch && matchCat && matchStatus;
    });
    if (sortBy === 'rating') {
      list.sort((a, b) => (b.stats?.averageRating || 0) - (a.stats?.averageRating || 0));
    } else if (sortBy === 'name') {
      list.sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
    }
    return list;
  }, [employees, search, filterCat, filterStatus, sortBy]);

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}><TextField fullWidth size="small" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} /></Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small"><InputLabel>Categoría</InputLabel>
              <Select value={filterCat} label="Categoría" onChange={(e) => setFilterCat(e.target.value)}>
                <MenuItem value="">Todas</MenuItem>
                {categories.map(c => <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small"><InputLabel>Estado</InputLabel>
              <Select value={filterStatus} label="Estado" onChange={(e) => setFilterStatus(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small"><InputLabel>Ordenar</InputLabel>
              <Select value={sortBy} label="Ordenar" onChange={(e) => setSortBy(e.target.value)}>
                <MenuItem value="name">Nombre A-Z</MenuItem>
                <MenuItem value="rating">⭐ Mayor calificación</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <IconButton onClick={onRefresh}><RefreshIcon /></IconButton>
            <Button variant="outlined" color="success" startIcon={<PersonAddIcon />} onClick={() => setShowInvite(true)} size="small">Invitar</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd}>Nuevo</Button>
          </Grid>
        </Grid>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2}>
        {filtered.map(emp => {
          const cat = categories.find(c => c.code === emp.category);
          const status = STATUS_CONFIG[emp.status] || STATUS_CONFIG.activo;
          const pay = PAYMENT_METHODS.find(p => p.value === emp.paymentMethod);
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={emp._id}>
              <Card sx={{ height: '100%' }}>
                <Box sx={{ position: 'relative', pt: '70%', bgcolor: '#f1f5f9' }}>
                  {emp.photo?.url ? (
                    <CardMedia component="img" image={`${API_URL.replace('/api', '')}${emp.photo.url}`} sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Avatar sx={{ width: 70, height: 70, bgcolor: cat?.color || '#6366f1', fontSize: '1.8rem' }}>{emp.firstName?.charAt(0)}{emp.lastName?.charAt(0)}</Avatar>
                    </Box>
                  )}
                  <Chip label={status.label} size="small" sx={{ position: 'absolute', top: 8, right: 8, bgcolor: status.color, color: 'white' }} />
                  {cat && <Chip label={cat.icon} size="small" sx={{ position: 'absolute', top: 8, left: 8, bgcolor: cat.color, color: 'white' }} />}
                </Box>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700}>{emp.firstName} {emp.lastName}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{emp.employeeCode}</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" color="text.secondary">📱 {emp.phone}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', my: 1 }}>
                    <Chip label={cat?.name || emp.category} size="small" sx={{ bgcolor: `${cat?.color}20`, color: cat?.color }} />
                    {pay && <Chip label={`${pay.icon} ${pay.label}`} size="small" sx={{ bgcolor: `${pay.color}20`, color: pay.color }} />}
                  </Box>
                  {/* Calificación promedio */}
                  {emp.stats?.averageRating > 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Rating value={emp.stats.averageRating} precision={0.1} readOnly size="small" />
                      <Typography variant="caption" fontWeight={700} sx={{ color: '#f59e0b' }}>
                        {emp.stats.averageRating}/5
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Sin calificaciones
                    </Typography>
                  )}
                  {emp.stats && (
                    <Box sx={{ bgcolor: '#f8fafc', p: 1, borderRadius: 1, mb: 1 }}>
                      <Grid container>
                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Días</Typography><Typography variant="body2" fontWeight={600}>{emp.stats.totalDaysWorked || 0}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Pendiente</Typography><Typography variant="body2" fontWeight={600} color={emp.stats.pendingPayment > 0 ? '#f59e0b' : '#10b981'}>{formatCurrency(emp.stats.pendingPayment || 0)}</Typography></Grid>
                      </Grid>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                      <Tooltip title="Ver QR"><IconButton size="small" onClick={() => onViewQR(emp)}><QrCode2Icon /></IconButton></Tooltip>
                      <Tooltip title="Evaluar / Historial"><IconButton size="small" onClick={() => onEval(emp)} sx={{ color: '#f59e0b' }}><StarIcon /></IconButton></Tooltip>
                      <Tooltip title="Crear/reenviar acceso al sistema"><IconButton size="small" onClick={() => onSendAccess(emp)} sx={{ color: '#6366f1' }}><SecurityIcon /></IconButton></Tooltip>
                      <Tooltip title="Historial de pagos"><IconButton size="small" onClick={() => onViewHistory && onViewHistory(emp)} sx={{ color: '#10b981' }}><HistoryIcon /></IconButton></Tooltip>
                      {(emp.phone || emp.whatsapp) && (
                        <Tooltip title="Abrir WhatsApp">
                          <IconButton size="small" sx={{ color: '#25D366' }}
                            onClick={() => {
                              const phone = (emp.whatsapp || emp.phone).replace(/\D/g, '');
                              window.open(`https://wa.me/${phone.startsWith('57') ? phone : '57' + phone}`, '_blank');
                            }}>
                            <WhatsAppIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Box>
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => onEdit(emp)} color="primary"><EditIcon /></IconButton></Tooltip>
                      {isAdmin && <Tooltip title="Eliminar"><IconButton size="small" onClick={() => onDelete(emp)} color="error"><DeleteIcon /></IconButton></Tooltip>}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
        {filtered.length === 0 && !loading && (
          <Grid item xs={12}><Paper sx={{ p: 4, textAlign: 'center' }}><PeopleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} /><Typography color="text.secondary">No se encontraron empleados</Typography></Paper></Grid>
        )}
      </Grid>

      {/* Dialog: Invitar nuevo empleado */}
      <Dialog open={showInvite} onClose={() => setShowInvite(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonAddIcon color="success" /> Invitar Nuevo Empleado
            </Box>
            <IconButton onClick={() => setShowInvite(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Comparte este enlace con el nuevo empleado para que complete su registro con datos personales, seguridad social y datos de pago.
          </Typography>

          <Paper sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LinkIcon color="success" />
            <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.85rem' }}>
              {registrationUrl}
            </Typography>
            <Tooltip title={copied ? "Copiado!" : "Copiar enlace"}>
              <IconButton onClick={handleCopyLink} color={copied ? "success" : "default"}>
                {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
          </Paper>

          <Divider sx={{ my: 2 }}><Chip label="Enviar por" size="small" /></Divider>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" color="success" startIcon={<WhatsAppIcon />} onClick={() => handleShareWhatsApp()}
              sx={{ textTransform: 'none' }}>
              Enviar por WhatsApp
            </Button>
            <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopyLink}
              sx={{ textTransform: 'none' }}>
              {copied ? 'Enlace copiado!' : 'Copiar enlace'}
            </Button>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            Cuando el empleado complete el formulario, aparecerá automáticamente en tu lista de empleados con estado "Pendiente".
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInvite(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// PAGOS PENDIENTES
const PaymentsPending = ({ employees, loading, onPay, onViewHistory, onRefresh, categories }) => (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
      <Typography variant="h5" fontWeight={700}>💰 Liquidación de Pagos</Typography>
      <IconButton onClick={onRefresh}><RefreshIcon /></IconButton>
    </Box>
    {loading && <LinearProgress sx={{ mb: 2 }} />}
    <Grid container spacing={2}>
      {employees.map(emp => {
        const cat = categories.find(c => c.code === emp.category);
        const pay = PAYMENT_METHODS.find(p => p.value === emp.paymentMethod);
        return (
          <Grid item xs={12} sm={6} md={4} key={emp._id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Avatar src={emp.photo ? `${API_URL.replace('/api', '')}${emp.photo}` : undefined} sx={{ width: 56, height: 56, bgcolor: cat?.color }}>
                    {!emp.photo && emp.fullName?.charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>{emp.fullName}</Typography>
                    <Typography variant="caption" color="text.secondary">{emp.employeeCode}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                      <Chip label={cat?.name || emp.category} size="small" sx={{ bgcolor: cat?.color, color: 'white', height: 20, fontSize: '0.7rem' }} />
                      {pay && <Chip label={pay.label} size="small" sx={{ bgcolor: pay.color, color: 'white', height: 20, fontSize: '0.7rem' }} />}
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ bgcolor: '#fef3c7', p: 2, borderRadius: 1, textAlign: 'center', mb: 2 }}>
                  <Typography variant="h4" fontWeight={700} color="#d97706">{formatCurrency(emp.netToPay || emp.pendingPayment)}</Typography>
                  <Typography variant="body2" color="text.secondary">Pendiente por pagar</Typography>
                </Box>
                <Button fullWidth variant="contained" startIcon={<SendIcon />} onClick={() => onPay(emp)} sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, mb: 1 }}>
                  Pagar Ahora
                </Button>
                <Button fullWidth variant="outlined" size="small" startIcon={<HistoryIcon />} onClick={() => onViewHistory(emp)}>
                  Ver Historial
                </Button>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
      {employees.length === 0 && !loading && (
        <Grid item xs={12}><Paper sx={{ p: 4, textAlign: 'center' }}><CheckCircleIcon sx={{ fontSize: 48, color: '#10b981', mb: 1 }} /><Typography color="text.secondary">¡Todos los pagos al día!</Typography></Paper></Grid>
      )}
    </Grid>
  </Box>
);

// CATEGORÍAS
const CategoriesManager = ({ categories, loading, onSave, onDelete, onRefresh }) => {
  const [dialog, setDialog] = useState({ open: false, data: null });
  const EMPTY_CAT = { name: '', code: '', description: '', color: '#6366f1', icon: '👷', dailyRate: 80000, hourlyRate: 0, standardHours: 10, sundayRateMode: 'same_as_weekday', sundayBonusPercent: 0, sundayMinHours: 0, saturdayOvertimeStartHour: 0, nightStartHour: 19, defaultBreakMinutes: 0, recargoNocturno: 35, recargoExtraDiurna: 25, recargoExtraNocturna: 75 };
  const [form, setForm] = useState(EMPTY_CAT);

  const handleEdit = (cat) => { setForm(cat || EMPTY_CAT); setDialog({ open: true, data: cat }); };
  const handleSave = async () => { await onSave(form); setDialog({ open: false, data: null }); };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>📋 Categorías y Tarifas</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={onRefresh}><RefreshIcon /></IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleEdit(null)}>Nueva</Button>
        </Box>
      </Box>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Grid container spacing={2}>
        {categories.map(cat => (
          <Grid item xs={12} sm={6} md={4} key={cat._id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ bgcolor: cat.color }}>{cat.icon}</Avatar>
                    <Box><Typography variant="h6" fontWeight={600}>{cat.name}</Typography><Chip label={cat.code} size="small" /></Box>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{cat.description || 'Sin descripción'}</Typography>
                <Divider sx={{ my: 1 }} />
                <Grid container spacing={1}>
                  <Grid item xs={6}><Typography variant="caption" color="text.secondary">Tarifa base/día</Typography><Typography variant="h6" fontWeight={700} color="#10b981">{formatCurrency(cat.dailyRate)}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="caption" color="text.secondary">Tarifa hora extra</Typography><Typography variant="h6" fontWeight={700} color="#3b82f6">{formatCurrency(cat.hourlyRate || Math.round(cat.dailyRate / (cat.standardHours || 10)))}</Typography></Grid>
                  <Grid item xs={12} sx={{ mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#eff6ff', borderRadius: 1, px: 1.5, py: 0.8, border: '1px solid #bfdbfe' }}>
                      <AccessTimeIcon sx={{ fontSize: 16, color: '#2563eb' }} />
                      <Typography variant="caption" color="#1e40af" fontWeight={600}>
                        Tarifa cubre {cat.standardHours || 10}h — después se cobra hora extra
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                  <IconButton size="small" onClick={() => handleEdit(cat)} color="primary"><EditIcon /></IconButton>
                  <IconButton size="small" onClick={() => onDelete(cat)} color="error"><DeleteIcon /></IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ─── Política de Puntualidad ─── */}
      <Paper variant="outlined" sx={{ mt: 4, p: 3, borderColor: '#f59e0b', borderWidth: 1.5 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>⏱️ Política de Puntualidad y Penalizaciones</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Aplica a todos los empleados. Los descuentos se aplican por cada llegada tarde al evento según el retraso registrado en el sistema de asistencia.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, bgcolor: '#fef3c7', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block">1 – 15 minutos tarde</Typography>
              <Typography variant="h5" fontWeight={800} color="#d97706">$5.000</Typography>
              <Typography variant="caption" color="text.secondary">Descuento por tardanza leve</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, bgcolor: '#ffedd5', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block">16 – 30 minutos tarde</Typography>
              <Typography variant="h5" fontWeight={800} color="#ea580c">$15.000</Typography>
              <Typography variant="caption" color="text.secondary">Descuento por tardanza moderada</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, bgcolor: '#fee2e2', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block">31 – 60 minutos tarde</Typography>
              <Typography variant="h5" fontWeight={800} color="#dc2626">$25.000</Typography>
              <Typography variant="caption" color="text.secondary">Descuento por tardanza grave</Typography>
            </Paper>
          </Grid>
        </Grid>
        <Alert severity="info" sx={{ mt: 2, py: 0.5 }}>
          Esta política se informa automáticamente a cada empleado en el mensaje de citación por WhatsApp al ser asignado a un evento.
        </Alert>
      </Paper>

      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, data: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog.data ? 'Editar' : 'Nueva'} Categoría</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={8}><TextField fullWidth label="Nombre" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Código" value={form.code} onChange={(e) => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} inputProps={{ maxLength: 4 }} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Descripción" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} /></Grid>
            {/* Horas base — primero y destacado */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0f9ff', borderColor: '#3b82f6' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#1d4ed8' }}>
                  ⏱️ Horas que cubre la tarifa diaria base
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={5}>
                    <TextField fullWidth type="number" label="Horas base incluidas" value={form.standardHours ?? 10}
                      onChange={(e) => setForm(f => ({ ...f, standardHours: parseInt(e.target.value) || 1 }))}
                      InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }}
                      helperText="Ej: 10 → después de 10h se cobra hora extra"
                      inputProps={{ min: 1, max: 24 }} />
                  </Grid>
                  <Grid item xs={7}>
                    <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
                      Tarifa diaria <strong>{formatCurrency(form.dailyRate)}</strong> cubre <strong>{form.standardHours || 10}h</strong><br />
                      Tarifa/hora extra: <strong>{formatCurrency(form.hourlyRate || Math.round(form.dailyRate / (form.standardHours || 10)))}</strong>
                      {form.hourlyRate ? ' (manual)' : ' (auto)'}
                    </Alert>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={6}><TextField fullWidth type="number" label="Tarifa Diaria (base)" value={form.dailyRate} onChange={(e) => setForm(f => ({ ...f, dailyRate: parseInt(e.target.value) || 0 }))} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} /></Grid>
            <Grid item xs={6}><TextField fullWidth type="number" label="Tarifa/Hora extra" value={form.hourlyRate} onChange={(e) => setForm(f => ({ ...f, hourlyRate: parseInt(e.target.value) || 0 }))} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} helperText="Dejar en 0 para calcular automático (Tarifa Diaria ÷ Horas Base)" /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Icono (emoji)" value={form.icon} onChange={(e) => setForm(f => ({ ...f, icon: e.target.value }))} /></Grid>
            <Grid item xs={4}><TextField fullWidth type="color" label="Color" value={form.color} onChange={(e) => setForm(f => ({ ...f, color: e.target.value }))} /></Grid>
            <Grid item xs={4}></Grid>

            {/* ─── Configuración Sábado ─── */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderColor: '#f59e0b' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#b45309' }}>📅 Configuración Sábado</Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth type="number" label="Horas extras desde las (hora 24h)" value={form.saturdayOvertimeStartHour || 0}
                      onChange={(e) => setForm(f => ({ ...f, saturdayOvertimeStartHour: parseInt(e.target.value) || 0 }))}
                      inputProps={{ min: 0, max: 23 }}
                      helperText={form.saturdayOvertimeStartHour > 0 ? `Horas extras desde las ${form.saturdayOvertimeStartHour}:00 del sábado` : 'Valor 0 = usa lógica normal (horas base)'}
                      InputProps={{ endAdornment: <InputAdornment position="end">:00</InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Alert severity="info" icon={false} sx={{ py: 0.5, fontSize: 12 }}>
                      {form.saturdayOvertimeStartHour > 0
                        ? `Si entra a las 8am: normal hasta las ${form.saturdayOvertimeStartHour}:00, luego se cobra hora extra por cada hora adicional.`
                        : 'Desactivado. El sábado se calcula igual que cualquier día (tarifa base + horas extra según jornada estándar).'}
                    </Alert>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* ─── Configuración Domingo ─── */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderColor: '#7c3aed' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#5b21b6' }}>☀️ Configuración Domingo</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Modo de pago dominical</InputLabel>
                      <Select value={form.sundayRateMode || 'same_as_weekday'} label="Modo de pago dominical"
                        onChange={(e) => setForm(f => ({ ...f, sundayRateMode: e.target.value }))}>
                        <MenuItem value="same_as_weekday">Sin recargo — igual que día de semana</MenuItem>
                        <MenuItem value="bonus_percent">Recargo % adicional sobre tarifa diaria</MenuItem>
                        <MenuItem value="hourly_fixed">Valor por horas (mínimo garantizado)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {form.sundayRateMode === 'bonus_percent' && (
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth type="number" label="% adicional domingo" value={form.sundayBonusPercent || 0}
                        onChange={(e) => setForm(f => ({ ...f, sundayBonusPercent: parseInt(e.target.value) || 0 }))}
                        inputProps={{ min: 0, max: 200 }}
                        helperText={`Domingo = tarifa diaria + ${form.sundayBonusPercent || 0}% → ${formatCurrency(Math.round((form.dailyRate || 0) * (1 + (form.sundayBonusPercent || 0) / 100)))}`}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      />
                    </Grid>
                  )}
                  {form.sundayRateMode === 'hourly_fixed' && (
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth type="number" label="Horas mínimas garantizadas" value={form.sundayMinHours || 0}
                        onChange={(e) => setForm(f => ({ ...f, sundayMinHours: parseInt(e.target.value) || 0 }))}
                        inputProps={{ min: 0, max: 24 }}
                        helperText={`Domingo = ${form.sundayMinHours || 0}h × ${formatCurrency(form.hourlyRate || Math.round((form.dailyRate || 0) / (form.standardHours || 10)))} = ${formatCurrency((form.sundayMinHours || 0) * (form.hourlyRate || Math.round((form.dailyRate || 0) / (form.standardHours || 10))))}`}
                        InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>

            {/* Recargos legales Colombia */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderColor: '#6366f1' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#4338ca' }}>⚖️ Recargos Legales Colombia 2026</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth type="number" label="Inicio nocturno (hora)" value={form.nightStartHour ?? 19}
                      onChange={(e) => setForm(f => ({ ...f, nightStartHour: parseInt(e.target.value) || 19 }))}
                      inputProps={{ min: 0, max: 23 }}
                      helperText="19 = 7pm (ley Colombia)"
                      InputProps={{ endAdornment: <InputAdornment position="end">:00</InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth type="number" label="Descanso por turno" value={form.defaultBreakMinutes ?? 0}
                      onChange={(e) => setForm(f => ({ ...f, defaultBreakMinutes: parseInt(e.target.value) || 0 }))}
                      inputProps={{ min: 0 }}
                      helperText="Minutos descontados"
                      InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth type="number" label="Recargo nocturno" value={form.recargoNocturno ?? 35}
                      onChange={(e) => setForm(f => ({ ...f, recargoNocturno: parseInt(e.target.value) || 0 }))}
                      inputProps={{ min: 0 }}
                      helperText="Hora ordinaria nocturna"
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth type="number" label="Recargo extra diurna" value={form.recargoExtraDiurna ?? 25}
                      onChange={(e) => setForm(f => ({ ...f, recargoExtraDiurna: parseInt(e.target.value) || 0 }))}
                      inputProps={{ min: 0 }}
                      helperText="Hora extra diurna"
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth type="number" label="Recargo extra nocturna" value={form.recargoExtraNocturna ?? 75}
                      onChange={(e) => setForm(f => ({ ...f, recargoExtraNocturna: parseInt(e.target.value) || 0 }))}
                      inputProps={{ min: 0 }}
                      helperText="Hora extra nocturna"
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={() => setDialog({ open: false, data: null })}>Cancelar</Button><Button variant="contained" onClick={handleSave}>Guardar</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

// ==========================================================================================
// GESTOR DE PLANTILLAS DE INSTRUCCIONES
// ==========================================================================================

const TEMPLATE_TYPES = [
  { value: 'sin_coordinador', label: 'Sin coordinador en sitio', color: '#ef4444', icon: '🔴' },
  { value: 'con_coordinador', label: 'Con coordinador en sitio', color: '#10b981', icon: '🟢' },
  { value: 'manual_equipo',   label: 'Manual de equipo',         color: '#6366f1', icon: '📖' },
  { value: 'otro',            label: 'Otro',                     color: '#6b7280', icon: '📋' },
];

const STEP_CATEGORIES = [
  { value: 'llegada',      label: '📍 Llegada al lugar' },
  { value: 'verificacion', label: '✅ Verificación' },
  { value: 'montaje',      label: '🔧 Montaje' },
  { value: 'conexion',     label: '⚡ Conexión eléctrica' },
  { value: 'seguridad',    label: '🦺 Seguridad' },
  { value: 'operacion',    label: '🎪 Operación del equipo' },
  { value: 'cierre',       label: '🔒 Cierre / Desmontaje' },
  { value: 'otro',         label: '📝 Otro' },
];

const EMPTY_STEP = { orden: 1, texto: '', categoria: 'otro', requiereConfirmacion: true, esObligatoria: true };
const EMPTY_TPL  = { name: '', type: 'sin_coordinador', description: '', steps: [], variables: [] };

const VARIABLES_HINT = [
  '{coordinador_nombre}', '{coordinador_telefono}',
  '{contratante_nombre}', '{contratante_telefono}',
  '{direccion}', '{nombre_equipo}', '{hora_inicio}',
];

const TemplateDialog = ({ open, onClose, template, onSave }) => {
  const [form, setForm]     = useState(EMPTY_TPL);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(template ? { ...EMPTY_TPL, ...template, steps: [...(template.steps || [])] } : { ...EMPTY_TPL });
  }, [template, open]);

  const upd = (f, v) => setForm(p => ({ ...p, [f]: v }));

  // Pasos
  const addStep = () => setForm(p => ({
    ...p,
    steps: [...p.steps, { ...EMPTY_STEP, orden: p.steps.length + 1 }]
  }));
  const removeStep = (i) => setForm(p => ({ ...p, steps: p.steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, orden: idx + 1 })) }));
  const updStep = (i, f, v) => setForm(p => {
    const steps = [...p.steps];
    steps[i] = { ...steps[i], [f]: v };
    return { ...p, steps };
  });
  const moveStep = (i, dir) => setForm(p => {
    const steps = [...p.steps];
    const j = i + dir;
    if (j < 0 || j >= steps.length) return p;
    [steps[i], steps[j]] = [steps[j], steps[i]];
    return { ...p, steps: steps.map((s, idx) => ({ ...s, orden: idx + 1 })) };
  });

  const handleSave = async () => {
    if (!form.name.trim()) { alert('El nombre es requerido'); return; }
    setSaving(true);
    await onSave(form);
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700}>
            {template?._id ? 'Editar Plantilla' : 'Nueva Plantilla de Instrucciones'}
          </Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={8}>
            <TextField fullWidth required label="Nombre de la plantilla" value={form.name} onChange={e => upd('name', e.target.value)} placeholder="Ej: Montaje sin coordinador - Inflable Grande" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth><InputLabel>Tipo / Situación</InputLabel>
              <Select value={form.type} label="Tipo / Situación" onChange={e => upd('type', e.target.value)}>
                {TEMPLATE_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.icon} {t.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={2} label="Descripción (cuándo usar esta plantilla)" value={form.description} onChange={e => upd('description', e.target.value)} />
          </Grid>
        </Grid>

        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Variables disponibles</strong> (se reemplazarán automáticamente):{' '}
          {VARIABLES_HINT.map(v => <code key={v} style={{ marginRight: 6, background: '#e0e7ff', padding: '1px 4px', borderRadius: 3 }}>{v}</code>)}
        </Alert>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>Pasos / Instrucciones ({form.steps.length})</Typography>
          <Button size="small" variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={addStep}>Agregar paso</Button>
        </Box>

        {form.steps.length === 0 && (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            No hay pasos aún. Haz clic en "Agregar paso" para comenzar.
          </Paper>
        )}

        <Stack spacing={1.5}>
          {form.steps.map((step, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderLeft: `4px solid ${STEP_CATEGORIES.find(c => c.value === step.categoria)?.value === step.categoria ? '#6366f1' : '#6366f1'}` }}>
              <Grid container spacing={1} alignItems="flex-start">
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth multiline minRows={2} size="small"
                    label={`Paso ${i + 1}`}
                    value={step.texto}
                    onChange={e => updStep(i, 'texto', e.target.value)}
                    placeholder="Escribe la instrucción aquí. Puedes usar {variables}"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small"><InputLabel>Categoría</InputLabel>
                    <Select value={step.categoria} label="Categoría" onChange={e => updStep(i, 'categoria', e.target.value)}>
                      {STEP_CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <FormControlLabel
                    control={<Checkbox checked={step.requiereConfirmacion} onChange={e => updStep(i, 'requiereConfirmacion', e.target.checked)} size="small" />}
                    label={<Typography variant="caption">Requiere confirmación</Typography>}
                  />
                </Grid>
                <Grid item xs={6} sm={1} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Tooltip title="Subir"><span><IconButton size="small" onClick={() => moveStep(i, -1)} disabled={i === 0}><ArrowUpwardIcon fontSize="small" /></IconButton></span></Tooltip>
                  <Tooltip title="Bajar"><span><IconButton size="small" onClick={() => moveStep(i, 1)} disabled={i === form.steps.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton></span></Tooltip>
                  <Tooltip title="Eliminar paso"><IconButton size="small" color="error" onClick={() => removeStep(i)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}>
          {saving ? 'Guardando...' : 'Guardar plantilla'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const InstructionsManager = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [dialog, setDialog]       = useState({ open: false, data: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API_URL}/instructions?isActive=true`);
      const d = await r.json();
      if (d.ok) setTemplates(d.templates || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    const method = form._id ? 'PUT' : 'POST';
    const url    = form._id ? `${API_URL}/instructions/${form._id}` : `${API_URL}/instructions`;
    await authFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    load();
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`¿Archivar la plantilla "${tpl.name}"?`)) return;
    await authFetch(`${API_URL}/instructions/${tpl._id}`, { method: 'DELETE' });
    load();
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Plantillas de instrucciones</Typography>
            <Typography variant="body2" color="text.secondary">
              Configura las instrucciones que recibirán los operarios según la situación del evento.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, data: null })}>Nueva plantilla</Button>
        </Box>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {templates.length === 0 && !loading && (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <AssignmentIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary" gutterBottom>No hay plantillas creadas</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, data: null })}>
            Crear primera plantilla
          </Button>
        </Paper>
      )}

      <Grid container spacing={2}>
        {TEMPLATE_TYPES.map(type => {
          const group = templates.filter(t => t.type === type.value);
          if (!group.length) return null;
          return (
            <Grid item xs={12} key={type.value}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: type.color }}>
                {type.icon} {type.label} ({group.length})
              </Typography>
              <Grid container spacing={2}>
                {group.map(tpl => (
                  <Grid item xs={12} sm={6} md={4} key={tpl._id}>
                    <Card variant="outlined" sx={{ height: '100%', borderLeft: `4px solid ${type.color}` }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>{tpl.name}</Typography>
                        {tpl.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{tpl.description}</Typography>}
                        <Chip label={`${tpl.steps?.length || 0} pasos`} size="small" sx={{ mb: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
                          <Tooltip title="Editar"><IconButton size="small" color="primary" onClick={() => setDialog({ open: true, data: tpl })}><EditIcon /></IconButton></Tooltip>
                          <Tooltip title="Archivar"><IconButton size="small" color="error" onClick={() => handleDelete(tpl)}><DeleteIcon /></IconButton></Tooltip>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          );
        })}
      </Grid>

      <TemplateDialog open={dialog.open} onClose={() => setDialog({ open: false, data: null })} template={dialog.data} onSave={handleSave} />
    </Box>
  );
};

// ===================== ALCANCÍA MARRANO =====================

const playCoinSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(1400, ctx.currentTime);
    osc1.frequency.exponentialRampToValueAtTime(700, ctx.currentTime + 0.15);
    gain1.gain.setValueAtTime(0.4, ctx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(ctx.currentTime);
    osc1.stop(ctx.currentTime + 0.35);
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(900, ctx.currentTime + 0.05);
    osc2.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.3);
    gain2.gain.setValueAtTime(0.2, ctx.currentTime + 0.05);
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(ctx.currentTime + 0.05);
    osc2.stop(ctx.currentTime + 0.4);
  } catch (e) { /* silencioso */ }
};

const PigSVG = ({ isShaking, coinFly }) => (
  <Box sx={{ position: 'relative', display: 'inline-block' }}>
    <Box sx={{
      animation: isShaking ? 'pigShake 0.5s ease' : 'pigFloat 3s ease-in-out infinite',
      display: 'inline-block',
      filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.2))',
      '@keyframes pigFloat': {
        '0%,100%': { transform: 'translateY(0px) rotate(0deg)' },
        '50%': { transform: 'translateY(-8px) rotate(1deg)' },
      },
      '@keyframes pigShake': {
        '0%': { transform: 'rotate(0deg)' },
        '20%': { transform: 'rotate(-8deg) scale(1.05)' },
        '40%': { transform: 'rotate(8deg) scale(1.08)' },
        '60%': { transform: 'rotate(-6deg) scale(1.05)' },
        '80%': { transform: 'rotate(4deg)' },
        '100%': { transform: 'rotate(0deg)' },
      },
    }}>
      <svg viewBox="0 0 220 240" width="180" height="180" xmlns="http://www.w3.org/2000/svg">
        {/* Sombra */}
        <ellipse cx="110" cy="230" rx="60" ry="10" fill="rgba(0,0,0,0.12)" />
        {/* Cuerpo */}
        <ellipse cx="110" cy="158" rx="72" ry="60" fill="#FFB3C6" />
        {/* Cabeza */}
        <circle cx="110" cy="88" r="52" fill="#FFB3C6" />
        {/* Oreja izquierda */}
        <ellipse cx="74" cy="48" rx="22" ry="28" fill="#FF8FAB" transform="rotate(-15,74,48)" />
        <ellipse cx="74" cy="48" rx="13" ry="18" fill="#FFCCD5" transform="rotate(-15,74,48)" />
        {/* Oreja derecha */}
        <ellipse cx="146" cy="48" rx="22" ry="28" fill="#FF8FAB" transform="rotate(15,146,48)" />
        <ellipse cx="146" cy="48" rx="13" ry="18" fill="#FFCCD5" transform="rotate(15,146,48)" />
        {/* Mejillas */}
        <ellipse cx="80" cy="98" rx="14" ry="10" fill="#FF9EB5" opacity="0.6" />
        <ellipse cx="140" cy="98" rx="14" ry="10" fill="#FF9EB5" opacity="0.6" />
        {/* Ojos */}
        <circle cx="93" cy="80" r="9" fill="white" />
        <circle cx="127" cy="80" r="9" fill="white" />
        <circle cx="95" cy="82" r="5" fill="#222" />
        <circle cx="129" cy="82" r="5" fill="#222" />
        <circle cx="97" cy="80" r="2" fill="white" />
        <circle cx="131" cy="80" r="2" fill="white" />
        {/* Hocico */}
        <ellipse cx="110" cy="107" rx="24" ry="16" fill="#FF8FAB" />
        <ellipse cx="103" cy="108" r="6" fill="#d4607a" opacity="0.65" />
        <ellipse cx="117" cy="108" r="6" fill="#d4607a" opacity="0.65" />
        {/* Sonrisa */}
        <path d="M 95 120 Q 110 130 125 120" stroke="#d4607a" strokeWidth="3" fill="none" strokeLinecap="round" />
        {/* Patas delanteras */}
        <rect x="72" y="200" width="24" height="30" rx="12" fill="#FFB3C6" />
        <rect x="124" y="200" width="24" height="30" rx="12" fill="#FFB3C6" />
        {/* Pezuñas */}
        <rect x="72" y="220" width="24" height="10" rx="5" fill="#FF8FAB" />
        <rect x="124" y="220" width="24" height="10" rx="5" fill="#FF8FAB" />
        {/* Cola */}
        <path d="M 180 148 Q 198 135 194 120 Q 190 105 200 98" stroke="#FF8FAB" strokeWidth="5.5" fill="none" strokeLinecap="round" />
        {/* Ranura moneda */}
        <rect x="95" y="36" width="30" height="8" rx="4" fill="#8B4513" opacity="0.85" />
        <rect x="97" y="38" width="26" height="4" rx="2" fill="#6B3310" opacity="0.5" />
      </svg>
    </Box>
    {/* Moneda animada volando */}
    {coinFly && (
      <Box sx={{
        position: 'absolute', top: 10, left: '50%',
        transform: 'translateX(-50%)',
        fontSize: 28,
        animation: 'coinDrop 0.6s ease-in forwards',
        '@keyframes coinDrop': {
          '0%': { transform: 'translateX(-50%) translateY(-30px) scale(1.2)', opacity: 1 },
          '100%': { transform: 'translateX(-50%) translateY(30px) scale(0.3)', opacity: 0 },
        },
      }}>🪙</Box>
    )}
  </Box>
);

const AlcanciaView = ({ employeeId, employeeName, isAdmin, authUser: authUserProp, pendingPayment: pendingProp = 0, onUpdate }) => {
  const [savings, setSavings] = useState({ balance: 0, transactions: [] });
  const [loading, setLoading] = useState(true);
  const [isShaking, setIsShaking] = useState(false);
  const [coinFly, setCoinFly] = useState(false);
  const [depositDialog, setDepositDialog] = useState(false);
  const [withdrawDialog, setWithdrawDialog] = useState(false);
  const [creditDialog, setCreditDialog] = useState(false);
  const [amount, setAmount] = useState('');
  const [desc, setDesc] = useState('');
  const [creditType, setCreditType] = useState('credito_admin');
  const [busy, setBusy] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');

  const fmt = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);

  const loadSavings = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await authFetch(`${API_URL}/staff/employees/${employeeId}/savings`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const d = await r.json();
      if (d.ok) setSavings(d);
    } catch (e) { /* silencioso */ }
    setLoading(false);
  };

  useEffect(() => { if (employeeId) loadSavings(); }, [employeeId]);

  const doDeposit = async () => {
    const n = parseInt(amount.replace(/\D/g, ''), 10);
    if (!n || n <= 0) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await authFetch(`${API_URL}/staff/employees/${employeeId}/savings/deposit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ monto: n, descripcion: desc || 'Depósito al marrano' }),
      });
      const d = await r.json();
      if (d.ok) {
        playCoinSound();
        setIsShaking(true);
        setCoinFly(true);
        setTimeout(() => { setIsShaking(false); setCoinFly(false); }, 700);
        await loadSavings();
        setDepositDialog(false); setAmount(''); setDesc('');
        setSnackMsg('🐷 ¡Moneda al marrano! Tu ahorro creció');
        if (onUpdate) onUpdate();
      } else setSnackMsg(d.error || 'Error al depositar');
    } catch (e) { setSnackMsg('Error de conexión'); }
    setBusy(false);
  };

  const doWithdraw = async () => {
    const n = parseInt(amount.replace(/\D/g, ''), 10);
    if (!n || n <= 0) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await authFetch(`${API_URL}/staff/employees/${employeeId}/savings/withdraw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ monto: n, descripcion: desc || 'Retiro del marrano' }),
      });
      const d = await r.json();
      if (d.ok) {
        await loadSavings();
        setWithdrawDialog(false); setAmount(''); setDesc('');
        setSnackMsg('💸 Retiro exitoso');
        if (onUpdate) onUpdate();
      } else setSnackMsg(d.error || 'Saldo insuficiente');
    } catch (e) { setSnackMsg('Error de conexión'); }
    setBusy(false);
  };

  const doCredit = async () => {
    const n = parseInt(amount.replace(/\D/g, ''), 10);
    if (!n || n <= 0) return;
    setBusy(true);
    try {
      const r = await authFetch(`${API_URL}/staff/employees/${employeeId}/savings/credit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monto: n, descripcion: desc, tipo: creditType, registradoPor: authUserProp?.username || 'admin' }),
      });
      const d = await r.json();
      if (d.ok) {
        playCoinSound();
        setIsShaking(true); setCoinFly(true);
        setTimeout(() => { setIsShaking(false); setCoinFly(false); }, 700);
        await loadSavings();
        setCreditDialog(false); setAmount(''); setDesc('');
        setSnackMsg('✅ Crédito registrado exitosamente');
      } else setSnackMsg(d.error || 'Error');
    } catch (e) { setSnackMsg('Error de conexión'); }
    setBusy(false);
  };

  const tipoColor = { deposito: '#2e7d32', retiro: '#c62828', premio_sorteo: '#e65100', bono_navidad: '#1565c0', credito_admin: '#6a1b9a' };
  const tipoLabel = { deposito: 'Depósito', retiro: 'Retiro', premio_sorteo: '🎲 Premio Sorteo', bono_navidad: '🎄 Bono Navidad', credito_admin: '⭐ Crédito Admin' };
  const tipoIcon = { deposito: '⬆️', retiro: '⬇️', premio_sorteo: '🎲', bono_navidad: '🎄', credito_admin: '⭐' };

  if (loading) return <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {/* Card principal - marrano + balance */}
      <Paper elevation={0} sx={{
        background: 'linear-gradient(135deg, #FF6B9D 0%, #FF8FAB 40%, #FFB3C6 100%)',
        borderRadius: 4, p: 3, mb: 3, textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        {/* Burbujas decorativas */}
        <Box sx={{ position: 'absolute', top: -20, right: -20, width: 120, height: 120, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)' }} />
        <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />

        <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600, mb: 1 }}>
          🐷 Mi Alcancía
          {employeeName && <Typography component="span" sx={{ fontSize: '0.8rem', opacity: 0.85, ml: 1 }}>— {employeeName}</Typography>}
        </Typography>

        <Box sx={{ my: 2 }}>
          <PigSVG isShaking={isShaking} coinFly={coinFly} />
        </Box>

        <Typography variant="h3" sx={{ color: 'white', fontWeight: 800, textShadow: '0 2px 8px rgba(0,0,0,0.2)', letterSpacing: '-1px' }}>
          {fmt(savings.balance)}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.5 }}>
          Saldo actual en tu marrano
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            onClick={() => { setDepositDialog(true); setAmount(''); setDesc(''); }}
            sx={{ bgcolor: 'white', color: '#d4607a', fontWeight: 700, borderRadius: 3, px: 3, '&:hover': { bgcolor: '#fff0f3' } }}
            startIcon={<span>🪙</span>}
          >
            Depositar
          </Button>
          <Button
            variant="outlined"
            onClick={() => { setWithdrawDialog(true); setAmount(''); setDesc(''); }}
            disabled={savings.balance <= 0}
            sx={{ borderColor: 'white', color: 'white', fontWeight: 700, borderRadius: 3, px: 3, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', borderColor: 'white' } }}
            startIcon={<span>💸</span>}
          >
            Retirar
          </Button>
          {isAdmin && (
            <Button
              variant="outlined"
              onClick={() => { setCreditDialog(true); setAmount(''); setDesc(''); }}
              sx={{ borderColor: 'rgba(255,255,255,0.7)', color: 'white', fontWeight: 700, borderRadius: 3, px: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
              startIcon={<span>⭐</span>}
            >
              Creditar
            </Button>
          )}
        </Box>
      </Paper>

      {/* Cards de beneficios */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Bono Navidad */}
        <Grid item xs={12} sm={6}>
          <Paper elevation={2} sx={{
            p: 2.5, borderRadius: 3,
            background: 'linear-gradient(135deg, #1a237e, #283593)',
            color: 'white', position: 'relative', overflow: 'hidden',
          }}>
            <Box sx={{ position: 'absolute', top: -15, right: -15, fontSize: 60, opacity: 0.15 }}>🎄</Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, fontSize: '1.1rem', mb: 0.5 }}>
              🎄 Bono Navidad 15%
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.5 }}>
              La liquidación se realizará el <strong>15 de Diciembre</strong> y aplicará sobre los dineros que lleven <strong>más de 30 días</strong> guardados en tu marrano antes de esa fecha.
            </Typography>
            <Box sx={{ mt: 1.5, bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 2, p: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Ej: Si tienes $200.000 ahorrados → recibes $30.000 extra directo al marrano 🐷
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Sorteos */}
        <Grid item xs={12} sm={6}>
          <Paper elevation={2} sx={{
            p: 2.5, borderRadius: 3,
            background: 'linear-gradient(135deg, #e65100, #f57c00)',
            color: 'white', position: 'relative', overflow: 'hidden',
          }}>
            <Box sx={{ position: 'absolute', top: -15, right: -15, fontSize: 60, opacity: 0.15 }}>🎲</Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, fontSize: '1.1rem', mb: 0.5 }}>
              🎲 Sorteos de Ahorradores
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.5 }}>
              Durante el año Da Silva realiza <strong>sorteos especiales</strong> para ahorradores. Los ganadores reciben un <strong>15% de su saldo</strong> directo al marrano.
            </Typography>
            <Box sx={{ mt: 1.5, bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 2, p: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                ¡Entre más ahorres, más ganas! El premio es proporcional a tu saldo 🏆
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Historial de movimientos */}
      <Paper elevation={1} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 2, bgcolor: '#fce4ec', borderBottom: '1px solid #f8bbd9' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#880e4f' }}>
            📋 Historial de Movimientos
          </Typography>
        </Box>
        {savings.transactions.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 48, mb: 1 }}>🐷</Typography>
            <Typography color="text.secondary">Tu marrano está vacío... ¡empieza a ahorrar!</Typography>
          </Box>
        ) : (
          <List dense sx={{ p: 0 }}>
            {savings.transactions.map((t, i) => (
              <React.Fragment key={i}>
                <ListItem sx={{ px: 2.5, py: 1.5 }}>
                  <Box sx={{ mr: 2, fontSize: 22 }}>{tipoIcon[t.tipo] || '💰'}</Box>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: tipoColor[t.tipo] }}>
                          {tipoLabel[t.tipo] || t.tipo}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 800, color: tipoColor[t.tipo] }}>
                          {t.tipo === 'retiro' ? '-' : '+'}{fmt(t.monto)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">{t.descripcion}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(t.fecha).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {i < savings.transactions.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Dialog Depositar */}
      <Dialog open={depositDialog} onClose={() => setDepositDialog(false)} PaperProps={{ sx: { borderRadius: 4, minWidth: 320 } }}>
        <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
          <Typography sx={{ fontSize: 48 }}>🪙</Typography>
          <Typography variant="h6" fontWeight={700}>Depositar al Marrano</Typography>
          {!isAdmin && (
            <Typography variant="body2" color={pendingProp > 0 ? '#2e7d32' : '#c62828'} fontWeight={600}>
              Saldo por cobrar disponible: {fmt(pendingProp)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth label="¿Cuánto vas a ahorrar? (COP)" type="number"
            value={amount} onChange={e => setAmount(e.target.value)}
            sx={{ mb: 2 }} autoFocus
            inputProps={{ min: 1000, step: 1000, max: isAdmin ? undefined : pendingProp }}
            error={!isAdmin && !!amount && parseInt(amount) > pendingProp}
            helperText={!isAdmin && !!amount && parseInt(amount) > pendingProp ? `Máximo disponible: ${fmt(pendingProp)}` : ''}
          />
          <TextField fullWidth label="Nota (opcional)" value={desc} onChange={e => setDesc(e.target.value)} placeholder="Ej: Ganancia del evento del sábado" />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDepositDialog(false)} sx={{ borderRadius: 2 }}>Cancelar</Button>
          <Button variant="contained" onClick={doDeposit}
            disabled={busy || !amount || (!isAdmin && parseInt(amount) > pendingProp)}
            sx={{ bgcolor: '#FF6B9D', '&:hover': { bgcolor: '#e05585' }, borderRadius: 2, fontWeight: 700 }}>
            {busy ? <CircularProgress size={20} /> : '🐷 ¡Al marrano!'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Retirar */}
      <Dialog open={withdrawDialog} onClose={() => setWithdrawDialog(false)} PaperProps={{ sx: { borderRadius: 4, minWidth: 320 } }}>
        <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
          <Typography sx={{ fontSize: 48 }}>💸</Typography>
          <Typography variant="h6" fontWeight={700}>Retirar del Marrano</Typography>
          <Typography variant="body2" color="text.secondary">Saldo disponible: {fmt(savings.balance)}</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth label="¿Cuánto vas a retirar? (COP)" type="number"
            value={amount} onChange={e => setAmount(e.target.value)}
            sx={{ mb: 2 }} autoFocus inputProps={{ min: 1000, max: savings.balance, step: 1000 }}
          />
          <TextField fullWidth label="Motivo (opcional)" value={desc} onChange={e => setDesc(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setWithdrawDialog(false)} sx={{ borderRadius: 2 }}>Cancelar</Button>
          <Button variant="contained" onClick={doWithdraw} disabled={busy || !amount || parseInt(amount) > savings.balance}
            sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#a51d1d' }, borderRadius: 2, fontWeight: 700 }}>
            {busy ? <CircularProgress size={20} /> : 'Retirar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Creditar (admin) */}
      {isAdmin && (
        <Dialog open={creditDialog} onClose={() => setCreditDialog(false)} PaperProps={{ sx: { borderRadius: 4, minWidth: 340 } }}>
          <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
            <Typography sx={{ fontSize: 48 }}>⭐</Typography>
            <Typography variant="h6" fontWeight={700}>Agregar Crédito a {employeeName}</Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Tipo de crédito</InputLabel>
              <Select value={creditType} onChange={e => setCreditType(e.target.value)} label="Tipo de crédito">
                <MenuItem value="credito_admin">⭐ Crédito Administrativo</MenuItem>
                <MenuItem value="premio_sorteo">🎲 Premio Sorteo</MenuItem>
                <MenuItem value="bono_navidad">🎄 Bono Navidad</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth label="Monto (COP)" type="number"
              value={amount} onChange={e => setAmount(e.target.value)}
              sx={{ mb: 2 }} autoFocus inputProps={{ min: 1000, step: 1000 }}
            />
            <TextField fullWidth label="Descripción" value={desc} onChange={e => setDesc(e.target.value)} placeholder="Ej: Premio sorteo Marzo 2026" />
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={() => setCreditDialog(false)} sx={{ borderRadius: 2 }}>Cancelar</Button>
            <Button variant="contained" onClick={doCredit} disabled={busy || !amount}
              sx={{ bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#6a1b9a' }, borderRadius: 2, fontWeight: 700 }}>
              {busy ? <CircularProgress size={20} /> : '✅ Registrar'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Snackbar */}
      <Snackbar open={!!snackMsg} autoHideDuration={3000} onClose={() => setSnackMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackMsg('')} severity="success" variant="filled" sx={{ borderRadius: 3 }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ===================== ALCANCÍAS ADMIN (vista de todas) =====================
const AlcanciaAdminTab = ({ employees: allEmployees, authUser: authUserProp }) => {
  const [savingsData, setSavingsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [raffleDialog, setRaffleDialog] = useState(false);
  const [raffleWinners, setRaffleWinners] = useState([]);
  const [raffleResults, setRaffleResults] = useState([]);
  const [snackMsg, setSnackMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const fmt = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);

  const loadAll = async () => {
    try {
      const r = await authFetch(`${API_URL}/staff/savings/all`);
      const d = await r.json();
      if (d.ok) setSavingsData(d.employees);
    } catch (e) { /* silencioso */ }
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const totalAhorros = savingsData.reduce((s, e) => s + e.balance, 0);
  const ahorradores = savingsData.filter(e => e.balance > 0);

  const doRaffle = async () => {
    if (raffleWinners.length === 0) return;
    setBusy(true);
    const results = [];
    for (const empId of raffleWinners) {
      const emp = savingsData.find(e => e._id === empId);
      if (!emp || emp.balance <= 0) continue;
      const prize = Math.round(emp.balance * 0.15);
      try {
        const r = await authFetch(`${API_URL}/staff/employees/${empId}/savings/credit`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ monto: prize, tipo: 'premio_sorteo', descripcion: `Premio sorteo ${new Date().toLocaleDateString('es-CO')}`, registradoPor: authUserProp?.username || 'admin' }),
        });
        const d = await r.json();
        results.push({ nombre: emp.nombre, premio: prize, ok: d.ok });
      } catch (e) { results.push({ nombre: emp.nombre, premio: 0, ok: false }); }
    }
    setRaffleResults(results);
    await loadAll();
    setBusy(false);
    setRaffleWinners([]);
  };

  const doChristmasBonus = async () => {
    if (!window.confirm('¿Aplicar bono navidad 15% a todos los ahorradores elegibles?')) return;
    setBusy(true);
    for (const emp of ahorradores) {
      if (!emp.ultimoDeposito) continue;
      const diasAhorrado = (new Date() - new Date(emp.ultimoDeposito)) / (1000 * 60 * 60 * 24);
      if (diasAhorrado < 30) continue;
      const bono = Math.round(emp.balance * 0.15);
      await authFetch(`${API_URL}/staff/employees/${emp._id}/savings/credit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monto: bono, tipo: 'bono_navidad', descripcion: `Bono Navidad 15% - Dic ${new Date().getFullYear()}`, registradoPor: authUserProp?.username || 'admin' }),
      });
    }
    await loadAll();
    setBusy(false);
    setSnackMsg('🎄 Bono Navidad aplicado a todos los elegibles');
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      {/* Stats top */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center', bgcolor: '#fce4ec' }}>
            <Typography sx={{ fontSize: 28 }}>🐷</Typography>
            <Typography variant="h5" fontWeight={800} color="#c2185b">{fmt(totalAhorros)}</Typography>
            <Typography variant="caption" color="text.secondary">Total ahorrado</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center', bgcolor: '#e8f5e9' }}>
            <Typography sx={{ fontSize: 28 }}>💰</Typography>
            <Typography variant="h5" fontWeight={800} color="#2e7d32">{ahorradores.length}</Typography>
            <Typography variant="caption" color="text.secondary">Ahorradores</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center', bgcolor: '#fff3e0' }}>
            <Typography sx={{ fontSize: 28 }}>🎲</Typography>
            <Typography variant="h5" fontWeight={800} color="#e65100">{fmt(totalAhorros * 0.15)}</Typography>
            <Typography variant="caption" color="text.secondary">Premio 15% total</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center', bgcolor: '#e8eaf6' }}>
            <Typography sx={{ fontSize: 28 }}>🎄</Typography>
            <Typography variant="h5" fontWeight={800} color="#3949ab">{fmt(ahorradores.reduce((s,e) => s + Math.round(e.balance * 0.15), 0))}</Typography>
            <Typography variant="caption" color="text.secondary">Bono Navidad est.</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Botones de acción */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button variant="contained" startIcon={<span>🎲</span>}
          onClick={() => { setRaffleDialog(true); setRaffleResults([]); setRaffleWinners([]); }}
          sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' }, borderRadius: 2, fontWeight: 700 }}>
          Hacer Sorteo
        </Button>
        <Button variant="contained" startIcon={<span>🎄</span>}
          onClick={doChristmasBonus} disabled={busy}
          sx={{ bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d1466' }, borderRadius: 2, fontWeight: 700 }}>
          Bono Navidad 15%
        </Button>
      </Box>

      {/* Lista de empleados */}
      {savingsData.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography sx={{ fontSize: 48 }}>🐷</Typography>
          <Typography color="text.secondary">Ningún empleado tiene ahorros todavía</Typography>
        </Paper>
      ) : (
        <Paper elevation={1} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <List disablePadding>
            {savingsData.map((emp, i) => (
              <React.Fragment key={emp._id}>
                <ListItem
                  secondaryAction={
                    <Button size="small" variant="outlined"
                      onClick={() => setSelectedEmp(emp)}
                      sx={{ borderRadius: 2, fontSize: '0.75rem' }}>
                      Ver / Creditar
                    </Button>
                  }
                  sx={{ px: 2.5, py: 1.5 }}
                >
                  <Box sx={{ mr: 1.5, fontSize: 28 }}>
                    {emp.balance > 0 ? (i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : '🐷') : '🐽'}
                  </Box>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" fontWeight={700}>{emp.nombre}</Typography>
                        <Chip label={emp.employeeCode} size="small" sx={{ fontSize: '0.7rem' }} />
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color={emp.balance > 0 ? '#2e7d32' : 'text.secondary'} fontWeight={emp.balance > 0 ? 700 : 400}>
                        {fmt(emp.balance)} {emp.balance > 0 ? `→ Premio 15%: ${fmt(Math.round(emp.balance * 0.15))}` : '(sin ahorros)'}
                      </Typography>
                    }
                  />
                </ListItem>
                {i < savingsData.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* Dialog ver alcancía de empleado específico */}
      {selectedEmp && (
        <Dialog open={!!selectedEmp} onClose={() => setSelectedEmp(null)} maxWidth="sm" fullWidth
          PaperProps={{ sx: { borderRadius: 4 } }}>
          <DialogTitle sx={{ pb: 0 }}>
            🐷 Alcancía de {selectedEmp.nombre}
            <IconButton onClick={() => setSelectedEmp(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>✕</IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <AlcanciaView employeeId={selectedEmp._id} employeeName={selectedEmp.nombre} isAdmin={true} authUser={authUserProp} />
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog Sorteo */}
      <Dialog open={raffleDialog} onClose={() => setRaffleDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: 42 }}>🎲</Typography>
          <Typography variant="h6" fontWeight={700}>Sorteo de Ahorradores</Typography>
          <Typography variant="body2" color="text.secondary">El ganador recibe el 15% de su saldo ahorrado</Typography>
        </DialogTitle>
        <DialogContent>
          {raffleResults.length > 0 ? (
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2, textAlign: 'center' }}>🏆 Resultados del Sorteo</Typography>
              {raffleResults.map((r, i) => (
                <Paper key={i} sx={{ p: 2, mb: 1, borderRadius: 2, bgcolor: r.ok ? '#e8f5e9' : '#ffebee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography fontWeight={700}>{r.ok ? '🎉' : '❌'} {r.nombre}</Typography>
                  {r.ok && <Chip label={`+${fmt(r.premio)}`} color="success" size="small" sx={{ fontWeight: 700 }} />}
                </Paper>
              ))}
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Selecciona los ganadores del sorteo (pueden ser uno o varios):
              </Typography>
              {ahorradores.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>No hay ahorradores con saldo</Typography>
              ) : (
                ahorradores.map(emp => (
                  <Paper key={emp._id} sx={{
                    p: 1.5, mb: 1, borderRadius: 2, cursor: 'pointer',
                    bgcolor: raffleWinners.includes(emp._id) ? '#fff3e0' : 'white',
                    border: raffleWinners.includes(emp._id) ? '2px solid #e65100' : '1px solid #eee',
                  }}
                    onClick={() => setRaffleWinners(w => w.includes(emp._id) ? w.filter(x => x !== emp._id) : [...w, emp._id])}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography fontWeight={700}>{raffleWinners.includes(emp._id) ? '✅' : '⬜'} {emp.nombre}</Typography>
                        <Typography variant="caption" color="text.secondary">Saldo: {fmt(emp.balance)} → Premio: {fmt(Math.round(emp.balance * 0.15))}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => { setRaffleDialog(false); setRaffleResults([]); setRaffleWinners([]); }} sx={{ borderRadius: 2 }}>
            {raffleResults.length > 0 ? 'Cerrar' : 'Cancelar'}
          </Button>
          {raffleResults.length === 0 && (
            <Button variant="contained" onClick={doRaffle} disabled={busy || raffleWinners.length === 0}
              sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' }, borderRadius: 2, fontWeight: 700 }}>
              {busy ? <CircularProgress size={20} /> : `🎲 Aplicar Premio (${raffleWinners.length} ganadores)`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackMsg} autoHideDuration={4000} onClose={() => setSnackMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" sx={{ borderRadius: 3 }}>{snackMsg}</Alert>
      </Snackbar>
    </Box>
  );
};

// ===================== COMPONENTE PRINCIPAL =====================
export default function StaffModule() {
  const authUserMain = useMemo(() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } }, []);
  const isCoordinador = authUserMain?.role === 'manager';

  // Tabs visibles según rol: coordinador solo ve Programar y Asistencia
  const COORDINATOR_TABS = new Set(['schedule', 'attendance', 'employees']);
  const visibleTabs = isCoordinador ? TABS.filter(t => COORDINATOR_TABS.has(t.id)) : TABS;
  const defaultTab = isCoordinador ? 'attendance' : 'dashboard';

  const [tab, setTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [coordEmployees, setCoordEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [todayAtt, setTodayAtt] = useState([]);
  const [pending, setPending] = useState([]);
  const authUser = useMemo(() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } }, []);
  const [credDialog, setCredDialog] = useState({ open: false, emp: null, username: '', password: '', email: '', waSent: false, created: false, loading: false });
  const [receiptDialog, setReceiptDialog] = useState({ open: false, paymentId: null, paymentNumber: '', uploading: false });
  const [histDialog, setHistDialog] = useState({ open: false, emp: null, payments: [], loading: false });
  const [myProfile, setMyProfile] = useState(null);
  const [myHistory, setMyHistory] = useState([]);
  const [myHistLoading, setMyHistLoading] = useState(false);
  const [clockForm, setClockForm] = useState({ pin: '', eventName: '', mode: 'in' });
  const [todayEvents, setTodayEvents] = useState([]);
  const [serviceOrders, setServiceOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [schedFilter, setSchedFilter] = useState('week');
  const [schedDialog, setSchedDialog] = useState({ open: false, order: null, selected: [], sendWa: true, note: '', saving: false, coordType: 'presencial', coordEmpId: '', coordVirtualName: '', coordVirtualPhone: '', manualArrivalTime: '', eventHours: '', minPayment: '' });
  const [empDialog, setEmpDialog]   = useState({ open: false, data: null });
  const [qrDialog, setQrDialog]     = useState({ open: false, data: null });
  const [evalDialog, setEvalDialog] = useState({ open: false, data: null });
  const [motivoDialog, setMotivoDialog] = useState({ open: false, emp: null, motivo: '' });
  const [openShifts, setOpenShifts] = useState([]);
  const [corrDialog, setCorrDialog] = useState({
    open: false, saving: false,
    employeeId: '', date: new Date().toISOString().slice(0, 10),
    checkInTime: '', checkOutTime: '', eventName: '', reason: ''
  });
  // Estados para auto-edición de perfil (empleado) — declarados aquí (fuera de if) por reglas de Hooks
  const [selfDialog, setSelfDialog] = useState({ open: false, saving: false, form: {} });
  const [selfPhotoFile, setSelfPhotoFile] = useState(null);
  const [pwdDialog, setPwdDialog] = useState({ open: false, current: '', next: '', confirm: '', saving: false, error: '' });

  const loadDashboard = useCallback(async () => {
    try {
      const r = await authFetch(`${API_URL}/staff/dashboard`);
      const d = await r.json();
      if (d.ok) { setStats(d.stats); setCategories(d.categories || []); }
    } catch (e) { console.error(e); }
  }, []);



  const loadServiceOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const r = await authFetch(`${API_URL}/orders/service`);
      const d = await r.json();
      if (d.ok) setServiceOrders(d.items || []);
    } catch (e) { console.error(e); }
    setOrdersLoading(false);
  }, []);

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API_URL}/staff/employees?status=todos`);
      const d = await r.json();
      if (d.ok) setEmployees(d.employees);
      // Si es coordinador, cargar también su lista filtrada
      if (authUser?.role === 'manager' && authUser?.username) {
        const r2 = await authFetch(`${API_URL}/staff/employees?status=todos&coordinator=${authUser.username}`);
        const d2 = await r2.json();
        if (d2.ok) setCoordEmployees(d2.employees);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [authUser?.role, authUser?.username]);

  const loadCategories = useCallback(async () => {
    try { const r = await authFetch(`${API_URL}/staff/categories`); const d = await r.json(); if (d.ok) setCategories(d.categories); } catch (e) { console.error(e); }
  }, []);

  const loadToday = useCallback(async () => {
    try {
      const r = await authFetch(`${API_URL}/staff/attendance/today`);
      const d = await r.json();
      if (d.ok) setTodayAtt(d.attendance);
      // También cargar turnos abiertos (solo admin/manager — employee recibe 403, se ignora)
      try {
        const ro = await authFetch(`${API_URL}/staff/attendance/open-shifts`);
        if (ro.status !== 403) {
          const do_ = await ro.json();
          if (do_.ok) setOpenShifts(do_.shifts || []);
        }
      } catch (_) {}
    } catch (e) { console.error(e); }
  }, []);

  const handleManualCorrect = async () => {
    const { employeeId, date, checkInTime, checkOutTime, eventName, reason } = corrDialog;
    if (!employeeId || !date) return;
    if (!checkInTime && !checkOutTime) return;
    setCorrDialog(p => ({ ...p, saving: true }));
    try {
      const toISO = (dateStr, timeStr) => {
        if (!timeStr) return null;
        return new Date(`${dateStr}T${timeStr}:00`).toISOString();
      };
      await authFetch(`${API_URL}/staff/attendance/manual-correct`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeId, date,
          checkInTime: toISO(date, checkInTime),
          checkOutTime: toISO(date, checkOutTime),
          eventName, reason,
          adminName: authUser?.name || authUser?.username || 'Administrador'
        })
      });
      setSnack({ open: true, message: '✅ Asistencia corregida exitosamente', severity: 'success' });
      setCorrDialog({ open: false, saving: false, employeeId: '', date: new Date().toISOString().slice(0, 10), checkInTime: '', checkOutTime: '', eventName: '', reason: '' });
      loadToday();
    } catch (e) {
      setSnack({ open: true, message: 'Error: ' + e.message, severity: 'error' });
      setCorrDialog(p => ({ ...p, saving: false }));
    }
  };

  const handleClearCheckout = async (record) => {
    if (!record.attendanceId) return setSnack({ open: true, message: 'No se puede identificar el registro', severity: 'error' });
    if (!window.confirm(`¿Quitar la salida de ${record.fullName}? El turno quedará abierto.`)) return;
    try {
      const r = await authFetch(`${API_URL}/staff/attendance/record/${record.employeeId}/${record.attendanceId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clearCheckOut: true, reason: 'Corrección admin — salida registrada por error' })
      });
      const d = await r.json();
      if (d.ok) { setSnack({ open: true, message: '✅ Salida eliminada — turno abierto nuevamente', severity: 'success' }); loadToday(); }
      else setSnack({ open: true, message: 'Error: ' + d.error, severity: 'error' });
    } catch (e) { setSnack({ open: true, message: 'Error: ' + e.message, severity: 'error' }); }
  };

  const handleDeleteAttRecord = async (record) => {
    if (!record.attendanceId) return setSnack({ open: true, message: 'No se puede identificar el registro', severity: 'error' });
    if (!window.confirm(`¿Eliminar COMPLETO el registro de turno de ${record.fullName}? Esta acción no se puede deshacer.`)) return;
    try {
      const r = await authFetch(`${API_URL}/staff/attendance/record/${record.employeeId}/${record.attendanceId}`, { method: 'DELETE' });
      const d = await r.json();
      if (d.ok) { setSnack({ open: true, message: '✅ Registro eliminado', severity: 'success' }); loadToday(); }
      else setSnack({ open: true, message: 'Error: ' + d.error, severity: 'error' });
    } catch (e) { setSnack({ open: true, message: 'Error: ' + e.message, severity: 'error' }); }
  };

  const handleEditAttRecord = (record) => {
    const toTime = (iso) => iso ? new Date(iso).toTimeString().slice(0, 5) : '';
    setCorrDialog({
      open: true, saving: false,
      employeeId: String(record.employeeId),
      date: new Date(record.checkIn || Date.now()).toISOString().slice(0, 10),
      checkInTime: toTime(record.checkIn),
      checkOutTime: toTime(record.checkOut),
      eventName: record.eventName || '',
      reason: ''
    });
  };

  const loadPending = useCallback(async () => {
    try { const r = await authFetch(`${API_URL}/staff/payments/pending`); const d = await r.json(); if (d.ok) setPending(d.employees); } catch (e) { console.error(e); }
  }, []);

  const loadMyProfile = useCallback(async () => {
    if (!authUser?.username) return;
    try {
      const r = await authFetch(`${API_URL}/staff/employees?status=todos`);
      const d = await r.json();
      if (d.ok) {
        const me = d.employees.find(e => e.employeeCode === authUser.username);
        setMyProfile(me || null);
      }
    } catch (e) { console.error(e); }
  }, [authUser?.username]);

  const loadMyHistory = useCallback(async (empId) => {
    if (!empId) return;
    setMyHistLoading(true);
    try {
      const r = await authFetch(`${API_URL}/staff/portal/${empId}/history`);
      const d = await r.json();
      if (d.ok) setMyHistory(d.history || []);
    } catch (e) { console.error(e); }
    setMyHistLoading(false);
  }, []);

  useEffect(() => {
    loadDashboard(); loadCategories();
    // Cargar eventos del calendario de hoy + órdenes de servicio de hoy para el escáner
    const _hoyNow = new Date();
    const hoyStr = `${_hoyNow.getFullYear()}-${String(_hoyNow.getMonth()+1).padStart(2,'0')}-${String(_hoyNow.getDate()).padStart(2,'0')}`;

    Promise.allSettled([
      authFetch(`${API_URL.replace('/api', '')}/api/calendar/today`).then(r => r.json()).catch(() => ({ ok: false })),
      authFetch(`${API_URL}/orders/service`).then(r => r.json()).catch(() => ({ ok: false }))
    ]).then(([calRes, ordRes]) => {
      const calEvents = (calRes.status === 'fulfilled' && calRes.value.ok)
        ? (calRes.value.events || []).filter(e => e.type === 'evento' || e.type === 'reserva' || !e.type)
        : [];
      const ordEvents = (ordRes.status === 'fulfilled' && ordRes.value.ok)
        ? (ordRes.value.items || [])
            .filter(o => (o.date || o.eventDetails?.eventDate || '').slice(0, 10) === hoyStr)
            .map(o => ({ title: `${o.client?.name || 'Cliente'} — ${o.event?.city || o.eventDetails?.city || ''}`, name: o.client?.name, orderId: o.id }))
        : [];
      setTodayEvents([...calEvents, ...ordEvents]);
    });
  }, [loadDashboard, loadCategories]);
  useEffect(() => {
    if (tab === 'employees') loadEmployees();
    if (tab === 'attendance') { loadToday(); loadEmployees(); }
    if (tab === 'payments') loadPending();
    if (tab === 'categories') loadCategories();
    if (tab === 'schedule') { loadServiceOrders(); loadEmployees(); }
  }, [tab, loadEmployees, loadToday, loadPending, loadCategories, loadServiceOrders]);

  // Carga inicial para empleados (vista simplificada)
  const isEmployeeView = authUser && !['admin', 'manager'].includes(authUser.role);
  useEffect(() => {
    if (isEmployeeView) {
      loadMyProfile();
      loadToday();
    }
  }, [isEmployeeView, loadMyProfile, loadToday]);

  const handleClockIn = async (code, pin, eventName) => {
    setLoading(true);
    try {
      const r = await authFetch(`${API_URL}/staff/attendance/pin/clock-in`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ employeeCode: code, pin, eventName }) });
      const d = await r.json();
      setSnack({ open: true, message: d.ok ? d.message : d.error, severity: d.ok ? 'success' : 'error' });
      if (d.ok) loadToday();
    } catch (e) { setSnack({ open: true, message: 'Error de conexión', severity: 'error' }); }
    setLoading(false);
  };

  const handleClockOut = async (code, pin) => {
    setLoading(true);
    try {
      const r = await authFetch(`${API_URL}/staff/attendance/pin/clock-out`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ employeeCode: code, pin }) });
      const d = await r.json();
      setSnack({ open: true, message: d.ok ? `${d.message} - Ganado: ${formatCurrency(d.attendance?.earnings?.total)}` : d.error, severity: d.ok ? 'success' : 'error' });
      if (d.ok) loadToday();
    } catch (e) { setSnack({ open: true, message: 'Error de conexión', severity: 'error' }); }
    setLoading(false);
  };

  const handleSaveEmployee = async (data) => {
    const isNew = !data._id;
    const url = isNew ? `${API_URL}/staff/employees` : `${API_URL}/staff/employees/${data._id}`;
    const r = await authFetch(url, { method: isNew ? 'POST' : 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
    const d = await r.json();
    if (d.ok) {
      setSnack({ open: true, message: 'Empleado guardado', severity: 'success' });
      loadEmployees();
      // Si se creó cuenta automáticamente, mostrar credenciales
      if (isNew && d.accountInfo) {
        const acc = d.accountInfo;
        setCredDialog({
          open: true, emp: d.employee,
          username: acc.username, password: acc.password,
          email: acc.email, waSent: acc.waSent || false, created: true, loading: false
        });
      }
      return d.employee;
    }
    throw new Error(d.error);
  };

  const handleSaveCategory = async (data) => {
    const r = await authFetch(`${API_URL}/staff/categories`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
    const d = await r.json();
    if (d.ok) { setSnack({ open: true, message: 'Categoría guardada', severity: 'success' }); loadCategories(); }
  };

  // ── Eliminar empleado ──
  const [confirmDelete, setConfirmDelete] = useState({ open: false, target: null, type: '' });
  const handleDeleteEmployee = async () => {
    const emp = confirmDelete.target;
    if (!emp?._id) return;
    try {
      const r = await authFetch(`${API_URL}/staff/employees/${emp._id}`, { method: 'DELETE' });
      const d = await r.json();
      if (d.ok) {
        setSnack({ open: true, message: `${emp.firstName} ${emp.lastName} eliminado`, severity: 'success' });
        loadEmployees();
      } else {
        setSnack({ open: true, message: d.error || 'No se pudo eliminar', severity: 'error' });
      }
    } catch (e) {
      setSnack({ open: true, message: 'Error de conexión', severity: 'error' });
    }
    setConfirmDelete({ open: false, target: null, type: '' });
  };

  // ── Eliminar categoría ──
  const handleDeleteCategory = async () => {
    const cat = confirmDelete.target;
    if (!cat?._id) return;
    try {
      const r = await authFetch(`${API_URL}/staff/categories/${cat._id}`, { method: 'DELETE' });
      const d = await r.json();
      if (d.ok) {
        setSnack({ open: true, message: `Categoría "${cat.name}" eliminada`, severity: 'success' });
        loadCategories();
      } else {
        setSnack({ open: true, message: d.error || 'No se pudo eliminar', severity: 'error' });
      }
    } catch (e) {
      setSnack({ open: true, message: 'Error de conexión', severity: 'error' });
    }
    setConfirmDelete({ open: false, target: null, type: '' });
  };

  // ── Procesar pago ──
  const EMPTY_PAY = { open: false, emp: null, details: null, loading: false, notes: '', payMethod: '', montoReal: '', comprobantes: [] };
  const [payDialog, setPayDialog] = useState(EMPTY_PAY);
  const payFileRef = useRef(null);

  const openPayDialog = async (emp) => {
    setPayDialog({ ...EMPTY_PAY, open: true, emp, loading: true, payMethod: emp.paymentMethod || 'efectivo' });
    try {
      const r = await authFetch(`${API_URL}/staff/payments/employee/${emp._id || emp.employeeId}/details`);
      const d = await r.json();
      if (d.ok) {
        const attendanceIds = (d.summary?.details || []).map(x => String(x._id));
        const netAmount = d.summary?.netToPay || d.summary?.totalEarnings || 0;
        setPayDialog(p => ({ ...p, details: { ...d, attendanceIds }, loading: false, montoReal: String(netAmount) }));
      } else setPayDialog(p => ({ ...p, loading: false }));
    } catch (_) { setPayDialog(p => ({ ...p, loading: false })); }
  };

  const handleProcessPayment = async () => {
    const det = payDialog.details;
    if (!det?.attendanceIds?.length) {
      setSnack({ open: true, message: 'No hay asistencias pendientes de pago para este empleado', severity: 'warning' });
      return;
    }
    setPayDialog(p => ({ ...p, loading: true }));
    try {
      // 1. Registrar liquidación en el sistema de personal
      const r = await authFetch(`${API_URL}/staff/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeId: det.employee._id || payDialog.emp._id,
          attendanceIds: det.attendanceIds,
          deductAdvances: true,
          notes: payDialog.notes,
          paymentMethod: payDialog.payMethod
        })
      });
      const d = await r.json();
      if (!d.ok) {
        setSnack({ open: true, message: d.error || 'Error al procesar pago', severity: 'error' });
        setPayDialog(p => ({ ...p, loading: false }));
        return;
      }

      // 2. Registrar automáticamente en Egresos (contabilidad)
      const monto = parseFloat(String(payDialog.montoReal).replace(/\./g, '').replace(',', '.')) || det.summary?.netToPay || det.summary?.totalEarnings || 0;
      const empName = `${payDialog.emp.firstName} ${payDialog.emp.lastName}`;
      try {
        await authFetch(`${API_URL}/accounting/transactions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            transactionNumber: d.payment?.paymentNumber || ('NOM-' + Date.now()),
            type: 'expense',
            category: 'nomina',
            amount: monto,
            netAmount: monto,
            description: `Pago nómina — ${empName} — ${d.payment?.paymentNumber || ''}`,
            source: 'manual',
            status: 'paid',
            notes: payDialog.notes || `Método: ${payDialog.payMethod}`
          })
        });
      } catch (_) {}

      // 3. Subir comprobantes si se adjuntaron
      if (payDialog.comprobantes.length > 0 && d.payment?._id) {
        for (const archivo of payDialog.comprobantes) {
          try {
            const fd = new FormData();
            fd.append('receipt', archivo);
            await authFetch(`${API_URL}/staff/payments/${d.payment._id}/receipt`, { method: 'POST', body: fd });
          } catch (_) {}
        }
      }

      setSnack({ open: true, message: `✅ Pago ${d.payment?.paymentNumber || ''} procesado y registrado en contabilidad`, severity: 'success' });
      setPayDialog(EMPTY_PAY);
      loadPending();
    } catch (e) {
      setSnack({ open: true, message: 'Error de conexión', severity: 'error' });
      setPayDialog(p => ({ ...p, loading: false }));
    }
  };

  const handleSendAccess = async (emp) => {
    setCredDialog({ open: true, emp, username: '', password: '', email: '', waSent: false, created: false, loading: true });
    try {
      const r = await authFetch(`${API_URL}/staff/employees/${emp._id}/create-account`, { method: 'POST' });
      const d = await r.json();
      if (d.ok) {
        setCredDialog(prev => ({ ...prev, loading: false, username: d.username, password: d.password, email: d.email, waSent: d.waSent, created: d.created }));
      } else {
        setSnack({ open: true, message: d.error || 'Error creando acceso', severity: 'error' });
        setCredDialog(prev => ({ ...prev, open: false, loading: false }));
      }
    } catch (e) {
      setSnack({ open: true, message: 'Error de conexión', severity: 'error' });
      setCredDialog(prev => ({ ...prev, open: false, loading: false }));
    }
  };

  const handleUploadReceipt = async (file, paymentId) => {
    const pid = paymentId || receiptDialog.paymentId;
    if (!file || !pid) return;
    setReceiptDialog(r => ({ ...r, uploading: true }));
    try {
      const fd = new FormData();
      fd.append('receipt', file);
      const res = await authFetch(`${API_URL}/staff/payments/${pid}/receipt`, { method: 'POST', body: fd });
      const d = await res.json();
      if (d.ok) {
        setSnack({ open: true, message: 'Comprobante subido correctamente', severity: 'success' });
        setReceiptDialog({ open: false, paymentId: null, paymentNumber: '', uploading: false });
        // Recargar historial para mostrar el comprobante
        if (histDialog.emp) {
          const r2 = await authFetch(`${API_URL}/staff/payments/employee/${histDialog.emp._id}/history`);
          const d2 = await r2.json();
          setHistDialog(prev => ({ ...prev, payments: d2.payments || [] }));
        }
      } else {
        setSnack({ open: true, message: d.error || 'Error subiendo comprobante', severity: 'error' });
        setReceiptDialog(r => ({ ...r, uploading: false }));
      }
    } catch (e) {
      setSnack({ open: true, message: 'Error de conexión', severity: 'error' });
      setReceiptDialog(r => ({ ...r, uploading: false }));
    }
  };

  const openHistoryDialog = async (emp) => {
    setHistDialog({ open: true, emp, payments: [], loading: true });
    try {
      const r = await authFetch(`${API_URL}/staff/payments/employee/${emp._id}/history`);
      const d = await r.json();
      setHistDialog(prev => ({ ...prev, payments: d.payments || [], loading: false }));
    } catch (e) {
      setHistDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // ── Asignación de personal a evento ─────────────────────────────────────────────────
  const handleConfirmAssignment = async () => {
    const { order, selected, sendWa, note, coordType, coordEmpId, coordVirtualName, coordVirtualPhone, manualArrivalTime, eventHours, minPayment } = schedDialog;
    if (!order || !selected.length) return;
    setSchedDialog(p => ({ ...p, saving: true }));
    try {
      const selectedEmps = employees.filter(e => selected.includes(String(e._id)));

      // Determinar coordinador
      let coordinador = null;
      if (coordType === 'presencial' && coordEmpId) {
        const coordEmp = employees.find(e => String(e._id) === coordEmpId);
        if (coordEmp) coordinador = { type: 'presencial', id: coordEmpId, name: `${coordEmp.firstName} ${coordEmp.lastName}`, phone: coordEmp.whatsapp || coordEmp.phone };
      } else if (coordType === 'virtual' && coordVirtualName) {
        coordinador = { type: 'virtual', name: coordVirtualName, phone: coordVirtualPhone };
      } else if (selectedEmps.length === 1) {
        const e = selectedEmps[0];
        coordinador = { type: 'presencial', id: String(e._id), name: `${e.firstName} ${e.lastName}`, phone: e.whatsapp || e.phone };
      }

      const assignedStaff = selectedEmps.map(e => ({
        id: e._id, name: `${e.firstName} ${e.lastName}`,
        category: e.category, phone: e.whatsapp || e.phone,
        isCoordinator: coordType === 'presencial' && String(e._id) === coordEmpId
      }));

      // Guardar en la orden — verificar respuesta
      const saveRes = await authFetch(`${API_URL}/orders/service/${order.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...order, assignedStaff, staffNote: note, coordinador })
      });
      const saveData = await saveRes.json().catch(() => ({}));
      if (!saveRes.ok || saveData.ok === false) {
        throw new Error(saveData.error || `Error ${saveRes.status} al guardar la orden`);
      }

      // Enviar WhatsApp de citación a cada empleado asignado
      const waResults = [];
      if (sendWa) {
        const eventDate = order.date || order.eventDetails?.eventDate || '';
        const eventLocation = [order.event?.address, order.event?.city].filter(Boolean).join(', ');
        const clientName = order.client?.name || 'Cliente';
        const eventStart = order.event?.start || '';
        // Usar hora de llegada manual si se proporcionó; si no, dejar vacío
        const arrivalTime = manualArrivalTime || '';

        const coordInfo = coordinador
          ? (coordinador.type === 'virtual'
              ? `👔 *Coordinador virtual:* ${coordinador.name}${coordinador.phone ? `\n📞 *Tel. coordinador:* ${coordinador.phone}` : ''}`
              : `👔 *Coordinador:* ${coordinador.name}${coordinador.phone ? `\n📞 *Tel. coordinador:* ${coordinador.phone}` : ''}`)
          : '';

        const equipos = (order.items || []).map(i => `• ${i.name || i.productName}${(i.qty || i.quantity) > 1 ? ` ×${i.qty || i.quantity}` : ''}`).join('\n');

        // Lista del equipo para enviar al coordinador
        const listaEquipo = selectedEmps
          .filter(e => coordinador?.type !== 'presencial' || String(e._id) !== coordEmpId)
          .map(e => `• *${e.firstName} ${e.lastName}* (${e.category || 'Operario'})${(e.whatsapp || e.phone) ? ` — 📱 ${e.whatsapp || e.phone}` : ''}`)
          .join('\n');

        const motivacion = `
🌟 *SISTEMA DE ESTRELLAS — Inflables Da Silva*
En cada evento serás valorado/a en 5 aspectos clave:

1️⃣ *Cumplimiento* — llegada a tiempo y completar el evento
2️⃣ *Presentación personal* — uniforme y aspecto correcto
3️⃣ *Desempeño en el montaje* — rapidez y cuidado al armar
4️⃣ *Desempeño durante el evento* — atención, actitud y trato
5️⃣ *Desempeño en el desmontaje* — orden, cuidado y velocidad

¿Por qué importan tus estrellas?
⭐ *Los mejores valorados son los primeros en ser programados* para nuevos eventos. Quienes más se destacan, más trabajan.

🏆 *Programa de premios:*
Basado en tu desempeño acumulado en los eventos, la empresa premia periódicamente a quienes más se han destacado con:
• 💵 Pagos extra en efectivo
• 🎬 Entradas dobles al cine
• 🍽️ Bonos para restaurantes
• 🛒 Bonos para supermercados y almacenes de cadena
• 🎁 Premios sorpresa

*¡El siguiente premiado puedes ser tú! Solo debes lograr una buena valoración en cada evento.* 💪`;

        // Instrucciones de escáner QR para el coordinador
        const instruccionesQR = `
📲 *REGISTRO DE ASISTENCIA — Tu responsabilidad como coordinador:*
Al llegar al evento, debes registrar la entrada de cada empleado así:

1️⃣ Abre el sistema: https://app.inflablesdasilva.com
2️⃣ Ve a *Personal → Asistencia*
3️⃣ Selecciona el evento del día
4️⃣ Pide a cada empleado que muestre su *código QR personal* (lo tienen en su perfil de la app)
5️⃣ Escanéalo con la cámara o pega el enlace en el campo de escaneo
6️⃣ Presiona *ENTRADA* al llegar y *SALIDA* al terminar

⚠️ Si un empleado olvidó su QR, puede usar su *código de empleado + PIN* en el mismo panel.
Si tienes algún problema, comunícate con la administración.`;

        for (const emp of selectedEmps) {
          const isCoord = coordinador?.type === 'presencial' && String(emp._id) === coordEmpId;

          // Calcular pago mínimo para este empleado
          const empCat = categories.find(c => c.code === emp.category || c.name === emp.category);
          const hrs = parseFloat(eventHours) || 0;
          const calculado = empCat && hrs > 0
            ? (empCat.hourlyRate ? Math.round(empCat.hourlyRate * hrs) : Math.round((empCat.dailyRate / (empCat.standardHours || 8)) * hrs))
            : 0;
          const pagoMinimo = minPayment ? parseInt(String(minPayment).replace(/\D/g,'')) : calculado;
          const fmtPago = (v) => v > 0 ? '$' + v.toLocaleString('es-CO') : null;
          const pagoLine = pagoMinimo > 0
            ? `💰 *Pago mínimo garantizado:* ${fmtPago(pagoMinimo)}${hrs > 0 ? ` (${hrs} horas)` : ''}\n   _(Puede ser mayor si hay recargos dominicales o nocturnos)_`
            : null;
          const pagoSolicitud = `\n💳 *Para solicitar tu pago* al finalizar el evento, envía *PAGO* por WhatsApp y lo procesamos en máximo 5 horas.`;

          let msgLines;
          if (isCoord) {
            msgLines = [
              `📋 *CITACIÓN EVENTO - Inflables Da Silva*`,
              ``,
              `Hola *${emp.firstName}*, eres el *COORDINADOR* del siguiente evento:`,
              ``,
              `📅 *Fecha:* ${eventDate}`,
              `🎪 *Evento:* ${clientName}`,
              `📍 *Lugar:* ${eventLocation || '—'}`,
              `⏰ *Inicio del evento:* ${eventStart || '—'}`,
              arrivalTime ? `🟢 *Debes llegar antes de las:* *${arrivalTime}*` : null,
              hrs > 0 ? `⏱ *Duración total:* ${hrs} horas (incluye montaje y desmontaje)` : null,
              pagoLine,
              pagoSolicitud,
              equipos ? `\n🎯 *Equipos a montar:*\n${equipos}` : null,
              listaEquipo ? `\n👥 *Tu equipo para este evento:*\n${listaEquipo}` : null,
              note ? `\n📝 *Nota:* ${note}` : null,
              ``,
              `⚠️ *Descuentos por tardanza:* 1-15 min: $5.000 | 16-30 min: $15.000 | +30 min: $25.000`,
              instruccionesQR,
              motivacion,
              ``,
              `❓ ¿Puedes asistir? Responde *SI* o *NO*`,
            ].filter(l => l !== null).join('\n');
          } else {
            msgLines = [
              `📋 *CITACIÓN EVENTO - Inflables Da Silva*`,
              ``,
              `Hola *${emp.firstName}*, has sido asignado/a al siguiente evento:`,
              ``,
              `📅 *Fecha:* ${eventDate}`,
              `🎪 *Evento:* ${clientName}`,
              `📍 *Lugar:* ${eventLocation || '—'}`,
              `⏰ *Inicio del evento:* ${eventStart || '—'}`,
              arrivalTime ? `🟢 *Debes llegar antes de las:* *${arrivalTime}*` : null,
              hrs > 0 ? `⏱ *Duración total:* ${hrs} horas (incluye montaje y desmontaje)` : null,
              pagoLine,
              pagoSolicitud,
              ``,
              `👷 *Tu rol:* ${emp.category || 'Operario'}`,
              coordInfo || null,
              equipos ? `\n🎯 *Equipos a montar:*\n${equipos}` : null,
              note ? `\n📝 *Nota:* ${note}` : null,
              ``,
              `⚠️ *Puntualidad:* Llegar tarde genera descuento:\n• 1-15 min: $5.000 | 16-30 min: $15.000 | +30 min: $25.000`,
              motivacion,
              ``,
              `❓ ¿Puedes asistir? Responde *SI* o *NO*`,
            ].filter(l => l !== null).join('\n');
          }

          try {
            const waRes = await authFetch(`${API_URL}/staff/employees/${emp._id}/notify-event`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ customMessage: msgLines, role: emp.category || 'Operario' })
            });
            const waData = await waRes.json().catch(() => ({}));
            if (waRes.ok && waData.ok !== false) {
              waResults.push({ name: emp.firstName, ok: true });
            } else {
              waResults.push({ name: emp.firstName, ok: false, error: waData.error || `Error ${waRes.status}` });
            }
          } catch (waErr) {
            waResults.push({ name: emp.firstName, ok: false, error: waErr.message });
          }
        }
      }

      // Mensaje final con resultado del WhatsApp
      const waSent = waResults.filter(r => r.ok).length;
      const waFailed = waResults.filter(r => !r.ok);
      let finalMsg = `✅ Personal asignado correctamente`;
      if (sendWa && waResults.length > 0) {
        if (waSent > 0) finalMsg += ` · WhatsApp enviado a ${waSent} persona${waSent > 1 ? 's' : ''}`;
        if (waFailed.length > 0) finalMsg += ` · ⚠️ Falló WhatsApp: ${waFailed.map(f => `${f.name} (${f.error})`).join(', ')}`;
      } else if (sendWa && waResults.length === 0) {
        finalMsg += ` (sin teléfonos registrados)`;
      }
      setSnack({ open: true, message: finalMsg, severity: waFailed.length > 0 ? 'warning' : 'success' });
      setSchedDialog({ open: false, order: null, selected: [], sendWa: true, note: '', saving: false, coordType: 'presencial', coordEmpId: '', coordVirtualName: '', coordVirtualPhone: '' });
      loadServiceOrders();
    } catch (e) {
      setSnack({ open: true, message: 'Error al asignar: ' + e.message, severity: 'error' });
      setSchedDialog(p => ({ ...p, saving: false }));
    }
  };

  // ── Vista simplificada para empleados (todos los roles no-admin) ────────────────────
  if (isEmployeeView) {
    const me = myProfile;
    const myAtt = todayAtt.filter(a => a.employeeCode === authUser.username);
    const st = me?.stats || {};

    // ── Auto-edición de perfil ──
    const openSelfEdit = () => setSelfDialog({
      open: true, saving: false,
      form: { phone: me?.phone || '', whatsapp: me?.whatsapp || '', email: me?.email || '',
              city: me?.city || '', address: me?.address || '',
              emergencyContact: me?.emergencyContact || '' }
    });

    const handleSelfSave = async () => {
      if (!me?._id) return;
      setSelfDialog(p => ({ ...p, saving: true }));
      try {
        // Guardar campos de texto
        await authFetch(`${API_URL}/staff/employees/${me._id}/self-update`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(selfDialog.form)
        });
        // Subir foto si seleccionó una
        if (selfPhotoFile) {
          const fd = new FormData();
          fd.append('photo', selfPhotoFile);
          await authFetch(`${API_URL}/staff/employees/${me._id}/photo`, { method: 'POST', body: fd });
          setSelfPhotoFile(null);
        }
        setSnack({ open: true, message: '✅ Tu perfil fue actualizado', severity: 'success' });
        setSelfDialog({ open: false, saving: false, form: {} });
        loadMyProfile();
      } catch (e) {
        setSnack({ open: true, message: 'Error al guardar: ' + e.message, severity: 'error' });
        setSelfDialog(p => ({ ...p, saving: false }));
      }
    };

    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
          👋 Mi Perfil
        </Typography>

        <Grid container spacing={2}>
          {/* ── Tarjeta de perfil ── */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              {/* Foto clickeable */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <input type="file" accept="image/*" id="self-photo-quick" style={{ display: 'none' }}
                  onChange={e => { setSelfPhotoFile(e.target.files[0] || null); openSelfEdit(); }} />
                <label htmlFor="self-photo-quick" style={{ cursor: 'pointer' }}>
                  <Avatar src={me?.photo?.url || ''} sx={{ width: 90, height: 90, bgcolor: '#6366f1', fontSize: '2rem', mx: 'auto', mb: 1, border: '3px solid #e0e7ff' }}>
                    {(!me?.photo?.url) && (me ? me.firstName.charAt(0).toUpperCase() : '?')}
                  </Avatar>
                  <Typography variant="caption" color="primary" sx={{ display: 'block' }}>📷 Toca para cambiar foto</Typography>
                </label>
                <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
                  {me ? `${me.firstName} ${me.lastName}` : authUser.username}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center', mt: 0.5 }}>
                  {me?.category && <Chip size="small" label={me.category} sx={{ bgcolor: '#ede9fe', color: '#6366f1' }} />}
                  {me?.status && <Chip size="small" label={STATUS_CONFIG[me.status]?.label || me.status} sx={{ bgcolor: '#dcfce7', color: '#16a34a' }} />}
                </Box>
              </Box>
              {me && (
                <Stack spacing={1} divider={<Divider />} sx={{ mb: 2 }}>
                  {me.documentNumber && <Box><Typography variant="caption" color="text.secondary">Documento</Typography><Typography variant="body2">{me.documentType} {me.documentNumber}</Typography></Box>}
                  <Box><Typography variant="caption" color="text.secondary">Teléfono</Typography><Typography variant="body2">{me.phone || '—'}</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary">Correo</Typography><Typography variant="body2">{me.email || '—'}</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary">Ciudad</Typography><Typography variant="body2">{me.city || '—'}</Typography></Box>
                </Stack>
              )}
              <Button fullWidth variant="contained" startIcon={<EditIcon />} sx={{ mb: 1 }} onClick={openSelfEdit}>
                ✏️ Editar mi información
              </Button>
              <Button fullWidth variant="outlined" color="warning" size="small"
                onClick={() => setPwdDialog({ open: true, current: '', next: '', confirm: '', saving: false, error: '' })}>
                🔑 Cambiar contraseña
              </Button>
            </Paper>

            {/* ── Solicitar pago ── */}
            {(st.pendingPayment || 0) > 0 && (
              <Paper sx={{ p: 2.5, borderRadius: 3, mt: 2, bgcolor: '#fef9c3', border: '1px solid #fde68a' }}>
                <Typography variant="subtitle2" fontWeight={700} color="#d97706" sx={{ mb: 0.5 }}>💰 Tienes saldo por cobrar</Typography>
                <Typography variant="h5" fontWeight={800} color="#d97706">{formatCurrency(st.pendingPayment)}</Typography>
                <Button fullWidth variant="contained" sx={{ mt: 1.5, bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' } }}
                  onClick={async () => {
                    try {
                      const phone = '573334008723';
                      const msg = encodeURIComponent(`Hola, soy ${me?.firstName || authUser.username} (${me?.employeeCode || ''}). Solicito el pago de mi saldo pendiente de ${formatCurrency(st.pendingPayment)}.`);
                      window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
                    } catch (_) {}
                  }}>
                  Solicitar mi pago por WhatsApp
                </Button>
              </Paper>
            )}
          </Grid>

          {/* ── Balance / Ganancias ── */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center', bgcolor: '#f0fdf4' }}>
                  <Typography variant="caption" color="text.secondary">Días trabajados</Typography>
                  <Typography variant="h5" fontWeight={700} color="#16a34a">{st.totalDaysWorked || 0}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center', bgcolor: '#eff6ff' }}>
                  <Typography variant="caption" color="text.secondary">Total ganado</Typography>
                  <Typography variant="h6" fontWeight={700} color="#1d4ed8" sx={{ fontSize: '1rem' }}>{formatCurrency(st.totalEarned || 0)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center', bgcolor: '#fef9c3' }}>
                  <Typography variant="caption" color="text.secondary">Por cobrar</Typography>
                  <Typography variant="h6" fontWeight={700} color="#d97706" sx={{ fontSize: '1rem' }}>{formatCurrency(st.pendingPayment || 0)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center', bgcolor: '#fdf2f8' }}>
                  <Typography variant="caption" color="text.secondary">Ya cobrado</Typography>
                  <Typography variant="h6" fontWeight={700} color="#9d174d" sx={{ fontSize: '1rem' }}>{formatCurrency(st.totalPaid || 0)}</Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* ── Asistencia: solo muestra estado + botón QR ── */}
            <Paper sx={{ p: 2.5, borderRadius: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  <AccessTimeIcon sx={{ verticalAlign: 'middle', mr: 0.5, color: '#3b82f6', fontSize: '1.1rem' }} />
                  Asistencia
                </Typography>
                <IconButton size="small" onClick={loadToday}><RefreshIcon /></IconButton>
              </Box>

              {/* Instrucción */}
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <QrCode2Icon sx={{ color: '#3b82f6', fontSize: '2rem', flexShrink: 0 }} />
                <Box>
                  <Typography variant="body2" fontWeight={600} color="#1d4ed8">Muéstrale tu QR al coordinador</Typography>
                  <Typography variant="caption" color="text.secondary">El coordinador escanea tu código para registrar entrada y salida</Typography>
                </Box>
              </Box>

              {/* Botón mostrar QR */}
              {me?.qrToken && (
                <Button
                  variant="contained"
                  startIcon={<QrCode2Icon />}
                  fullWidth
                  sx={{ mb: 2, bgcolor: '#1e293b', '&:hover': { bgcolor: '#334155' } }}
                  onClick={() => setQrDialog({ open: true, data: me })}
                >
                  Mostrar mi Código QR
                </Button>
              )}

              {/* Asistencia hoy */}
              {myAtt.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No hay registros de asistencia hoy.</Typography>
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>HOY</Typography>
                  {myAtt.map((a, i) => (
                    <Box key={a._id || i} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e5e7eb', mt: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{a.eventName || 'Sin evento'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Entrada: {a.checkIn ? formatTime(a.checkIn) : '—'} · Salida: {a.checkOut ? formatTime(a.checkOut) : 'en curso'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {!a.checkOut && <Chip size="small" label="En curso" sx={{ bgcolor: '#dbeafe', color: '#1d4ed8' }} />}
                        {a.earnings?.total > 0 && <Chip size="small" label={`+${formatCurrency(a.earnings.total)}`} sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 700 }} />}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>

            {/* ── Botones historial ── */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {me && (
                <Button variant="outlined" startIcon={<HistoryIcon />} onClick={() => openHistoryDialog(me)}>
                  Historial de Pagos
                </Button>
              )}
              {me?._id && (
                <Button
                  variant="outlined" startIcon={<WorkIcon />} color="secondary"
                  onClick={() => loadMyHistory(me._id)}
                >
                  Historial de Eventos
                </Button>
              )}
            </Box>

            {/* ── Historial de eventos ── */}
            {myHistory.length > 0 && (
              <Paper sx={{ p: 2, borderRadius: 3, mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Mis Eventos</Typography>
                  {myHistLoading && <CircularProgress size={18} />}
                </Box>
                <TableContainer sx={{ maxHeight: 280 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Evento</TableCell>
                        <TableCell>Horas</TableCell>
                        <TableCell align="right">Ganado</TableCell>
                        <TableCell>Estado pago</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {myHistory.map((h, i) => (
                        <TableRow key={h._id || i} hover>
                          <TableCell>{h.date ? new Date(h.date).toLocaleDateString('es-CO') : '—'}</TableCell>
                          <TableCell>{h.eventName || '—'}</TableCell>
                          <TableCell>{h.hoursWorked ? `${h.hoursWorked}h` : '—'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#16a34a' }}>{formatCurrency(h.earnings?.total || 0)}</TableCell>
                          <TableCell>
                            <Chip size="small" label={h.paymentStatus === 'pagado' ? 'Pagado' : 'Pendiente'} sx={{ bgcolor: h.paymentStatus === 'pagado' ? '#dcfce7' : '#fef9c3', color: h.paymentStatus === 'pagado' ? '#16a34a' : '#d97706' }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
            {/* ALCANCÍA */}
            <Box sx={{ mt: 3 }}>
              <AlcanciaView
                employeeId={me?._id}
                employeeName={me ? `${me.firstName} ${me.lastName}` : ''}
                isAdmin={false}
                authUser={authUser}
                pendingPayment={st.pendingPayment || 0}
                onUpdate={loadMyProfile}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Dialog historial de pagos */}
        <Dialog open={histDialog.open} onClose={() => setHistDialog(h => ({ ...h, open: false }))} maxWidth="md" fullWidth>
          <DialogTitle>Historial de Pagos — {histDialog.emp ? `${histDialog.emp.firstName} ${histDialog.emp.lastName}` : ''}</DialogTitle>
          <DialogContent>
            {histDialog.loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', my: 3 }} /> : histDialog.payments.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No hay pagos registrados.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>N° Pago</TableCell><TableCell>Fecha</TableCell>
                    <TableCell align="right">Total</TableCell><TableCell>Método</TableCell><TableCell>Estado</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {histDialog.payments.map(p => (
                      <TableRow key={p._id}>
                        <TableCell>{p.paymentNumber || '—'}</TableCell>
                        <TableCell>{(p.completedAt || p.createdAt) ? new Date(p.completedAt || p.createdAt).toLocaleDateString('es-CO') : '—'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#16a34a' }}>{formatCurrency(p.netAmount || p.grossAmount || 0)}</TableCell>
                        <TableCell>{p.paymentMethod || '—'}</TableCell>
                        <TableCell><Chip size="small" label={p.status || 'pagado'} color={p.status === 'completed' ? 'success' : 'default'} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions><Button onClick={() => setHistDialog(h => ({ ...h, open: false }))}>Cerrar</Button></DialogActions>
        </Dialog>

        {/* ── Diálogo auto-edición de perfil ── */}
        <Dialog open={selfDialog.open} onClose={() => !selfDialog.saving && setSelfDialog(p => ({ ...p, open: false }))} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: '#f8fafc' }}>
            <Typography variant="h6" fontWeight={700}>Actualizar mis datos</Typography>
            <Typography variant="caption" color="text.secondary">Solo puedes editar tus datos de contacto y foto</Typography>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Teléfono" value={selfDialog.form.phone || ''}
                  onChange={e => setSelfDialog(p => ({ ...p, form: { ...p.form, phone: e.target.value } }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="WhatsApp" value={selfDialog.form.whatsapp || ''}
                  onChange={e => setSelfDialog(p => ({ ...p, form: { ...p.form, whatsapp: e.target.value } }))}
                  helperText="Si es el mismo número, puede dejarlo igual" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Correo electrónico" value={selfDialog.form.email || ''}
                  onChange={e => setSelfDialog(p => ({ ...p, form: { ...p.form, email: e.target.value } }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Ciudad" value={selfDialog.form.city || ''}
                  onChange={e => setSelfDialog(p => ({ ...p, form: { ...p.form, city: e.target.value } }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Dirección" value={selfDialog.form.address || ''}
                  onChange={e => setSelfDialog(p => ({ ...p, form: { ...p.form, address: e.target.value } }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Contacto de emergencia (nombre y teléfono)" value={selfDialog.form.emergencyContact || ''}
                  onChange={e => setSelfDialog(p => ({ ...p, form: { ...p.form, emergencyContact: e.target.value } }))} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  <CameraAltIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />Foto de perfil (opcional)
                </Typography>
                <input type="file" accept="image/*" onChange={e => setSelfPhotoFile(e.target.files[0] || null)}
                  style={{ fontSize: 13 }} />
                {selfPhotoFile && <Chip size="small" label={selfPhotoFile.name} sx={{ mt: 1 }} onDelete={() => setSelfPhotoFile(null)} />}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelfDialog(p => ({ ...p, open: false }))} disabled={selfDialog.saving}>Cancelar</Button>
            <Button variant="contained" onClick={handleSelfSave} disabled={selfDialog.saving}
              startIcon={selfDialog.saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
              {selfDialog.saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Diálogo QR */}
        <QRDialog open={qrDialog.open} onClose={() => setQrDialog({ open: false, data: null })} employee={qrDialog.data || {}} categories={categories} />

        {/* Diálogo cambiar contraseña */}
        <Dialog open={pwdDialog.open} onClose={() => setPwdDialog(p => ({ ...p, open: false }))} maxWidth="xs" fullWidth>
          <DialogTitle>🔑 Cambiar Contraseña</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {pwdDialog.error && <Alert severity="error">{pwdDialog.error}</Alert>}
              <TextField fullWidth type="password" label="Contraseña actual" value={pwdDialog.current}
                onChange={e => setPwdDialog(p => ({ ...p, current: e.target.value }))} />
              <TextField fullWidth type="password" label="Nueva contraseña" value={pwdDialog.next}
                onChange={e => setPwdDialog(p => ({ ...p, next: e.target.value }))} />
              <TextField fullWidth type="password" label="Confirmar nueva contraseña" value={pwdDialog.confirm}
                onChange={e => setPwdDialog(p => ({ ...p, confirm: e.target.value }))} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPwdDialog(p => ({ ...p, open: false }))} disabled={pwdDialog.saving}>Cancelar</Button>
            <Button variant="contained" disabled={pwdDialog.saving} onClick={async () => {
              if (pwdDialog.next !== pwdDialog.confirm) return setPwdDialog(p => ({ ...p, error: 'Las contraseñas no coinciden' }));
              if (pwdDialog.next.length < 6) return setPwdDialog(p => ({ ...p, error: 'Mínimo 6 caracteres' }));
              setPwdDialog(p => ({ ...p, saving: true, error: '' }));
              try {
                const r = await authFetch(`${API_URL}/auth/change-password`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ currentPassword: pwdDialog.current, newPassword: pwdDialog.next, username: authUser?.username })
                });
                const d = await r.json();
                if (d.success) {
                  setPwdDialog({ open: false, current: '', next: '', confirm: '', saving: false, error: '' });
                  setSnack({ open: true, message: '✅ Contraseña actualizada', severity: 'success' });
                } else {
                  setPwdDialog(p => ({ ...p, saving: false, error: d.message || 'Error' }));
                }
              } catch { setPwdDialog(p => ({ ...p, saving: false, error: 'Error de conexión' })); }
            }}>Guardar</Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.message}</Alert>
        </Snackbar>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>👷 Gestión de Personal</Typography>
        <Typography color="text.secondary">Control de asistencia, empleados y pagos</Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {visibleTabs.map(t => <Tab key={t.id} value={t.id} label={t.label} icon={t.icon} iconPosition="start" />)}
        </Tabs>
      </Paper>

      {tab === 'dashboard' && stats && (
        <Grid container spacing={3}>
          {/* ── Panel de reglas de permisos ── */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#92400e', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                🔐 Reglas de acceso del sistema
              </Typography>
              <Grid container spacing={1}>
                {[
                  { role: 'Administrador', color: '#ef4444', bg: '#fef2f2', access: 'Acceso total: crear, editar, eliminar órdenes, documentos, empleados y configuración.' },
                  { role: 'Coordinador (manager)', color: '#6366f1', bg: '#ede9fe', access: 'Ve todo el sistema y usa el escáner de asistencia. NO puede eliminar registros.' },
                  { role: 'Empleado (employee)', color: '#3b82f6', bg: '#eff6ff', access: 'Solo puede ver. Puede actualizar su propio perfil (foto, teléfono, correo, ciudad).' },
                  { role: 'Bodega (staff)', color: '#10b981', bg: '#f0fdf4', access: 'Puede diligenciar órdenes de bodega: salida, llegada y observaciones. No edita otro.' },
                ].map(r => (
                  <Grid item xs={12} sm={6} md={3} key={r.role}>
                    <Box sx={{ bgcolor: r.bg, border: `1px solid ${r.color}30`, borderRadius: 1.5, p: 1.5 }}>
                      <Chip size="small" label={r.role} sx={{ bgcolor: r.color, color: 'white', fontWeight: 700, fontSize: 11, mb: 0.5 }} />
                      <Typography variant="caption" display="block" color="text.secondary">{r.access}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}><StatCard title="Total Empleados" value={stats.totalEmployees} icon={<PeopleIcon />} color="#6366f1" /></Grid>
          <Grid item xs={12} sm={6} md={3}><StatCard title="Activos" value={stats.activeEmployees} icon={<CheckCircleIcon />} color="#10b981" /></Grid>
          <Grid item xs={12} sm={6} md={3}><StatCard title="Trabajando Hoy" value={stats.workingToday} icon={<WorkIcon />} color="#3b82f6" /></Grid>
          <Grid item xs={12} sm={6} md={3}><StatCard title="Por Pagar" value={formatCurrency(stats.totalPendingPayment)} subtitle={`${stats.pendingPaymentsCount} empleados`} icon={<AttachMoneyIcon />} color="#f59e0b" /></Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f0fdf4' }}>
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WhatsAppIcon sx={{ color: '#25d366' }} /> WhatsApp Business (Meta)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Conectado vía Meta Cloud API. La gestión de conversaciones está en el módulo "Conversaciones".
                </Typography>
              </Box>
              <Chip label="🟢 Activo" size="small" sx={{ bgcolor: '#10b981', color: 'white', fontWeight: 600 }} />
            </Paper>
          </Grid>

        </Grid>
      )}

      {tab === 'schedule' && (() => {
        const _now = new Date();
        const hoyStr = `${_now.getFullYear()}-${String(_now.getMonth()+1).padStart(2,'0')}-${String(_now.getDate()).padStart(2,'0')}`;
        const _prox7 = new Date(_now); _prox7.setDate(_prox7.getDate() + 7);
        const proxStr = `${_prox7.getFullYear()}-${String(_prox7.getMonth()+1).padStart(2,'0')}-${String(_prox7.getDate()).padStart(2,'0')}`;
        const _prox14 = new Date(_now); _prox14.setDate(_prox14.getDate() + 14);
        const prox14Str = `${_prox14.getFullYear()}-${String(_prox14.getMonth()+1).padStart(2,'0')}-${String(_prox14.getDate()).padStart(2,'0')}`;

        const getOrderDate = o => (o.date || o.eventDetails?.eventDate || '').slice(0, 10);
        const filteredOrders = serviceOrders
          .filter(o => {
            const d = getOrderDate(o);
            if (!d) return false;
            if (schedFilter === 'today')    return d === hoyStr;
            if (schedFilter === 'week')     return d >= hoyStr && d <= proxStr;
            return d >= hoyStr && d <= prox14Str;
          })
          .sort((a, b) => getOrderDate(a).localeCompare(getOrderDate(b)));

        return (
          <Box>
            {/* Filtros */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mr: 1 }}>Órdenes de Servicio:</Typography>
              {[{ k: 'today', l: 'Hoy' }, { k: 'week', l: 'Esta semana' }, { k: 'upcoming', l: 'Próximos 14 días' }].map(f => (
                <Button key={f.k} size="small" variant={schedFilter === f.k ? 'contained' : 'outlined'}
                  onClick={() => setSchedFilter(f.k)}>{f.l}</Button>
              ))}
              <Button size="small" startIcon={<RefreshIcon />} onClick={loadServiceOrders} disabled={ordersLoading}>Actualizar</Button>
            </Box>

            {ordersLoading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

            {!ordersLoading && filteredOrders.length === 0 && (
              <Alert severity="info">No hay órdenes de servicio para este período.</Alert>
            )}

            <Grid container spacing={2}>
              {filteredOrders.map(order => {
                const oDate  = getOrderDate(order);
                const city   = order.event?.city || order.eventDetails?.city || '—';
                const addr   = order.event?.address || order.eventDetails?.address || '';
                const start  = order.event?.start || order.eventDetails?.startTime || '';
                const isToday = oDate === hoyStr;
                const assigned = order.assignedStaff || [];

                return (
                  <Grid item xs={12} md={6} key={order.id}>
                    <Card sx={{ border: isToday ? '2px solid #6366f1' : '1px solid #e2e8f0', borderRadius: 2 }}>
                      <CardContent sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: isToday ? '#6366f1' : '#64748b', textTransform: 'uppercase' }}>
                              {isToday ? '⚡ HOY' : new Date(oDate + 'T12:00:00').toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric', month: 'short' })}
                              {start && ` · ${start}`}
                            </Typography>
                            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                              {order.client?.name || 'Sin nombre'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              📍 {city}{addr ? ` — ${addr}` : ''}
                            </Typography>
                          </Box>
                          <Chip size="small" label={order.id} sx={{ bgcolor: '#f1f5f9', fontFamily: 'monospace', fontSize: 11 }} />
                        </Box>

                        {/* Productos */}
                        <Box sx={{ mb: 1 }}>
                          {(order.items || []).slice(0, 3).map((item, i) => (
                            <Chip key={i} size="small" label={item.name || item.productName || item.code} sx={{ mr: 0.5, mb: 0.5, bgcolor: '#f8fafc', fontSize: 11 }} />
                          ))}
                          {(order.items || []).length > 3 && (
                            <Chip size="small" label={`+${(order.items || []).length - 3} más`} sx={{ bgcolor: '#f1f5f9', fontSize: 11 }} />
                          )}
                        </Box>

                        {/* Personal asignado */}
                        {assigned.length > 0 ? (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">Personal asignado:</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                              {assigned.map((s, i) => {
                                const isCoord = order.coordinador?.type === 'presencial' && String(s.id) === String(order.coordinador?.id);
                                const conf = s.confirmado;
                                const confColor = conf === 'si' ? '#16a34a' : conf === 'no' ? '#dc2626' : '#6b7280';
                                const confBg   = conf === 'si' ? '#dcfce7' : conf === 'no' ? '#fee2e2' : '#f3f4f6';
                                const confIcon = conf === 'si' ? '✅' : conf === 'no' ? '❌' : '⏳';
                                const bg = isCoord ? '#fef9c3' : confBg;
                                const color = isCoord ? '#a16207' : confColor;
                                return (
                                  <Chip key={i} size="small" icon={<PersonIcon />}
                                    label={`${isCoord ? '👔 ' : conf ? confIcon + ' ' : ''}${s.name}`}
                                    sx={{ bgcolor: bg, color, fontWeight: isCoord ? 700 : 400, fontSize: 11 }} />
                                );
                              })}
                            </Box>
                            {/* Coordinador virtual */}
                            {order.coordinador?.type === 'virtual' && order.coordinador?.name && (
                              <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 600, display: 'block', mt: 0.5 }}>
                                👔 Coord. virtual: {order.coordinador.name}{order.coordinador.phone ? ` (${order.coordinador.phone})` : ''}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Alert severity="warning" sx={{ py: 0, fontSize: 12, mb: 1 }}>Sin personal asignado</Alert>
                        )}
                      </CardContent>
                      <Box sx={{ px: 2, pb: 2 }}>
                        <Button fullWidth variant="contained" size="small" startIcon={<PersonAddIcon />}
                          onClick={() => {
                            const coord = order.coordinador || null;
                            setSchedDialog({
                              open: true, order,
                              selected: assigned.map(s => String(s.id)),
                              sendWa: true,
                              note: order.staffNote || '',
                              saving: false,
                              // Restaurar coordinador guardado
                              coordType: coord?.type || (assigned.length === 1 ? 'presencial' : 'presencial'),
                              coordEmpId: coord?.type === 'presencial' ? (coord?.id || '') : '',
                              coordVirtualName: coord?.type === 'virtual' ? (coord?.name || '') : '',
                              coordVirtualPhone: coord?.type === 'virtual' ? (coord?.phone || '') : '',
                              manualArrivalTime: '',
                            });
                          }}>
                          {assigned.length ? 'Modificar Personal' : 'Programar Personal'}
                        </Button>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* DIÁLOGO ASIGNACIÓN */}
            <Dialog open={schedDialog.open} onClose={() => !schedDialog.saving && setSchedDialog(p => ({ ...p, open: false }))} maxWidth="md" fullWidth>
              <DialogTitle sx={{ bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <Typography variant="h6" fontWeight={700}>Programar Personal</Typography>
                {schedDialog.order && (
                  <Typography variant="body2" color="text.secondary">
                    {schedDialog.order.client?.name} · {getOrderDate(schedDialog.order)} · {schedDialog.order.event?.city || ''}
                  </Typography>
                )}
              </DialogTitle>
              <DialogContent>
                {schedDialog.order && (
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    {/* Detalles del evento */}
                    <Grid item xs={12} md={5}>
                      <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Detalles del evento</Typography>
                        <Typography variant="body2"><strong>Cliente:</strong> {schedDialog.order.client?.name}</Typography>
                        <Typography variant="body2"><strong>Teléfono:</strong> {schedDialog.order.client?.phone || '—'}</Typography>
                        <Typography variant="body2"><strong>Fecha:</strong> {getOrderDate(schedDialog.order)}</Typography>
                        <Typography variant="body2"><strong>Hora:</strong> {schedDialog.order.event?.start || '—'} – {schedDialog.order.event?.end || '—'}</Typography>
                        <Typography variant="body2"><strong>Dirección:</strong> {schedDialog.order.event?.address || '—'}</Typography>
                        <Typography variant="body2"><strong>Ciudad:</strong> {schedDialog.order.event?.city || schedDialog.order.eventDetails?.city || '—'}</Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Equipos:</Typography>
                        {(schedDialog.order.items || []).map((item, i) => (
                          <Typography key={i} variant="body2">• {item.name || item.productName} {item.qty > 1 ? `(×${item.qty})` : ''}</Typography>
                        ))}
                      </Paper>
                    </Grid>

                    {/* Lista de empleados */}
                    <Grid item xs={12} md={7}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Seleccionar personal ({schedDialog.selected.length} seleccionados)
                      </Typography>
                      {employees.filter(e => e.status === 'activo' || e.status === 'prueba').length === 0 && (
                        <Alert severity="warning">No hay empleados activos. Ve a la pestaña Empleados.</Alert>
                      )}
                      {/* Agrupar por categoría */}
                      {Array.from(new Set(employees.filter(e => ['activo','prueba'].includes(e.status)).map(e => e.category || 'Sin categoría'))).map(cat => {
                        const catEmps = employees.filter(e => (e.category || 'Sin categoría') === cat && ['activo','prueba'].includes(e.status));
                        return (
                          <Box key={cat} sx={{ mb: 2 }}>
                            <Typography variant="caption" fontWeight={700} sx={{ color: '#6366f1', textTransform: 'uppercase', letterSpacing: 1 }}>{cat}</Typography>
                            {catEmps.map(emp => (
                              <Box key={emp._id} sx={{ display: 'flex', alignItems: 'center', py: 0.3 }}>
                                <Checkbox size="small"
                                  checked={schedDialog.selected.includes(String(emp._id))}
                                  onChange={e => setSchedDialog(p => ({
                                    ...p,
                                    selected: e.target.checked
                                      ? [...p.selected, String(emp._id)]
                                      : p.selected.filter(id => id !== String(emp._id))
                                  }))} />
                                <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: '#6366f1', mr: 1 }}>
                                  {emp.firstName.charAt(0)}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>{emp.firstName} {emp.lastName}</Typography>
                                  <Typography variant="caption" color="text.secondary">{emp.phone || '—'}</Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        );
                      })}

                      {/* ─── Designar Coordinador ─── */}
                      {schedDialog.selected.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 2, mt: 2, borderColor: '#6366f1', borderWidth: 1.5 }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#6366f1' }}>
                            👔 Coordinador del evento
                          </Typography>
                          <RadioGroup row value={schedDialog.coordType}
                            onChange={e => setSchedDialog(p => ({ ...p, coordType: e.target.value, coordEmpId: '', coordVirtualName: '', coordVirtualPhone: '' }))}>
                            <FormControlLabel value="presencial" control={<Radio size="small" />}
                              label={<Typography variant="body2">Presencial (va al evento)</Typography>} />
                            <FormControlLabel value="virtual" control={<Radio size="small" />}
                              label={<Typography variant="body2">Virtual (supervisa desde otro evento)</Typography>} />
                          </RadioGroup>

                          {schedDialog.coordType === 'presencial' && (
                            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                              <InputLabel>Empleado coordinador</InputLabel>
                              <Select value={schedDialog.coordEmpId}
                                onChange={e => setSchedDialog(p => ({ ...p, coordEmpId: e.target.value }))}
                                label="Empleado coordinador">
                                <MenuItem value=""><em>— Seleccionar —</em></MenuItem>
                                {employees.filter(e => schedDialog.selected.includes(String(e._id))).map(e => (
                                  <MenuItem key={e._id} value={String(e._id)}>
                                    {e.firstName} {e.lastName} ({e.category || 'Sin cat.'})
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}

                          {schedDialog.coordType === 'virtual' && (
                            <Grid container spacing={1} sx={{ mt: 0.5 }}>
                              <Grid item xs={6}>
                                <TextField fullWidth size="small" label="Nombre del coordinador"
                                  value={schedDialog.coordVirtualName}
                                  onChange={e => setSchedDialog(p => ({ ...p, coordVirtualName: e.target.value }))}
                                  placeholder="Ej: Carlos López" />
                              </Grid>
                              <Grid item xs={6}>
                                <TextField fullWidth size="small" label="Teléfono / WhatsApp"
                                  value={schedDialog.coordVirtualPhone}
                                  onChange={e => setSchedDialog(p => ({ ...p, coordVirtualPhone: e.target.value }))}
                                  placeholder="Ej: 3001234567" />
                              </Grid>
                              <Grid item xs={12}>
                                <Alert severity="info" sx={{ py: 0.5 }}>
                                  El equipo debe contactar a este coordinador en caso de dudas durante el evento.
                                </Alert>
                              </Grid>
                            </Grid>
                          )}
                        </Paper>
                      )}

                      <Divider sx={{ my: 2 }} />
                      <FormControlLabel
                        control={<Checkbox checked={schedDialog.sendWa} onChange={e => setSchedDialog(p => ({ ...p, sendWa: e.target.checked }))} />}
                        label={<Typography variant="body2">Enviar WhatsApp con instrucciones a cada empleado</Typography>}
                      />
                      {schedDialog.sendWa && (
                        <TextField fullWidth size="small" label="⏰ Hora de llegada (se envía en el WhatsApp)"
                          type="time" value={schedDialog.manualArrivalTime}
                          onChange={e => setSchedDialog(p => ({ ...p, manualArrivalTime: e.target.value }))}
                          InputLabelProps={{ shrink: true }} sx={{ mt: 1 }}
                          helperText="Hora a la que deben llegar al lugar del evento (ej: 07:00 para montaje a las 8:10)" />
                      )}
                      {schedDialog.sendWa && (
                        <Box sx={{ mt: 1.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                          <TextField size="small" label="⏱ Duración total del evento (horas)"
                            type="number" inputProps={{ min: 1, max: 24, step: 0.5 }}
                            value={schedDialog.eventHours}
                            onChange={e => setSchedDialog(p => ({ ...p, eventHours: e.target.value }))}
                            sx={{ flex: 1, minWidth: 180 }}
                            helperText="Incluye montaje + evento + desmontaje" />
                          <TextField size="small" label="💰 Pago mínimo ofrecido ($)"
                            type="number" inputProps={{ min: 0, step: 1000 }}
                            value={schedDialog.minPayment}
                            onChange={e => setSchedDialog(p => ({ ...p, minPayment: e.target.value }))}
                            sx={{ flex: 1, minWidth: 180 }}
                            helperText={(() => {
                              const hrs = parseFloat(schedDialog.eventHours) || 0;
                              if (!hrs) return 'Ingresa las horas para ver el cálculo sugerido';
                              const sel = employees.filter(e => schedDialog.selected.includes(String(e._id)));
                              const sugeridos = sel.map(emp => {
                                const cat = categories.find(c => c.code === emp.category || c.name === emp.category);
                                if (!cat) return null;
                                const rate = cat.hourlyRate || Math.round(cat.dailyRate / (cat.standardHours || 8));
                                return Math.round(rate * hrs);
                              }).filter(Boolean);
                              if (!sugeridos.length) return 'Sin tarifa registrada para los empleados seleccionados';
                              const min = Math.min(...sugeridos);
                              const max = Math.max(...sugeridos);
                              return min === max
                                ? `Sugerido: $${min.toLocaleString('es-CO')}`
                                : `Rango sugerido: $${min.toLocaleString('es-CO')} – $${max.toLocaleString('es-CO')} (varía por categoría)`;
                            })()}
                          />
                        </Box>
                      )}
                      <TextField fullWidth size="small" multiline rows={2} label="Nota adicional para el personal (opcional)"
                        value={schedDialog.note}
                        onChange={e => setSchedDialog(p => ({ ...p, note: e.target.value }))}
                        placeholder="Ej: Usar uniforme azul, llevar herramientas... acceso difícil, llevar transporte propio" sx={{ mt: 1 }} />
                    </Grid>
                  </Grid>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Button onClick={() => setSchedDialog(p => ({ ...p, open: false }))} disabled={schedDialog.saving}>Cancelar</Button>
                <Button variant="outlined" color="warning" size="small" startIcon={<StarIcon />}
                  disabled={schedDialog.saving || !schedDialog.selected.length}
                  onClick={async () => {
                    const { order, selected } = schedDialog;
                    const selEmps = employees.filter(e => selected.includes(String(e._id)));
                    const msg = `⭐ *CONDICIONES Y SISTEMA DE CALIFICACIÓN — Inflables Da Silva*\n\nHola! Te recordamos las condiciones de trabajo con nosotros:\n\n🌟 *Sistema de Estrellas (1 a 5)*\nEn cada evento recibirás una calificación en 5 aspectos:\n\n1️⃣ *Cumplimiento* — llegada a tiempo y completar el evento\n2️⃣ *Presentación personal* — uniforme y aspecto correcto\n3️⃣ *Desempeño en el montaje* — rapidez y cuidado al armar\n4️⃣ *Desempeño durante el evento* — atención, actitud y trato\n5️⃣ *Desempeño en el desmontaje* — orden, cuidado y velocidad\n\n¿Para qué sirven tus estrellas?\n⭐ Los mejores valorados son los primeros en ser programados en nuevos eventos.\n🏆 Periódicamente la empresa premia a los de mejor desempeño con:\n• 💵 Pagos extra en efectivo\n• 🎬 Entradas dobles al cine\n• 🍽️ Bonos para restaurantes\n• 🛒 Bonos para supermercados y almacenes de cadena\n• 🎁 Premios sorpresa\n¡El siguiente premiado puedes ser tú!\n\n⏰ *Puntualidad*\nLlegar tarde genera descuento:\n• 1 a 15 min: -$5.000\n• 16 a 30 min: -$15.000\n• Más de 30 min: -$25.000\n\n🚫 *Causales de retiro inmediato*\n• No presentarse sin avisar\n• Conducta inapropiada con clientes\n• Daño intencional a equipos\n\n✅ *Confirmación de eventos*\nCuando te citemos, responde *SI* o *NO* a este número. Tu respuesta queda registrada.\n\n¡Gracias por ser parte del equipo! 💪`;
                    let sent = 0;
                    for (const emp of selEmps) {
                      const phone = emp.whatsapp || emp.phone;
                      if (!phone) continue;
                      try {
                        await authFetch(`${API_URL}/staff/employees/${emp._id}/notify-event`, {
                          method: 'POST', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ customMessage: msg })
                        });
                        sent++;
                      } catch (_) {}
                    }
                    setSnack({ open: true, message: `⭐ Condiciones enviadas a ${sent} empleado${sent !== 1 ? 's' : ''}`, severity: 'success' });
                  }}>
                  Enviar condiciones
                </Button>
                <Button variant="contained" onClick={handleConfirmAssignment}
                  disabled={schedDialog.saving || !schedDialog.selected.length}
                  startIcon={schedDialog.saving ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}>
                  {schedDialog.saving ? 'Guardando...' : `Confirmar (${schedDialog.selected.length} persona${schedDialog.selected.length !== 1 ? 's' : ''})`}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        );
      })()}

      {tab === 'attendance' && (
        <>
          {/* ─── Panel rápido: Entrada/Salida por empleado (sin PIN) ─── */}
          {(() => {
            // Admin ve todos; coordinador solo ve los de sus eventos + él mismo
            const attendancePool = isCoordinador
              ? (() => {
                  const pool = [...coordEmployees];
                  // Incluir al coordinador mismo si tiene registro de empleado
                  const meEmp = employees.find(e => e.employeeCode === authUser?.username);
                  if (meEmp && !pool.some(e => String(e._id) === String(meEmp._id))) pool.unshift(meEmp);
                  return pool;
                })()
              : employees;
            const activePool = attendancePool.filter(e => ['activo','prueba'].includes(e.status));
            if (!['admin','manager'].includes(authUser?.role) || activePool.length === 0) return null;
            return (
            <Paper sx={{ p: 2.5, borderRadius: 3, mb: 3, border: '2px solid #6366f1' }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: '#6366f1' }}>
                ⚡ Registro Rápido — sin PIN
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {activePool.map(emp => {
                  const attHoy = todayAtt.find(a => String(a.employeeId) === String(emp._id));
                  const tieneEntrada = !!attHoy?.checkIn;
                  const tieneSalida  = !!attHoy?.checkOut;
                  return (
                    <Box key={emp._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2, bgcolor: tieneSalida ? '#f0fdf4' : tieneEntrada ? '#eff6ff' : '#fafafa', border: '1px solid #e5e7eb' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{emp.firstName} {emp.lastName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tieneSalida ? `✅ Salida: ${new Date(attHoy.checkOut).toLocaleTimeString('es-CO',{hour:'2-digit',minute:'2-digit'})}` :
                           tieneEntrada ? `🟢 Entró: ${new Date(attHoy.checkIn).toLocaleTimeString('es-CO',{hour:'2-digit',minute:'2-digit'})}` :
                           '⬜ Sin registro hoy'}
                        </Typography>
                      </Box>
                      <Button size="small" variant={tieneEntrada ? 'outlined' : 'contained'} color="success"
                        disabled={tieneEntrada || loading}
                        onClick={() => {
                          const ev = todayEvents[0]?.title || todayEvents[0]?.name || '';
                          if (ev) {
                            // Hay evento — registrar directo
                            setLoading(true);
                            authFetch(`${API_URL}/staff/attendance/coordinator-clock`, {
                              method: 'POST', headers: {'Content-Type':'application/json'},
                              body: JSON.stringify({ employeeId: emp._id, action: 'in', eventName: ev })
                            }).then(r => r.json()).then(d => {
                              setSnack({ open: true, message: d.ok ? d.message : d.error, severity: d.ok ? 'success' : 'error' });
                              if (d.ok) loadToday();
                            }).catch(() => setSnack({ open: true, message: 'Error de conexión', severity: 'error' }))
                              .finally(() => setLoading(false));
                          } else {
                            // Sin evento — pedir motivo
                            setMotivoDialog({ open: true, emp, motivo: '' });
                          }
                        }}
                        sx={{ minWidth: 80, fontSize: 11 }}>
                        Entrada
                      </Button>
                      <Button size="small" variant={tieneSalida ? 'outlined' : 'contained'} color="error"
                        disabled={!tieneEntrada || tieneSalida || loading}
                        onClick={async () => {
                          setLoading(true);
                          try {
                            const r = await authFetch(`${API_URL}/staff/attendance/coordinator-clock`, {
                              method: 'POST', headers: {'Content-Type':'application/json'},
                              body: JSON.stringify({ employeeId: emp._id, action: 'out' })
                            });
                            const d = await r.json();
                            setSnack({ open: true, message: d.ok ? `${d.message} — ${formatCurrency(d.attendance?.earnings?.total || 0)}` : d.error, severity: d.ok ? 'success' : 'error' });
                            if (d.ok) loadToday();
                          } catch { setSnack({ open: true, message: 'Error de conexión', severity: 'error' }); }
                          setLoading(false);
                        }}
                        sx={{ minWidth: 80, fontSize: 11 }}>
                        Salida
                      </Button>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
            );
          })()}

          {/* Diálogo motivo de entrada sin evento */}
          <Dialog open={motivoDialog.open} onClose={() => setMotivoDialog({ open: false, emp: null, motivo: '' })} maxWidth="xs" fullWidth>
            <DialogTitle>¿Motivo de entrada?</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No hay un evento programado para hoy. Indica el motivo o actividad por la que vas a trabajar:
              </Typography>
              <TextField fullWidth multiline rows={3} label="Motivo / Actividad" autoFocus
                value={motivoDialog.motivo}
                onChange={e => setMotivoDialog(p => ({ ...p, motivo: e.target.value }))}
                placeholder="Ej: Mantenimiento de equipos, reunión administrativa, inventario..." />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setMotivoDialog({ open: false, emp: null, motivo: '' })}>Cancelar</Button>
              <Button variant="contained" color="success" disabled={!motivoDialog.motivo.trim() || loading}
                onClick={async () => {
                  setLoading(true);
                  try {
                    const r = await authFetch(`${API_URL}/staff/attendance/coordinator-clock`, {
                      method: 'POST', headers: {'Content-Type':'application/json'},
                      body: JSON.stringify({ employeeId: motivoDialog.emp._id, action: 'in', eventName: motivoDialog.motivo.trim() })
                    });
                    const d = await r.json();
                    setSnack({ open: true, message: d.ok ? d.message : d.error, severity: d.ok ? 'success' : 'error' });
                    if (d.ok) { loadToday(); setMotivoDialog({ open: false, emp: null, motivo: '' }); }
                  } catch { setSnack({ open: true, message: 'Error de conexión', severity: 'error' }); }
                  setLoading(false);
                }}>
                Registrar Entrada
              </Button>
            </DialogActions>
          </Dialog>

          {/* Reloj */}
          <AttendanceClock />
          {/* Escáner para coordinador/admin (registro por QR sin PIN) */}
          <CoordinatorScanner onSuccess={loadToday} todayEvents={todayEvents} />
          <TodayTable
            data={isCoordinador && coordEmployees.length > 0
              ? todayAtt.filter(a => coordEmployees.some(e => String(e._id) === String(a.employeeId)))
              : todayAtt}
            loading={loading} onRefresh={loadToday} categories={categories}
            isAdmin={authUser?.role === 'admin'}
            isCoord={authUser?.role === 'manager'}
            onEdit={handleEditAttRecord}
            onClearCheckout={handleClearCheckout}
            onDelete={handleDeleteAttRecord} />

          {/* ─── Turnos abiertos: visible para admin Y coordinador ─── */}
          {['admin', 'manager'].includes(authUser?.role) && openShifts.length > 0 && (
            <Alert severity="warning" sx={{ mt: 3, mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                ⚠️ {openShifts.length} turno{openShifts.length > 1 ? 's' : ''} sin registrar salida
              </Typography>
              {openShifts.map(s => (
                <Typography key={String(s.attendanceId)} variant="body2">
                  • <strong>{s.employeeName}</strong> — entró {new Date(s.checkInTime).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })} {s.eventName ? `(${s.eventName})` : ''}
                  {' '}
                  <Button size="small" variant="outlined" color="warning" sx={{ ml: 1, py: 0, px: 1 }}
                    onClick={() => setCorrDialog({ open: true, saving: false, employeeId: String(s.employeeId), date: new Date(s.date).toISOString().slice(0, 10), checkInTime: '', checkOutTime: '', eventName: s.eventName || '', reason: 'Coordinador registró salida' })}>
                    Registrar salida
                  </Button>
                </Typography>
              ))}
            </Alert>
          )}

          {/* ─── Panel de Correcciones manuales (solo admin) ─── */}
          {authUser?.role === 'admin' && (
            <Box sx={{ mt: 2 }}>
              <Paper variant="outlined" sx={{ p: 2, borderColor: '#6366f1' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" fontWeight={700}>🔧 Correcciones de Asistencia</Typography>
                  <Button variant="outlined" size="small" startIcon={<EditIcon />}
                    onClick={() => setCorrDialog({ open: true, saving: false, employeeId: '', date: new Date().toISOString().slice(0, 10), checkInTime: '', checkOutTime: '', eventName: '', reason: '' })}>
                    Registrar manualmente
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Úsalo cuando el coordinador olvide registrar la entrada o salida de un empleado. Se guarda con nota de corrección manual.
                </Typography>
              </Paper>
            </Box>
          )}

          {/* ─── Dialog de corrección manual ─── */}
          <Dialog open={corrDialog.open} onClose={() => setCorrDialog(p => ({ ...p, open: false }))} maxWidth="sm" fullWidth>
            <DialogTitle>🔧 Corrección Manual de Asistencia</DialogTitle>
            <DialogContent dividers>
              <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                Completa solo los campos que necesitas corregir. Si el empleado no entró ni salió, llena ambos. Si solo falta la salida, llena solo "Hora de salida".
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Empleado</InputLabel>
                    <Select value={corrDialog.employeeId} label="Empleado"
                      onChange={e => setCorrDialog(p => ({ ...p, employeeId: e.target.value }))}>
                      <MenuItem value=""><em>— Seleccionar —</em></MenuItem>
                      {employees.filter(e => ['activo','prueba'].includes(e.status)).map(e => (
                        <MenuItem key={e._id} value={String(e._id)}>
                          {e.firstName} {e.lastName} ({e.category || '—'})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" type="date" label="Fecha del evento"
                    value={corrDialog.date}
                    onChange={e => setCorrDialog(p => ({ ...p, date: e.target.value }))}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" type="time" label="Hora de entrada"
                    value={corrDialog.checkInTime}
                    onChange={e => setCorrDialog(p => ({ ...p, checkInTime: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    helperText="Dejar vacío si ya está registrada" />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" type="time" label="Hora de salida"
                    value={corrDialog.checkOutTime}
                    onChange={e => setCorrDialog(p => ({ ...p, checkOutTime: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    helperText="Dejar vacío si ya está registrada" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Nombre del evento (opcional)"
                    value={corrDialog.eventName}
                    onChange={e => setCorrDialog(p => ({ ...p, eventName: e.target.value }))}
                    placeholder="Ej: Evento Ana García" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Motivo de la corrección" required
                    value={corrDialog.reason}
                    onChange={e => setCorrDialog(p => ({ ...p, reason: e.target.value }))}
                    placeholder="Ej: Coordinador olvidó registrar la salida"
                    helperText="Se guarda como nota en el registro de asistencia" />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setCorrDialog(p => ({ ...p, open: false }))} disabled={corrDialog.saving}>Cancelar</Button>
              <Button variant="contained" onClick={handleManualCorrect}
                disabled={corrDialog.saving || !corrDialog.employeeId || (!corrDialog.checkInTime && !corrDialog.checkOutTime) || !corrDialog.reason}
                startIcon={corrDialog.saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
                {corrDialog.saving ? 'Guardando...' : 'Guardar corrección'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {tab === 'employees' && (
        <EmployeesList employees={isCoordinador ? coordEmployees : employees} categories={categories} loading={loading}
          onEdit={(e) => setEmpDialog({ open: true, data: e })}
          onDelete={(e) => setConfirmDelete({ open: true, target: e, type: 'employee' })}
          onViewQR={(e) => setQrDialog({ open: true, data: e })}
          onAdd={() => setEmpDialog({ open: true, data: null })}
          onRefresh={loadEmployees}
          onEval={(e) => setEvalDialog({ open: true, data: e })}
          onSendAccess={handleSendAccess}
          onViewHistory={openHistoryDialog}
          isAdmin={authUser?.role === 'admin'}
        />
      )}

      {tab === 'payments' && <PaymentsPending employees={pending} loading={loading} onPay={openPayDialog} onViewHistory={openHistoryDialog} onRefresh={loadPending} categories={categories} />}
      {tab === 'categories'   && <CategoriesManager categories={categories} loading={loading} onSave={handleSaveCategory} onDelete={(c) => setConfirmDelete({ open: true, target: c, type: 'category' })} onRefresh={loadCategories} />}
      {tab === 'instructions' && <InstructionsManager />}
      {tab === 'savings' && (
        <AlcanciaAdminTab employees={employees} authUser={authUser} />
      )}

      <EmployeeDialog open={empDialog.open} onClose={() => setEmpDialog({ open: false, data: null })} employee={empDialog.data} categories={categories} onSave={handleSaveEmployee} onSendAccess={handleSendAccess} />
      <QRDialog open={qrDialog.open} onClose={() => setQrDialog({ open: false, data: null })} employee={qrDialog.data} categories={categories} />
      <EmployeeEvalDialog open={evalDialog.open} onClose={() => setEvalDialog({ open: false, data: null })} employee={evalDialog.data} />

      {/* DIÁLOGO CONFIRMACIÓN ELIMINAR */}
      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, target: null, type: '' })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: '#fef2f2' }}>
          <Typography variant="h6" fontWeight={700} color="error">Confirmar eliminación</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {confirmDelete.type === 'employee' && (
            <Typography>
              ¿Eliminar al empleado <strong>{confirmDelete.target?.firstName} {confirmDelete.target?.lastName}</strong>?
              Esta acción no se puede deshacer.
            </Typography>
          )}
          {confirmDelete.type === 'category' && (
            <Typography>
              ¿Eliminar la categoría <strong>"{confirmDelete.target?.name}"</strong>?
              No se puede eliminar si tiene empleados asignados.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, target: null, type: '' })}>Cancelar</Button>
          <Button variant="contained" color="error"
            onClick={confirmDelete.type === 'employee' ? handleDeleteEmployee : handleDeleteCategory}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIÁLOGO PROCESAR PAGO */}
      <Dialog open={payDialog.open} onClose={() => !payDialog.loading && setPayDialog(EMPTY_PAY)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f0fdf4', pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>💳 Procesar Pago</Typography>
          {payDialog.emp && <Typography variant="body2" color="text.secondary">{payDialog.emp.firstName} {payDialog.emp.lastName} — {payDialog.emp.employeeCode}</Typography>}
        </DialogTitle>
        <DialogContent dividers>
          {payDialog.loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /><Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Procesando...</Typography></Box>}
          {!payDialog.loading && payDialog.details && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>

              {/* Resumen montos */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f0fdf4' }}>
                <Grid container spacing={2}>
                  <Grid item xs={4} sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Turnos</Typography>
                    <Typography variant="h5" fontWeight={700}>{payDialog.details.attendanceIds?.length || 0}</Typography>
                  </Grid>
                  <Grid item xs={4} sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Bruto</Typography>
                    <Typography variant="h5" fontWeight={700} color="#10b981">
                      {formatCurrency(payDialog.details.summary?.totalEarnings || 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">A pagar</Typography>
                    <Typography variant="h5" fontWeight={700} color="#059669">
                      {formatCurrency(payDialog.details.summary?.netToPay || payDialog.details.summary?.totalEarnings || 0)}
                    </Typography>
                  </Grid>
                </Grid>
                {payDialog.details.summary?.pendingAdvances > 0 && (
                  <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                    Descuento anticipos: {formatCurrency(payDialog.details.summary.pendingAdvances)}
                  </Alert>
                )}
              </Paper>

              {/* Método de pago */}
              <FormControl fullWidth>
                <InputLabel>Método de Pago</InputLabel>
                <Select value={payDialog.payMethod} label="Método de Pago"
                  onChange={e => setPayDialog(p => ({ ...p, payMethod: e.target.value }))}>
                  {PAYMENT_METHODS.map(m => (
                    <MenuItem key={m.value} value={m.value}>{m.icon} {m.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Datos de la cuenta del empleado */}
              {payDialog.emp?.paymentDetails && payDialog.payMethod && (
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fffbeb' }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>📋 Datos de pago del empleado:</Typography>
                  {payDialog.payMethod === 'nequi' && payDialog.emp.paymentDetails.nequiNumber && (
                    <Typography variant="body2" fontWeight={600}>Nequi: {payDialog.emp.paymentDetails.nequiNumber}</Typography>
                  )}
                  {payDialog.payMethod === 'daviplata' && payDialog.emp.paymentDetails.daviplataNumber && (
                    <Typography variant="body2" fontWeight={600}>Daviplata: {payDialog.emp.paymentDetails.daviplataNumber}</Typography>
                  )}
                  {payDialog.payMethod === 'transferencia' && (
                    <Typography variant="body2" fontWeight={600}>
                      {payDialog.emp.paymentDetails.bankName} — {payDialog.emp.paymentDetails.bankAccountType} — {payDialog.emp.paymentDetails.bankAccountNumber}
                    </Typography>
                  )}
                  {payDialog.payMethod === 'efectivo' && (
                    <Typography variant="body2" color="text.secondary">Pago en efectivo</Typography>
                  )}
                </Paper>
              )}

              {/* Monto real pagado */}
              <TextField
                fullWidth
                label="Monto pagado"
                value={payDialog.montoReal}
                onChange={e => setPayDialog(p => ({ ...p, montoReal: e.target.value }))}
                placeholder="Ej: 120000"
                helperText="Edite si la transferencia fue por un monto diferente al calculado"
                InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography> }}
              />

              {/* Comprobantes múltiples */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Comprobantes de pago (opcional — puede adjuntar varios)
                </Typography>
                <input type="file" accept="image/*,application/pdf" ref={payFileRef} style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file) setPayDialog(p => ({ ...p, comprobantes: [...p.comprobantes, file] }));
                    if (payFileRef.current) payFileRef.current.value = '';
                  }} />
                {payDialog.comprobantes.map((f, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>📎 {f.name}</Typography>
                    <IconButton size="small" onClick={() => setPayDialog(p => ({ ...p, comprobantes: p.comprobantes.filter((_, j) => j !== i) }))}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button variant="outlined" size="small" startIcon={<CameraAltIcon />}
                  onClick={() => payFileRef.current?.click()}>
                  {payDialog.comprobantes.length === 0 ? 'Adjuntar comprobante' : 'Agregar otro comprobante'}
                </Button>
              </Box>

              {/* Notas */}
              <TextField fullWidth multiline rows={2} label="Notas del pago (opcional)" value={payDialog.notes}
                onChange={e => setPayDialog(p => ({ ...p, notes: e.target.value }))}
                placeholder="Ej: Pago quincenal, evento 14 de marzo..." />

              <Alert severity="success" icon={false} sx={{ py: 0.5 }}>
                ✅ El pago quedará registrado automáticamente en <strong>Egresos</strong> de contabilidad.
              </Alert>
            </Box>
          )}
          {!payDialog.loading && !payDialog.details && (
            <Alert severity="warning" sx={{ mt: 1 }}>No se pudieron cargar los detalles. Verifique que el empleado tiene asistencias pendientes de pago.</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPayDialog(EMPTY_PAY)} disabled={payDialog.loading}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={handleProcessPayment}
            disabled={payDialog.loading || !payDialog.details?.attendanceIds?.length}
            startIcon={payDialog.loading ? <CircularProgress size={18} /> : <PaymentsIcon />}
            sx={{ minWidth: 160 }}>
            Confirmar Pago
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIÁLOGO CREDENCIALES DE ACCESO */}
      <Dialog open={credDialog.open} onClose={() => !credDialog.loading && setCredDialog(d => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f5f3ff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon sx={{ color: '#6366f1' }} />
            <Box>
              <Typography variant="h6" fontWeight={700}>Acceso al Sistema</Typography>
              {credDialog.emp && <Typography variant="body2" color="text.secondary">{credDialog.emp.firstName} {credDialog.emp.lastName}</Typography>}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {credDialog.loading ? (
            <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /><Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Generando acceso...</Typography></Box>
          ) : (
            <>
              <Alert severity={credDialog.created ? 'success' : 'info'} sx={{ mb: 2 }}>
                {credDialog.created ? 'Cuenta creada correctamente.' : 'Credenciales de acceso:'}
                {credDialog.waSent ? ' Se enviaron las credenciales por WhatsApp.' : ''}
              </Alert>
              <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>Datos de ingreso:</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>🌐 <strong>URL:</strong> {process.env.REACT_APP_FRONTEND_URL || 'https://app.inflablesdasilva.com'}</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>👤 <strong>Usuario:</strong> <code style={{ background: '#e2e8f0', padding: '2px 6px', borderRadius: 4 }}>{credDialog.username}</code></Typography>
                <Typography variant="body2">🔑 <strong>Contraseña:</strong> <code style={{ background: '#fef3c7', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>{credDialog.password}</code></Typography>
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setCredDialog(d => ({ ...d, open: false }))} disabled={credDialog.loading}>Cerrar</Button>
          {!credDialog.loading && (
            <Button variant="outlined" color="warning" size="small"
              onClick={async () => {
                setCredDialog(d => ({ ...d, loading: true }));
                const r = await authFetch(`${API_URL}/staff/employees/${credDialog.emp._id}/create-account`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reset: true }) });
                const d2 = await r.json();
                if (d2.ok) setCredDialog(prev => ({ ...prev, loading: false, username: d2.username, password: d2.password, email: d2.email, waSent: d2.waSent, created: false }));
                else setCredDialog(prev => ({ ...prev, loading: false }));
              }}>
              Resetear contraseña
            </Button>
          )}
          {!credDialog.loading && (
            <Button variant="outlined" startIcon={<WhatsAppIcon />} color="success"
              onClick={() => {
                const msg = encodeURIComponent(`Hola ${credDialog.emp?.firstName}! Tu acceso al sistema:\n🌐 https://app.inflablesdasilva.com\n👤 Usuario: ${credDialog.username}\n🔑 Contraseña: ${credDialog.password}`);
                const phone = (credDialog.emp?.whatsapp || credDialog.emp?.phone || '').replace(/\D/g, '');
                window.open(`https://wa.me/${phone.startsWith('57') ? phone : '57' + phone}?text=${msg}`, '_blank');
              }}>
              Enviar por WhatsApp
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* DIÁLOGO SUBIR COMPROBANTE */}
      <Dialog open={receiptDialog.open} onClose={() => !receiptDialog.uploading && setReceiptDialog({ open: false, paymentId: null, paymentNumber: '', uploading: false })} maxWidth="xs" fullWidth>
        <DialogTitle>Subir Comprobante de Pago</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pago: <strong>{receiptDialog.paymentNumber}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Adjunta la imagen o PDF del comprobante (Nequi, Daviplata, transferencia, etc.)
          </Typography>
          {receiptDialog.uploading
            ? <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress /></Box>
            : (
              <Button component="label" variant="outlined" fullWidth startIcon={<DownloadIcon />}>
                Seleccionar archivo
                <input type="file" hidden accept="image/*,.pdf" onChange={e => handleUploadReceipt(e.target.files?.[0])} />
              </Button>
            )
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiptDialog({ open: false, paymentId: null, paymentNumber: '', uploading: false })} disabled={receiptDialog.uploading}>
            Omitir por ahora
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIÁLOGO HISTORIAL DE PAGOS */}
      <Dialog open={histDialog.open} onClose={() => setHistDialog({ open: false, emp: null, payments: [], loading: false })} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight={700}>Historial de Pagos</Typography>
          {histDialog.emp && <Typography variant="body2" color="text.secondary">{histDialog.emp.fullName || `${histDialog.emp.firstName} ${histDialog.emp.lastName}`} — {histDialog.emp.employeeCode}</Typography>}
        </DialogTitle>
        <DialogContent dividers>
          {histDialog.loading && <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box>}
          {!histDialog.loading && histDialog.payments.length === 0 && (
            <Alert severity="info">No hay pagos registrados para este empleado.</Alert>
          )}
          {!histDialog.loading && histDialog.payments.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Número</strong></TableCell>
                    <TableCell><strong>Período</strong></TableCell>
                    <TableCell align="right"><strong>Días</strong></TableCell>
                    <TableCell align="right"><strong>Bruto</strong></TableCell>
                    <TableCell align="right"><strong>Neto</strong></TableCell>
                    <TableCell><strong>Método</strong></TableCell>
                    <TableCell><strong>Comprobante</strong></TableCell>
                    {authUser?.role === 'admin' && <TableCell align="center"><strong>Eliminar</strong></TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {histDialog.payments.map(p => {
                    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');
                    return (
                      <TableRow key={p._id} hover>
                        <TableCell>{p.paymentNumber}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {p.periodStart ? new Date(p.periodStart).toLocaleDateString('es-CO') : '—'}
                          {' → '}
                          {p.periodEnd ? new Date(p.periodEnd).toLocaleDateString('es-CO') : '—'}
                        </TableCell>
                        <TableCell align="right">{p.daysWorked}</TableCell>
                        <TableCell align="right">${(p.grossAmount || 0).toLocaleString('es-CO')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#10b981' }}>${(p.netAmount || 0).toLocaleString('es-CO')}</TableCell>
                        <TableCell><Chip label={p.paymentMethod || '—'} size="small" /></TableCell>
                        <TableCell>
                          {p.receipt?.filename
                            ? <Button size="small" href={`${baseUrl}${p.receipt.filename}`} target="_blank" startIcon={<DownloadIcon />}>Ver</Button>
                            : (
                              <Button size="small" component="label" variant="outlined" startIcon={<DownloadIcon />}>
                                Subir
                                <input type="file" hidden accept="image/*,.pdf" onChange={e => {
                                  if (e.target.files?.[0]) {
                                    handleUploadReceipt(e.target.files[0], p._id);
                                  }
                                }} />
                              </Button>
                            )
                          }
                        </TableCell>
                        {authUser?.role === 'admin' && (
                          <TableCell align="center">
                            <Tooltip title="Eliminar liquidación (revierte asistencias a pendiente)">
                              <IconButton size="small" color="error" onClick={async () => {
                                if (!window.confirm(`¿Eliminar liquidación ${p.paymentNumber}? Las asistencias volverán a estado "pendiente".`)) return;
                                const r = await authFetch(`${API_URL}/staff/payments/${p._id}`, { method: 'DELETE' });
                                const d = await r.json();
                                setSnack({ open: true, message: d.ok ? d.message : d.error, severity: d.ok ? 'success' : 'error' });
                                if (d.ok) {
                                  setHistDialog(h => ({ ...h, payments: h.payments.filter(x => x._id !== p._id) }));
                                  loadPending();
                                }
                              }}><DeleteIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistDialog({ open: false, emp: null, payments: [], loading: false })}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}

// ===================== PANEL DE INICIO PARA EMPLEADOS =====================
export function EmployeeDashboard({ calendarEvents = [], setActiveSection }) {
  const authUser = useMemo(() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } }, []);
  const [profile, setProfile]       = useState(null);
  const [todayAtt, setTodayAtt]     = useState([]);
  const [myHistory, setMyHistory]   = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [loading, setLoading]       = useState(false);
  const [snack, setSnack]           = useState({ open: false, message: '', severity: 'success' });
  const [pwdDialog, setPwdDialog]   = useState({ open: false, current: '', next: '', confirm: '', saving: false, error: '' });
  const [clockPin, setClockPin]     = useState('');
  const [clockEvent, setClockEvent] = useState('');

  const BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api';

  const loadData = useCallback(async () => {
    try {
      const [empRes, attRes] = await Promise.all([
        authFetch(`${BASE}/staff/employees?status=todos`),
        authFetch(`${BASE}/staff/attendance/today`),
      ]);
      const [empD, attD] = await Promise.all([empRes.json(), attRes.json()]);
      if (empD.ok) {
        const me = empD.employees.find(e => e.employeeCode === authUser?.username);
        setProfile(me || null);
        if (me?._id) loadHistory(me._id);
      }
      if (attD.ok) setTodayAtt(attD.attendance || []);
    } catch (e) { console.error(e); }
  }, [authUser?.username]); // eslint-disable-line

  const loadHistory = async (empId) => {
    setHistLoading(true);
    try {
      const r = await authFetch(`${BASE}/staff/portal/${empId}/history`);
      const d = await r.json();
      if (d.ok) setMyHistory((d.history || []).slice(0, 10));
    } catch (_) {}
    setHistLoading(false);
  };

  useEffect(() => { loadData(); }, [loadData]);

  const handleChangePwd = async () => {
    const { current, next, confirm } = pwdDialog;
    if (next !== confirm) return setPwdDialog(p => ({ ...p, error: 'Las contraseñas nuevas no coinciden' }));
    if (next.length < 6) return setPwdDialog(p => ({ ...p, error: 'La nueva contraseña debe tener al menos 6 caracteres' }));
    setPwdDialog(p => ({ ...p, saving: true, error: '' }));
    try {
      const r = await authFetch(`${BASE}/auth/change-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: current, newPassword: next, username: authUser?.username })
      });
      const d = await r.json();
      if (d.success) {
        setPwdDialog({ open: false, current: '', next: '', confirm: '', saving: false, error: '' });
        setSnack({ open: true, message: '✅ Contraseña actualizada exitosamente', severity: 'success' });
      } else {
        setPwdDialog(p => ({ ...p, saving: false, error: d.message || 'Error al cambiar contraseña' }));
      }
    } catch (e) {
      setPwdDialog(p => ({ ...p, saving: false, error: 'Error de conexión' }));
    }
  };

  const clockIn = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BASE}/staff/attendance/pin/clock-in`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeCode: authUser?.username, pin: clockPin, eventName: clockEvent }),
      });
      const d = await r.json();
      setSnack({ open: true, message: d.ok ? d.message : d.error, severity: d.ok ? 'success' : 'error' });
      if (d.ok) { setClockPin(''); setClockEvent(''); loadData(); }
    } catch (_) { setSnack({ open: true, message: 'Error de conexión', severity: 'error' }); }
    setLoading(false);
  };

  const clockOut = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BASE}/staff/attendance/pin/clock-out`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeCode: authUser?.username, pin: clockPin }),
      });
      const d = await r.json();
      setSnack({ open: true, message: d.ok ? `${d.message} — Ganado: ${formatCurrency(d.attendance?.earnings?.total)}` : d.error, severity: d.ok ? 'success' : 'error' });
      if (d.ok) { setClockPin(''); loadData(); }
    } catch (_) { setSnack({ open: true, message: 'Error de conexión', severity: 'error' }); }
    setLoading(false);
  };

  const st = profile?.stats || {};
  const myAtt = todayAtt.filter(a => a.employeeCode === authUser?.username);
  const hasActiveEntry = myAtt.some(a => a.checkIn && !a.checkOut);

  // Ingresos del mes actual calculados desde historial
  const now = new Date();
  const thisMonthEarned = myHistory
    .filter(h => { const d = new Date(h.date); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); })
    .reduce((s, h) => s + (h.earnings?.total || 0), 0);

  // Próximos eventos del calendario (7 días)
  const nextWeek = new Date(now); nextWeek.setDate(now.getDate() + 7);
  const upcoming = calendarEvents
    .filter(ev => { const d = new Date(ev.start); return d >= now && d <= nextWeek && ev.type === 'evento'; })
    .sort((a, b) => new Date(a.start) - new Date(b.start))
    .slice(0, 5);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Saludo */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800}>
          ¡Hola, {profile?.firstName || authUser?.username}! 👋
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {new Date().toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
        </Typography>
      </Box>

      {/* ── Tarjetas de resumen ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center', bgcolor: '#f0fdf4' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>DÍAS TRABAJADOS</Typography>
            <Typography variant="h4" fontWeight={800} color="#16a34a">{st.totalDaysWorked || 0}</Typography>
            <Typography variant="caption" color="text.secondary">histórico</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center', bgcolor: '#eff6ff' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>ESTE MES</Typography>
            <Typography variant="h6" fontWeight={800} color="#1d4ed8" sx={{ mt: 0.5, fontSize: { xs: '1rem', sm: '1.25rem' } }}>{formatCurrency(thisMonthEarned)}</Typography>
            <Typography variant="caption" color="text.secondary">ingresos</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center', bgcolor: '#fef9c3' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>POR COBRAR</Typography>
            <Typography variant="h6" fontWeight={800} color="#d97706" sx={{ mt: 0.5, fontSize: { xs: '1rem', sm: '1.25rem' } }}>{formatCurrency(st.pendingPayment || 0)}</Typography>
            <Typography variant="caption" color="text.secondary">saldo</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center', bgcolor: '#fdf2f8' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>YA COBRADO</Typography>
            <Typography variant="h6" fontWeight={800} color="#9d174d" sx={{ mt: 0.5, fontSize: { xs: '1rem', sm: '1.25rem' } }}>{formatCurrency(st.totalPaid || 0)}</Typography>
            <Typography variant="caption" color="text.secondary">total</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* ── Columna izquierda: Asistencia hoy ── */}
        <Grid item xs={12} md={7}>
          {/* Info asistencia (solo lectura, el coordinador registra) */}
          <Paper sx={{ p: 2.5, borderRadius: 3, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              <AccessTimeIcon sx={{ verticalAlign: 'middle', mr: 0.5, color: '#3b82f6', fontSize: '1.1rem' }} />
              Asistencia de Hoy
            </Typography>
            <Alert severity="info" sx={{ py: 0.5, mb: 1.5 }}>
              El coordinador del evento registra tu entrada y salida. Si hay un error, contacta al administrador.
            </Alert>

            {/* Asistencia hoy */}
            {myAtt.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>HOY</Typography>
                {myAtt.map((a, i) => (
                  <Box key={a._id || i} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e5e7eb', mt: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{a.eventName || 'Sin evento'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Entrada: {formatTime(a.checkIn)} · Salida: {a.checkOut ? formatTime(a.checkOut) : 'en curso'}
                      </Typography>
                    </Box>
                    {a.checkOut && a.earnings?.total > 0 && (
                      <Chip size="small" label={`+${formatCurrency(a.earnings.total)}`} sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 700 }} />
                    )}
                    {!a.checkOut && (
                      <Chip size="small" label="En curso" sx={{ bgcolor: '#dbeafe', color: '#1d4ed8' }} />
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          {/* Historial reciente */}
          <Paper sx={{ p: 2.5, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                <HistoryIcon sx={{ verticalAlign: 'middle', mr: 0.5, color: '#6366f1', fontSize: '1.1rem' }} />
                Últimos Eventos
              </Typography>
              {histLoading && <CircularProgress size={16} />}
              <Button size="small" onClick={() => setActiveSection && setActiveSection('staff')}>
                Ver todo
              </Button>
            </Box>
            {myHistory.length === 0 && !histLoading && (
              <Typography variant="body2" color="text.secondary">Sin historial aún.</Typography>
            )}
            {myHistory.map((h, i) => (
              <Box key={h._id || i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid #f1f5f9' }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{h.eventName || 'Evento'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {h.date ? new Date(h.date).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                    {h.hoursWorked ? ` · ${h.hoursWorked}h` : ''}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" fontWeight={700} color="#16a34a">{formatCurrency(h.earnings?.total || 0)}</Typography>
                  <Chip size="small" label={h.paymentStatus === 'pagado' ? 'Pagado' : 'Pendiente'}
                    sx={{ bgcolor: h.paymentStatus === 'pagado' ? '#dcfce7' : '#fef9c3', color: h.paymentStatus === 'pagado' ? '#16a34a' : '#d97706', height: 18, fontSize: '0.65rem' }} />
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* ── Columna derecha: Próximos eventos + Acceso rápido ── */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2.5, borderRadius: 3, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
              📅 Próximos Eventos
            </Typography>
            {upcoming.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CheckCircleIcon sx={{ color: '#cbd5e1', fontSize: 36 }} />
                <Typography variant="body2" color="text.secondary">Sin eventos esta semana</Typography>
              </Box>
            ) : upcoming.map(ev => (
              <Box key={ev.id} sx={{ display: 'flex', gap: 1.5, mb: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e5e7eb' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff1f2', color: '#e11d48', borderRadius: 2, minWidth: 44, height: 44 }}>
                  <Typography variant="h6" fontWeight={800} lineHeight={1}>{new Date(ev.start).getDate()}</Typography>
                  <Typography variant="caption" lineHeight={1} sx={{ fontSize: '0.6rem' }}>{new Date(ev.start).toLocaleDateString('es-CO', { month: 'short' })}</Typography>
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight={600}>{ev.title}</Typography>
                  {ev.location && <Typography variant="caption" color="text.secondary">{ev.location}</Typography>}
                </Box>
              </Box>
            ))}
          </Paper>

          {/* Mi perfil rápido */}
          {profile && (
            <Paper sx={{ p: 2.5, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Avatar sx={{ bgcolor: '#6366f1', width: 48, height: 48, fontWeight: 700 }}>
                  {profile.firstName?.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>{profile.firstName} {profile.lastName}</Typography>
                  <Chip size="small" label={profile.category || 'Empleado'} sx={{ bgcolor: '#ede9fe', color: '#6366f1' }} />
                </Box>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <Stack spacing={0.8}>
                {profile.phone && <Typography variant="body2">📞 {profile.phone}</Typography>}
                {profile.city && <Typography variant="body2">📍 {profile.city}</Typography>}
                <Typography variant="body2">🏷️ {profile.employeeCode}</Typography>
              </Stack>
              <Button variant="outlined" size="small" fullWidth sx={{ mt: 1.5 }}
                onClick={() => setActiveSection && setActiveSection('staff')}>
                Ver Mi Perfil Completo
              </Button>
              <Button variant="outlined" color="warning" size="small" fullWidth sx={{ mt: 1 }}
                onClick={() => setPwdDialog({ open: true, current: '', next: '', confirm: '', saving: false, error: '' })}>
                🔑 Cambiar Contraseña
              </Button>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* ── Dialog cambiar contraseña ── */}
      <Dialog open={pwdDialog.open} onClose={() => setPwdDialog(p => ({ ...p, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>🔑 Cambiar Contraseña</DialogTitle>
        <DialogContent dividers>
          {pwdDialog.error && <Alert severity="error" sx={{ mb: 2 }}>{pwdDialog.error}</Alert>}
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField label="Contraseña actual" type="password" size="small" fullWidth
              value={pwdDialog.current} onChange={e => setPwdDialog(p => ({ ...p, current: e.target.value }))} />
            <TextField label="Nueva contraseña" type="password" size="small" fullWidth
              value={pwdDialog.next} onChange={e => setPwdDialog(p => ({ ...p, next: e.target.value }))}
              helperText="Mínimo 6 caracteres" />
            <TextField label="Confirmar nueva contraseña" type="password" size="small" fullWidth
              value={pwdDialog.confirm} onChange={e => setPwdDialog(p => ({ ...p, confirm: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPwdDialog(p => ({ ...p, open: false }))} disabled={pwdDialog.saving}>Cancelar</Button>
          <Button variant="contained" disabled={pwdDialog.saving || !pwdDialog.current || !pwdDialog.next || !pwdDialog.confirm}
            onClick={handleChangePwd}>
            {pwdDialog.saving ? <CircularProgress size={20} color="inherit" /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}